import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, lazy, Suspense } from 'react';
import './App.css';
import { Html5Qrcode } from 'html5-qrcode';
import { toast, Toaster } from 'sonner';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Area } from 'recharts';

// ═══════════════════════════════════════════════════════════════════════════════
// CONFIG & UTILITIES - Extracted for modularity
// ═══════════════════════════════════════════════════════════════════════════════
import { 
  BACKEND_URL, 
  DEV_MODE, 
  STORAGE_KEYS,
  KNOWN_CHAINS, 
  isKnownChainRestaurant, 
  SMART_SUGGESTIONS, 
  getCuisineType,
  shouldForceFailure 
} from './config/appConfig';
import debug from './utils/debug';

// iOS COMPATIBILITY UTILITIES
import {
  isIOS,
  isSafari,
  getDeviceInfo,
  safeGetItem,
  safeSetItem,
  safeRemoveItem,
  shouldUseFileInputForCamera,
  handleCameraError,
  logApiEvent,
  updateCameraState,
  updateAuthState,
  updateEntitlementsState
} from './utils/iosCompat';

// ANALYTICS (PostHog)
import analytics, { EVENTS, identifyUser, resetUser, track, trackMealLogged, trackUpgradeCTA } from './utils/analytics';

// BACK BUTTON HANDLER
import useBackButton from './hooks/useBackButton';
// EXTRACTED HOOKS (Phase 5)
import usePWA from './hooks/usePWA';
import useHydration from './hooks/useHydration';
import useMenuSearch from './hooks/useMenuSearch';
import useMealHistory from './hooks/useMealHistory';
import useSmartCoach from './hooks/useSmartCoach';
import useOnboarding from './hooks/useOnboarding';
import useDailyCheckin from './hooks/useDailyCheckin';
import useNutritionGoals from './hooks/useNutritionGoals';
import useMealData from './hooks/useMealData';
import useUpgradeFlow from './hooks/useUpgradeFlow';
import useMIQHealth from './hooks/useMIQHealth';
import usePostMealFlow from './hooks/usePostMealFlow';
import useFavorites from './hooks/useFavorites';

// PWA UTILITIES
// PWA utilities now in usePWA hook
import PWAInstallModal from './components/settings/PWAInstallModal';

// CUISINE PREFERENCES
import CuisinePreferencesModal from './components/CuisinePreferencesModal';
import AskWhyModal from './components/modals/AskWhyModal';
import FixThisModal from './components/modals/FixThisModal';

// ═══════════════════════════════════════════════════════════════════════════════
// LAZY-LOADED HEAVY COMPONENTS (R1 Performance Sprint)
// These components are loaded on-demand to improve initial load time
// ═══════════════════════════════════════════════════════════════════════════════
const SmartSwapsView = lazy(() => import('./components/smart-swaps/SmartSwapsView'));
const CookbookView = lazy(() => import('./components/cookbook/CookbookView'));
const MIQViewLazy = lazy(() => import('./components/miq/MIQView').then(module => ({ default: module.MIQView })));
const TesterDiagnosticsPanel = lazy(() => import('./components/settings/TesterDiagnosticsPanel'));
const TesterDashboard = lazy(() => import('./components/settings/TesterDashboard'));
const SettingsView = lazy(() => import('./components/settings/SettingsView'));
const SubscriptionView = lazy(() => import('./components/settings/SubscriptionView'));
const HistoryView = lazy(() => import('./components/history/HistoryView'));
const ProfileView = lazy(() => import('./components/profile/ProfileView'));
const PrivacyView = lazy(() => import('./components/legal/PrivacyView'));
const DisclaimerView = lazy(() => import('./components/legal/DisclaimerView'));
const PrototypeRouter = lazy(() => import('./components/prototypes/PrototypeRouter'));

// Loading fallback component for lazy-loaded views
const ViewLoadingFallback = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
    color: 'var(--text-secondary)'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ 
        width: '32px', 
        height: '32px', 
        border: '3px solid rgba(147, 51, 234, 0.2)',
        borderTopColor: '#9333ea',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        margin: '0 auto 12px'
      }} />
      <span style={{ fontSize: '14px' }}>Loading...</span>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// PHASE 1 & 1.5 COMPRESSION: Extracted Components
// These are presentational components with NO behavior changes - just code organization
// ═══════════════════════════════════════════════════════════════════════════════
import { SessionGate, AuthLoadingScreen, AuthPage, SessionExpiredModal } from './components/auth';
import { AINutritionGoalsModal, ProfileCompletePrompt, OnboardingModal, FirstStepsJourney, FirstMealInsightModal, PremiumJourneyPrompt, UpgradeModal, PremiumTrackingModal, ManualEntryModal, AdvancedGoalsModal } from './components/modals';
import FeatureDiscoveryCard from './components/onboarding/FeatureDiscoveryCard';
import HydrationQuickLogModal from './components/modals/HydrationQuickLogModal';
import { DailyCheckInButton, TierSimulatorButtons, MIQView, IntelligenceLevelIndicator, MiqIntakeForm } from './components/miq';
import { calculateFactorCounts, CONFIDENCE_FACTOR_CONFIG } from './components/miq/MIQView';
import { MIQMiniStrip, GoalCTA, AIGoalsActiveBadge, QuickStartHome, DailyAlignmentChart, CoachStrip, CoachToast, HomeView, QuickActionBar } from './components/home';
import CameraView from './components/camera/CameraView';
import SmartHub from './components/smart-hub/SmartHub';
import NavigationWheel, { HOME_SECTIONS, MIQ_SECTIONS, SMARTHUB_SECTIONS, useWheelSections } from './components/navigation/NavigationWheel';
import PostMealCompleteSheet, { shouldShowPostMealSheet, markPostMealSheetShown } from './components/home/PostMealCompleteSheet';
import { AITransparencyNotice, NutritionDataDisclaimer } from './components/ui/UtilityComponents';
import BodyTypeGuidanceModal from './components/modals/BodyTypeGuidanceModal';
import DailyCheckinModal from './components/modals/DailyCheckinModal';
import SmartMealsModal from './components/modals/SmartMealsModal';
import FastingConsentModal from './components/modals/FastingConsentModal';
import FoodContributionModal from './components/modals/FoodContributionModal';
import BarcodeScannerModal from './components/modals/BarcodeScannerModal';
import ExerciseLoggerModal from './components/modals/ExerciseLoggerModal';
import FavoritesModal from './components/modals/FavoritesModal';
import EditPortionModal from './components/modals/EditPortionModal';
import MenuSearchModal from './components/modals/MenuSearchModal';
import PaywallModal from './components/modals/PaywallModal';
import IFTimerTab from './components/if/IFTimerTab';
import useNextBestStep, { markMIQTabVisited } from './hooks/useNextBestStep';
import { GuestLandingScreen, FirstMealSuccessSheet, PersonalizeTargetsModal, TargetsUpdatedSheet, SignUpPromptSheet } from './components/onboarding/FrictionlessOnboarding';
import { SmartPlAIteWordmark } from './components/brand/BrandLogo';
import { brand, getPrimaryButtonStyle, getSecondaryButtonStyle } from './styles/brand';
import { clearEntitlementsCache } from './hooks/useEntitlements';

// ═══════════════════════════════════════════════════════════════════════════════
// REFACTORED HOOKS & SERVICES - Extracted from App.js for maintainability
// ═══════════════════════════════════════════════════════════════════════════════
import { useGuestMeals } from './hooks/useGuestMeals';
import { useExercises } from './hooks/useExercises';
import { useProfile } from './hooks/useProfile';
import { useSubscription } from './hooks/useSubscription';
import { useAuth } from './hooks/useAuth';
import * as authService from './services/authService';
import * as mealsService from './services/mealsService';
import AdminDashboard from './components/admin/AdminDashboard';
// MetabolicProfileModal removed - redundant with MIQ insights
// TrendChartsModal removed Feb 23 2026 - replaced by Smart Hub sparklines + MIQ gauge

function App() {
  const [currentView, setCurrentView] = useState('home'); // 'home', 'camera', 'profile', 'settings', 'miq', 'if', 'disclaimer', 'auth'
  // Optional deep-link target for the MIQ Rhythm tab switcher — set by Smart
  // Hub's "Past 7 Days" CTA, consumed once by IFTimerTab, then cleared.
  const [ifDeepLinkTab, setIfDeepLinkTab] = useState(null);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // EXTRACTED HOOKS - Authentication (useAuth hook)
  // ═══════════════════════════════════════════════════════════════════════════════
  const {
    userId,
    authUser,
    isAuthenticated,
    authLoading,
    authReady,
    authError,
    isGuestMode,
    authView,
    setAuthView,
    authForm,
    setAuthForm,
    handleLogin: authHandleLogin,
    handleSignup: authHandleSignup,
    handleGoogleSignIn,
    handleLogout: authHandleLogout,
    clearAuthError,
    setUserId,
    setAuthUser,
    setIsAuthenticated,
    setIsGuestMode,
    setAuthError,
    setAuthReady
  } = useAuth();
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // EXTRACTED HOOKS - Exercise tracking (P1 decomposition)
  // ═══════════════════════════════════════════════════════════════════════════════
  const {
    todayExercises,
    dailySteps,
    totalCaloriesBurned,
    showExerciseLogger,
    exerciseForm,
    isLoggingExercise,
    setShowExerciseLogger,
    setExerciseForm,
    loadTodayExercises,
    logSteps,
    logExercise,
    deleteExercise
  } = useExercises(userId);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // EXTRACTED HOOKS - Profile management (P1 decomposition - strangler pattern)
  // ═══════════════════════════════════════════════════════════════════════════════
  const {
    userProfile,
    profileLoaded,
    profileLoading,
    profileError,
    refreshProfile,
    saveProfile,
    clearProfile,
    setUserProfile,
    setProfileLoaded
  } = useProfile();
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // EXTRACTED HOOKS - Subscription/Entitlements (P1 decomposition - strangler pattern)
  // ═══════════════════════════════════════════════════════════════════════════════
  const {
    entitlements: userEntitlements,
    tier: userTier,
    entitlementsLoading,
    entitlementsError,
    entitlementsReady,
    entitlementSource,
    refreshEntitlements,
    clearEntitlements,
    clearPendingCall: clearEntitlementsPendingCall,
    setEntitlements: setUserEntitlements,
    setTier: setUserTier,
    setEntitlementsReady
  } = useSubscription();
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // POSTHOG ANALYTICS INITIALIZATION
  // ═══════════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    analytics.init();
  }, []);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // APP LOADED EVENT - Dismiss emergency recovery screen
  // ═══════════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    // Signal to index.html that the app has loaded successfully
    console.log('[BOOT] App component mounted, dispatching loaded event');
    window.dispatchEvent(new CustomEvent('smartplaite-loaded'));
    
    // Also hide the emergency recovery screen directly
    const emergencyEl = document.getElementById('emergency-recovery');
    if (emergencyEl) {
      emergencyEl.style.display = 'none';
    }
  }, []);
  
  // PWA STATE (extracted to usePWA hook)
  const {
    pwaUpdateAvailable, setPwaUpdateAvailable,
    showPwaInstallModal, setShowPwaInstallModal,
    pwaInstallPlatform,
    isPwaInstalled,
    canShowInstallPrompt,
    handlePwaUpdate,
    handlePwaInstallClick
  } = usePWA();

  
  // Identify user when they log in
  useEffect(() => {
    if (userId && authUser) {
      identifyUser(userId, {
        email: authUser.email,
        auth_provider: authUser.auth_provider || 'email',
        created_at: authUser.created_at
      });
    }
  }, [userId, authUser]);
  
  // IF (Intermittent Fasting) state - read from localStorage for tab badge
  const [ifFastingActive, setIfFastingActive] = useState(false);
  const [ifTimeRemaining, setIfTimeRemaining] = useState(null);
  
  // Check fasting state for IF tab badge
  useEffect(() => {
    const checkFastingState = () => {
      if (!userId) return;
      const saved = localStorage.getItem(`miq_fasting_${userId}`);
      if (saved) {
        try {
          const state = JSON.parse(saved);
          if (state.isFasting && state.startTime && state.preset) {
            setIfFastingActive(true);
            // Calculate remaining time
            const elapsedSeconds = Math.floor((Date.now() - state.startTime) / 1000);
            const targetSeconds = parseInt(state.preset, 10) * 3600;
            const remaining = Math.max(targetSeconds - elapsedSeconds, 0);
            setIfTimeRemaining(remaining);
          } else {
            setIfFastingActive(false);
            setIfTimeRemaining(null);
          }
        } catch (e) {
          setIfFastingActive(false);
          setIfTimeRemaining(null);
        }
      } else {
        setIfFastingActive(false);
        setIfTimeRemaining(null);
      }
    };
    
    // Check immediately
    checkFastingState();
    
    // Check every 30 seconds for tab badge updates
    const interval = setInterval(checkFastingState, 30000);
    
    // Also check when tab becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden) checkFastingState();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userId]);
  
  // Format IF time remaining for badge (compact format)
  const formatIfBadgeTime = (seconds) => {
    if (!seconds || seconds <= 0) return null;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };
  
  // Format meal instructions - cleans up raw AI response text
  const formatMealInstructions = (instructions) => {
    if (!instructions || typeof instructions !== 'string') return instructions;
    
    // If instructions contain raw AI markers, parse and clean them
    if (instructions.includes('**PREP_DAY_PLAN:**') || 
        instructions.includes('**FREEZER_PLAN:**') ||
        instructions.includes('| step:')) {
      // Split by the markers and extract just the useful parts
      let cleaned = instructions
        .replace(/\*\*PREP_DAY_PLAN:\*\*/g, '\n📋 Prep Day Plan:\n')
        .replace(/\*\*FREEZER_PLAN:\*\*/g, '\n❄️ Freezer Plan:\n')
        .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove markdown bold markers
        .replace(/\| parallel: (YES|NO)/g, '') // Remove parallel markers
        .replace(/\| time: /g, ' • ')
        .replace(/\| step: /g, '\n• ')
        .replace(/\s*\|\s*/g, ' ')
        .replace(/\n{3,}/g, '\n\n') // Reduce excessive newlines
        .trim();
      return cleaned;
    }
    
    return instructions;
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // GUEST MODE - Using refactored useGuestMeals hook
  // ═══════════════════════════════════════════════════════════════════════════════
  const {
    guestMeals,
    guestMealCount,
    guestProfile,
    guestTargets: guestTargetsFromHook,
    guestTotals,
    lastGuestMealData,
    addGuestMeal,
    removeGuestMeal,
    updateGuestProfile,
    updateGuestTargets: setGuestTargetsInHook,
    clearGuestData,
    enterGuestMode,
    exitGuestMode,
    getMigrationData,
    setGuestMeals,
    setGuestMealCount,
    setGuestProfile,
    setLastGuestMealData,
    setGuestTargets
  } = useGuestMeals();
  
  // UI modal states for guest flow (kept separate from hook)
  const [showFirstMealSuccess, setShowFirstMealSuccess] = useState(false);
  const [showPersonalizeTargets, setShowPersonalizeTargets] = useState(false);
  const [showTargetsUpdated, setShowTargetsUpdated] = useState(false);
  const [showSignUpPrompt, setShowSignUpPrompt] = useState(false);
  const [calculatedGuestTargets, setCalculatedGuestTargets] = useState(null);
  const [showAuthFromGuest, setShowAuthFromGuest] = useState(false); // Show auth page from guest mode
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // CONSOLIDATED STATE for extracted hooks (moved up to avoid TDZ)
  // ═══════════════════════════════════════════════════════════════════════════════
  const [accountAgeDays, setAccountAgeDays] = useState(0);
  const [isPremium, setIsPremium] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState({
    is_premium: false,
    is_trial: false,
    is_trialing: false,
    trial_days_remaining: 0,
    plan_type: 'free',
    plan_name: 'Free',
    plan_key: null,
    expires_at: null,
    current_period_end: null,
    cancel_at_period_end: false,
    is_founders: false,
    stripe_customer_id: null,
    stripe_subscription_id: null,
    status: 'free',
    available_plans: {}
  });
  const [userTimezone, setUserTimezone] = useState(() => {
    return Math.round(-new Date().getTimezoneOffset() / 60);
  });

  // ═══════════════════════════════════════════════════════════════════════════════
  // EXTRACTED HOOKS - Phase 5 Heavy Batch Round 1 & 2
  // ═══════════════════════════════════════════════════════════════════════════════
  const {
    aiMiq, setAiMiq,
    aiMiqLoading,
    showCalibrationCompleteModal, setShowCalibrationCompleteModal,
    calibrationCompleteDismissed, setCalibrationCompleteDismissed,
    metabolicProfile, setMetabolicProfile,
    loadingMetabolicProfile, setLoadingMetabolicProfile,
    plateauDiagnostics,
    showPlateauCard,
    miqNudge, setMiqNudge,
    miqNudgeShownThisSession, setMiqNudgeShownThisSession,
    bodyTypeGuidance, setBodyTypeGuidance,
    showBodyTypeGuidance, setShowBodyTypeGuidance,
    allBodyTypeGuidance,
    expandWhatIsMiq, setExpandWhatIsMiq,
    expandMiqFactors, setExpandMiqFactors,
    expandMiqConfidence, setExpandMiqConfidence,
    loadAiMiq,
    loadMetabolicProfile,
    generateMetabolicProfile,
    loadPlateauDiagnostics,
    loadBodyTypeGuidance,
    loadAllBodyTypeGuidance,
    getGenericInsight
  } = useMIQHealth({ userId });
  const {
    showUpgradeModal, setShowUpgradeModal,
    upgradeContext,
    subscriptionTiers,
    showPremiumTrackingModal, setShowPremiumTrackingModal,
    homeCheckingOut, setHomeCheckingOut,
    showPaywall, setShowPaywall,
    paywallFeature,
    showUpgrade,
    handleHomeUpgrade,
    requirePremium,
    handlePremiumUpgrade,
    loadSubscriptionTiers
  } = useUpgradeFlow({ userId, isPremium, setCurrentView });

  const {
    showAiNutritionGoals, setShowAiNutritionGoals,
    aiNutritionGoals, setAiNutritionGoals,
    miqIntakeOptions,
    smartGoals,
    smartGoalsLoading, setSmartGoalsLoading,
    goalsToast, setGoalsToast,
    intelligenceLevel,
    previousIntelligenceLevel,
    openSmartGoalsGated,
    loadSmartGoals,
    refreshSmartGoals,
    calculateIntelligenceLevel,
    loadAiNutritionGoals,
    loadMiqIntakeOptions
  } = useNutritionGoals({ userId, userTier, metabolicProfile, isAuthenticated, authLoading, showUpgrade });

  const {
    todayMeals, setTodayMeals,
    todayTotals, setTodayTotals,
    mealsLoaded, setMealsLoaded,
    totalMealsLogged, setTotalMealsLogged,
    nutritionGoals, setNutritionGoals,
    firstMealLogged, setFirstMealLogged,
    showQuickStart, setShowQuickStart,
    dashboardStage,
    achievements,
    shownAchievements, setShownAchievements,
    calculateDashboardStage,
    loadTodayMeals,
    deleteMeal
  } = useMealData({ userId, userTimezone, accountAgeDays, isPremium });

  const {
    showPostLogInsight, setShowPostLogInsight,
    postLogInsight, setPostLogInsight,
    lastLoggedMeal, setLastLoggedMeal,
    showFoodContribution, setShowFoodContribution,
    showPostMealComplete, setShowPostMealComplete,
    postMealData, setPostMealData,
    foodContribution, setFoodContribution,
    showEditPortion, setShowEditPortion,
    editingMeal, setEditingMeal,
    portionPercent, setPortionPercent,
    portionLoading,
    fetchPostLogInsight,
    dismissPostLogInsight,
    savePortionEdit
  } = usePostMealFlow({ userId, isPremium, loadTodayMeals });

  const {
    favorites, setFavorites,
    showFavorites, setShowFavorites,
    loadFavorites,
    addToFavorites,
    logFavorite,
    removeFavorite
  } = useFavorites({ userId, loadTodayMeals, fetchPostLogInsight });
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // LOADING GATE - Prevents Welcome Modal race condition
  // All three must be true before we check whether to show Welcome Modal
  // ═══════════════════════════════════════════════════════════════════════════════
  // authReady is now from useAuth hook - true only after session is resolved
  // profileLoaded is now from useProfile hook
  // Loading gate: mealsLoaded now in useMealData hook
  const [signupInProgress, setSignupInProgress] = useState(false); // Prevents welcome flash during signup
  
  // Derived: All data needed for Welcome Modal decision is ready
  // Also require signupInProgress to be false to prevent flash during signup flow
  const dataReady = authReady && profileLoaded && mealsLoaded && !signupInProgress;
  
  // First Steps Journey - guides new users through optimal sequence
  const [showFirstStepsJourney, setShowFirstStepsJourney] = useState(false);
  // firstMealLogged now in useMealData hook
  const [showPremiumPrompt, setShowPremiumPrompt] = useState(false);
  
  // ONBOARDING STATE (extracted to useOnboarding hook)
  const {
    showOnboarding, setShowOnboarding,
    onboardingStep, setOnboardingStep,
    onboardingData, setOnboardingData
  } = useOnboarding();
  const [onboardingMetabolicProfile, setOnboardingMetabolicProfile] = useState(null);
  
  // MIQ state now in useMIQHealth hook
  
  // Calibration state now in useMIQHealth hook
  
  // Entitlements orchestration state (tester overrides remain in App.js)
  // NOTE: userEntitlements, userTier, entitlementsReady now come from useSubscription hook
  const [isTester, setIsTester] = useState(false); // Is user a designated tester
  const [isAdmin, setIsAdmin] = useState(false); // Is user an admin
  const adminNavRef = React.useRef(null);
  const [testerOverrideActive, setTesterOverrideActive] = useState(false); // Is tester override currently active
  
  // DEV MODE: Tier Simulator (TEST_MODE only)
  const [isTestMode, setIsTestMode] = useState(false);
  const [simulatingTier, setSimulatingTier] = useState(false);
  
  // SmartGoals, Intelligence Level, MIQ intake now in useNutritionGoals hook
  
  // Tier-gated Daily Check-in opener - available to all tiers but with limited features for free
  // Note: Daily check-in is available to all but Sleep/Stress/Activity insights are Genius-only
  const openDailyCheckinGated = useCallback((source = 'unknown') => {
    // Daily check-in is available to all users
    track('daily_checkin_opened', { source, tier: userTier });
    setShowDailyCheckin(true);
  }, [userTier]);
  
  // Upgrade/Pricing state now in useUpgradeFlow hook
  
  // Cuisine Preferences for better food recognition
  const [showCuisinePreferencesModal, setShowCuisinePreferencesModal] = useState(false);
  const [cuisinePreferences, setCuisinePreferences] = useState([]);
  const [cuisinePreferencesSet, setCuisinePreferencesSet] = useState(false);
  
  // Intelligence Level, Quick Start, dashboardStage, totalMealsLogged now in hooks
  const [showFirstMealInsight, setShowFirstMealInsight] = useState(false); // Post-first-meal insight
  const [firstMealData, setFirstMealData] = useState(null); // Data for first meal insight
  
  // MIQ nudge state now in useMIQHealth hook
  
  // Profile Completion Prompt - Strong nudge after profile setup
  const [showProfileCompletePrompt, setShowProfileCompletePrompt] = useState(false);
  
  // Smart Goals, todayMeals, todayTotals, nutritionGoals now in hooks
  // isPremium, subscriptionDetails moved up for hook dependencies
  // showPaywall, paywallFeature now in useUpgradeFlow hook
  const [analyzing, setAnalyzing] = useState(false);
  const [cameraMode, setCameraMode] = useState(null); // 'camera' or 'upload'
  // MENU SEARCH STATE (extracted to useMenuSearch hook)
  const {
    showMenuSearch, setShowMenuSearch,
    menuSearchQuery,
    menuSearchCity, setMenuSearchCity,
    menuSearchResults, setMenuSearchResults,
    selectedMenuItem, setSelectedMenuItem,
    isKnownChain,
    showSmartSuggestions,
    menuSearchLoading,
    searchRestaurantMenu,
    handleRestaurantQueryChange,
    closeMenuSearch
  } = useMenuSearch();
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualEntryText, setManualEntryText] = useState('');
  const [manualEntryResult, setManualEntryResult] = useState(null);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [barcodeResult, setBarcodeResult] = useState(null);
  const [barcodeServings, setBarcodeServings] = useState(1);
  const [scannerActive, setScannerActive] = useState(false);
  const [scannerInstance, setScannerInstance] = useState(null);
  const [showMealPlanner, setShowMealPlanner] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null); // null = today, 'YYYY-MM-DD' = past date
  const [mealPlannerFitMode, setMealPlannerFitMode] = useState(false);
  const [fitMacroResults, setFitMacroResults] = useState(null);
  const [fitMacroLoading, setFitMacroLoading] = useState(false);
  const [loggingFitMealIdx, setLoggingFitMealIdx] = useState(null);
  // Health Intelligence
  // SMART COACH STATE (extracted to useSmartCoach hook)
  const {
    showAskWhyModal, setShowAskWhyModal,
    askWhyPrefill, setAskWhyPrefill,
    askWhyInsightType, setAskWhyInsightType,
    askWhyUsedToday, setAskWhyUsedToday,
    showFixThisModal, setShowFixThisModal,
    fixThisInsightType, setFixThisInsightType,
    fixThisHeadline, setFixThisHeadline,
    handleHealthIntelFixThis,
    handleHealthIntelAskWhy,
    openSmartCoach
  } = useSmartCoach();
  // Promo code entered on auth page (applied after signup)
  const [pendingPromoCode, setPendingPromoCode] = useState('');
  const [mealPlanPreferences, setMealPlanPreferences] = useState({
    cuisines: [],
    dietaryRestrictions: [],
    mealsPerDay: 3,
    optimizeForBodyType: false,
    bodyType: '',
    intermittentFasting: false,
    fastingSchedule: '',
    useSmartGoals: false
  });
  
  // Body Type Guidance
  // Body type state now in useMIQHealth hook
  
  // Post-log insight state now in usePostMealFlow hook
  
  // Intermittent Fasting
  const [fastingConsentGiven, setFastingConsentGiven] = useState(false);
  const [showFastingConsent, setShowFastingConsent] = useState(false);
  
  const [mealPlanResult, setMealPlanResult] = useState(null);
  const [savingSmartMealId, setSavingSmartMealId] = useState(null); // Track which SmartMeal is being saved
  // Post-meal sheet, food contribution, favorites, portion edit state now in hooks
  
  // Plateau, MIQ accordion state now in useMIQHealth hook
  
  // Daily Check-In state (MIQ companion feature)
  // HYDRATION STATE (extracted to useHydration hook)
  const {
    showHydrationModal, setShowHydrationModal,
    hydrationStatus, setHydrationStatus,
    loadHydrationStatus
  } = useHydration();
  
  const [selectedMedicalConditions, setSelectedMedicalConditions] = useState([]);
  const [selectedFoodAllergies, setSelectedFoodAllergies] = useState([]);
  const [expandedHealthCategories, setExpandedHealthCategories] = useState({});
  
  // Coach Toast state for post-action notifications
  const [coachToast, setCoachToast] = useState(null);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // WHEEL SECTION STATE - Controls which sections are visible per screen
  // ═══════════════════════════════════════════════════════════════════════════════
  const { openSections: homeOpenSections, toggleSection: toggleHomeSection } = useWheelSections('home', userId);
  const { openSections: miqOpenSections, toggleSection: toggleMiqSection } = useWheelSections('miq', userId);
  const { openSections: hubOpenSections, toggleSection: toggleHubSection } = useWheelSections('hub', userId);
  
  // Smart Swaps state (Healthy Recipe Hub)
  const [showSmartSwaps, setShowSmartSwaps] = useState(false);
  
  // Cookbook state (Saved Recipes - Genius Plaite)
  const [showCookbook, setShowCookbook] = useState(false);
  
  // Eating Out context (passed from Smart Swaps)
  const [eatingOutContext, setEatingOutContext] = useState(null);

  // Smart Hub expansion states
  const [smartHubFavoritesExpanded, setSmartHubFavoritesExpanded] = useState(true);
  const [smartHubTodayExpanded, setSmartHubTodayExpanded] = useState(true);
  const [smartIntakeExpanded, setSmartIntakeExpanded] = useState(false); // Default collapsed per spec
  const [savedRecipesExpanded, setSavedRecipesExpanded] = useState(true); // Saved recipes from Smart Swaps
  
  // Saved Recipes state (from Smart Hub - Smart Swaps saves)
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [savedRecipesLoading, setSavedRecipesLoading] = useState(false);
  
  // Smart Intake History state
  const [mealHistoryFilter, setMealHistoryFilter] = useState('all');
  const [mealHistoryDays, setMealHistoryDays] = useState(14);

  // ═══════════════════════════════════════════════════════════════════════════════
  // R1 PERFORMANCE: Refs for tracking pending API calls (deduplication)
  // ═══════════════════════════════════════════════════════════════════════════════
  const pendingApiCalls = useRef({
    entitlements: null,
    profile: null,
    miq: null
  });

  // ═══════════════════════════════════════════════════════════════════════════════
  // ANALYTICS-WRAPPED NAVIGATION HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════════
  const openSmartSwaps = useCallback(() => {
    setShowSmartSwaps(true);
    track(EVENTS.SMART_PICKS_OPENED);
  }, []);

  const openCookbook = useCallback(() => {
    // Smart Hub is a Genius Plaite feature
    const hasGeniusTier = userTier === 'miq' || userTier === 'genius';
    if (!hasGeniusTier) {
      // Show upgrade modal instead
      setShowUpgradeModal(true);
      track(EVENTS.UPGRADE_MODAL_OPENED, { trigger: 'smart_hub' });
      return;
    }
    // Navigate to Smart Hub (which uses 'history' view)
    setCurrentView('history');
    track(EVENTS.SMART_HUB_OPENED);
  }, [userTier]);

  const openMiqView = useCallback(() => {
    setCurrentView('miq');
    track(EVENTS.MIQ_VIEW_OPENED);
  }, []);

  const openSmartMealsFitMode = useCallback(() => {
    setMealPlannerFitMode(true);
    setFitMacroResults(null);
    setShowMealPlanner(true);
    setFitMacroLoading(true);
    // Compute macro targets inline (derived consts are declared later in component)
    const dg = aiNutritionGoals?.nutrition_goals?.daily_targets?.calories
      || userProfile?.daily_calories || 2000;
    const tp = aiNutritionGoals?.nutrition_goals?.daily_targets?.protein_g
      || nutritionGoals?.protein_g || 50;
    const tc = aiNutritionGoals?.nutrition_goals?.daily_targets?.carbs_g
      || nutritionGoals?.carbs_g || 250;
    const tf = aiNutritionGoals?.nutrition_goals?.daily_targets?.fat_g
      || nutritionGoals?.fat_g || 65;
    const calRemaining = Math.max((dg || 2000) - (todayTotals?.calories || 0), 0);
    const protRemaining = Math.max((tp || 50) - (todayTotals?.protein || 0), 0);
    const fatRemaining = Math.max((tf || 65) - (todayTotals?.fat || 0), 0);
    const carbsRemaining = Math.max((tc || 250) - (todayTotals?.carbs || 0), 0);
    const fatOverLimit = (todayTotals?.fat || 0) > (tf || 65) * 1.1;
    fetch(`${BACKEND_URL}/api/health-intelligence/fit-remaining-meals`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        calories_remaining: calRemaining,
        protein_remaining: protRemaining,
        fat_remaining: fatRemaining,
        carbs_remaining: carbsRemaining,
        fat_over_limit: fatOverLimit,
        diet_type: userProfile?.dietary_preference || '',
        allergies: [
          ...(userProfile?.food_allergies?.filter(a => a && a !== 'none') || []),
          ...(aiNutritionGoals?.intake_data?.food_allergies?.filter(a => a && a !== 'none') || [])
        ].filter((v, idx, arr) => arr.indexOf(v) === idx)
      })
    })
    .then(r => r.ok ? r.json() : null)
    .then(data => { if (data) setFitMacroResults(data); })
    .catch(() => {})
    .finally(() => setFitMacroLoading(false));
  }, [todayTotals, userId, userProfile, aiNutritionGoals, nutritionGoals]);

  // Health Considerations categories and options
  const healthCategories = {
    'Metabolic & Blood Sugar': [
      'Type 1 Diabetes',
      'Type 2 Diabetes',
      'Prediabetes',
      'Insulin Resistance',
      'Hypoglycemia',
      'Gestational Diabetes'
    ],
    'Heart & Cardiovascular': [
      'High Blood Pressure',
      'High Cholesterol',
      'Heart Disease (general)',
      'Atherosclerosis',
      'Heart Failure',
      'Atrial Fibrillation'
    ],
    'Digestive & Food-Related': [
      'IBS (Irritable Bowel Syndrome)',
      'IBD (Crohn\'s/Ulcerative Colitis)',
      'Acid Reflux / GERD',
      'Celiac Disease',
      'Lactose Intolerance',
      'Gastroparesis',
      'Diverticulitis'
    ],
    'Kidney & Liver': [
      'Chronic Kidney Disease',
      'Kidney Stones',
      'Fatty Liver Disease (NAFLD)',
      'Liver Cirrhosis',
      'Gout'
    ],
    'Hormonal & Endocrine': [
      'PCOS (Polycystic Ovary Syndrome)',
      'Hypothyroidism',
      'Hyperthyroidism',
      'Adrenal Insufficiency',
      'Menopause/Perimenopause'
    ],
    'Allergies & Sensitivities': [
      'Nut Allergy',
      'Shellfish Allergy',
      'Egg Allergy',
      'Soy Allergy',
      'Gluten Sensitivity',
      'Dairy Allergy',
      'Multiple Food Allergies'
    ],
    'Autoimmune & Inflammatory': [
      'Rheumatoid Arthritis',
      'Lupus',
      'Multiple Sclerosis',
      'Hashimoto\'s Thyroiditis',
      'Psoriasis'
    ],
    'Mental Health & Eating': [
      'Eating Disorder Recovery',
      'Anxiety (food-related)',
      'Depression (appetite affected)'
    ],
    'Other Conditions': [
      'Anemia',
      'Osteoporosis',
      'Cancer (in treatment)',
      'Post-Bariatric Surgery',
      'Pregnancy',
      'Breastfeeding'
    ]
  };
  
  const toggleHealthCategory = (category) => {
    setExpandedHealthCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  
  const toggleHealthCondition = (condition) => {
    if (selectedMedicalConditions.includes(condition)) {
      setSelectedMedicalConditions(selectedMedicalConditions.filter(c => c !== condition));
    } else {
      setSelectedMedicalConditions([...selectedMedicalConditions, condition]);
    }
  };
  // Pre-selected image from guest flow (passed to CameraView)
  const [pendingCameraImage, setPendingCameraImage] = useState(null);
  const [cameraPermissionStatus, setCameraPermissionStatus] = useState('unknown'); // 'unknown', 'granted', 'denied', 'prompt'
  
  // iOS-specific state (authReady already declared above)
  // NOTE: entitlementsReady now comes from useSubscription hook
  const [showDiagnosticsPanel, setShowDiagnosticsPanel] = useState(false); // Tester diagnostics panel
  
  // Steps and Exercise tracking - MOVED TO useExercises hook
  // (todayExercises, dailySteps, totalCaloriesBurned, showExerciseLogger, exerciseForm)
  
  // Achievements and daily summary
  // achievements, shownAchievements now in useMealData hook
  const [showDailySummary, setShowDailySummary] = useState(false);
  const [dailySummaries, setDailySummaries] = useState([]);
  // historyViewPeriod state removed Feb 23 2026 — was only used by the deleted Intake History bar charts
  
  // Units preference
  const [useImperial, setUseImperial] = useState(false);

  // DAILY CHECK-IN STATE (extracted to useDailyCheckin hook)
  const {
    showDailyCheckin, setShowDailyCheckin,
    dailyCheckinData, setDailyCheckinData,
    dailyCheckinLoading, setDailyCheckinLoading,
    checkedInToday, setCheckedInToday,
    dailyCheckinContribution, setDailyCheckinContribution,
    checkinFormData, setCheckinFormData,
    weightCheckStatus, setWeightCheckStatus
  } = useDailyCheckin({ userId, useImperial, todayExercises });
  
  // userTimezone moved up for hook dependencies

  // MEAL HISTORY STATE (extracted to useMealHistory hook)
  const {
    mealHistory, setMealHistory,
    mealHistoryLoading,
    loadMealHistory
  } = useMealHistory(userTimezone);
  
  // Premium feature names for paywall
  const PREMIUM_FEATURES = {
    mealPlanner: 'SmartMeals',
    barcodeScanner: 'Barcode Scanner',
    micronutrients: 'Micronutrient Tracking',
    trendCharts: 'Trend Charts',
    aiGoals: 'AI-Suggested Goals',
    healthAwarePlanning: 'Health-Aware Meal Planning',
    intermittentFasting: 'Intermittent Fasting Scheduling'
  };
  
  // RDA (Recommended Daily Allowance) values based on gender and age
  // Returns RDA values for micronutrients
  const getRDAValues = () => {
    const gender = userProfile?.gender?.toLowerCase() || 'male';
    const age = userProfile?.age || 30;
    
    // Base RDA values for adults (adjustable by gender/age)
    const rdaBase = {
      fiber: gender === 'female' ? 25 : 38, // g
      sugar: 25, // g (max recommended)
      sodium: 2300, // mg (max recommended)
      potassium: gender === 'female' ? 2600 : 3400, // mg
      cholesterol: 300, // mg (max recommended)
      vitamin_a: gender === 'female' ? 700 : 900, // mcg
      vitamin_c: gender === 'female' ? 75 : 90, // mg
      vitamin_d: age > 70 ? 20 : 15, // mcg
      calcium: age > 50 && gender === 'female' ? 1200 : 1000, // mg
      iron: gender === 'female' && age < 51 ? 18 : 8 // mg
    };
    
    return rdaBase;
  };
  
  // Advanced Goals & Trends
  const [showAdvancedGoals, setShowAdvancedGoals] = useState(false);
  const [advancedGoals, setAdvancedGoals] = useState(null);
  const [goalFormMode, setGoalFormMode] = useState('ai'); // 'ai' or 'manual'
  const [goalFormData, setGoalFormData] = useState({
    goal_weight: '',
    timeline_weeks: '12',
    goal_pace: 'standard', // 'relaxed', 'standard', 'aggressive'
    daily_calorie_target: '',
    protein_g: '',
    carbs_g: '',
    fat_g: ''
  });
  // trendData / trendPeriod / showTrendCharts state removed Feb 23 2026 (Trend Charts modal deprecated)

  // ═══════════════════════════════════════════════════════════════════════════════
  // MASTER BACK BUTTON HANDLER — Prevents back-button logout on iOS/Android
  // ═══════════════════════════════════════════════════════════════════════════════
  useBackButton({
    currentView,
    setCurrentView,
    modals: [
      // Priority order: most common user-opened modals first
      { name: 'mealPlanner', isOpen: showMealPlanner, close: () => setShowMealPlanner(false) },
      { name: 'smartSwaps', isOpen: showSmartSwaps, close: () => setShowSmartSwaps(false) },
      { name: 'aiNutritionGoals', isOpen: showAiNutritionGoals, close: () => setShowAiNutritionGoals(false) },
      { name: 'dailyCheckin', isOpen: showDailyCheckin, close: () => setShowDailyCheckin(false) },
      { name: 'askWhy', isOpen: showAskWhyModal, close: () => setShowAskWhyModal(false) },
      { name: 'fixThis', isOpen: showFixThisModal, close: () => setShowFixThisModal(false) },
      { name: 'favorites', isOpen: showFavorites, close: () => setShowFavorites(false) },
      { name: 'menuSearch', isOpen: showMenuSearch, close: () => setShowMenuSearch(false) },
      { name: 'barcodeScanner', isOpen: showBarcodeScanner, close: () => setShowBarcodeScanner(false) },
      { name: 'manualEntry', isOpen: showManualEntry, close: () => setShowManualEntry(false) },
      { name: 'bodyTypeGuidance', isOpen: showBodyTypeGuidance, close: () => setShowBodyTypeGuidance(false) },
      { name: 'fastingConsent', isOpen: showFastingConsent, close: () => setShowFastingConsent(false) },
      { name: 'foodContribution', isOpen: showFoodContribution, close: () => setShowFoodContribution(false) },
      { name: 'editPortion', isOpen: showEditPortion, close: () => setShowEditPortion(false) },
      { name: 'upgradeModal', isOpen: showUpgradeModal, close: () => setShowUpgradeModal(false) },
      { name: 'paywall', isOpen: showPaywall, close: () => setShowPaywall(false) },
      { name: 'advancedGoals', isOpen: showAdvancedGoals, close: () => setShowAdvancedGoals(false) },
      { name: 'hydrationModal', isOpen: showHydrationModal, close: () => setShowHydrationModal(false) },
      { name: 'cuisinePrefs', isOpen: showCuisinePreferencesModal, close: () => setShowCuisinePreferencesModal(false) },
      { name: 'onboarding', isOpen: showOnboarding, close: () => setShowOnboarding(false) },
    ]
  });
  
  // Force scroll to top and enable scrolling after React is ready
  // Note: OAuth session_id capture is now handled by useAuth hook
  useLayoutEffect(() => {
    // Force scroll to top synchronously
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Remove the prevent-scroll style that was added in index.html
    // This re-enables scrolling now that React has control
    const preventScrollStyle = document.getElementById('prevent-scroll-style');
    if (preventScrollStyle) {
      preventScrollStyle.remove();
    }
    
    // Additional scroll after enabling scrolling
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });
    
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, 50);
    
    return () => clearTimeout(timer);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════════
  // CAMERA PERMISSION - Request early to improve UX
  // ═══════════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    const checkAndRequestCameraPermission = async () => {
      try {
        // First check if permissions API is available
        if (navigator.permissions && navigator.permissions.query) {
          try {
            const result = await navigator.permissions.query({ name: 'camera' });
            setCameraPermissionStatus(result.state); // 'granted', 'denied', or 'prompt'
            
            // Listen for permission changes
            result.onchange = () => {
              setCameraPermissionStatus(result.state);
            };
            
            // If permission is 'prompt', request it proactively when user is on home screen
            if (result.state === 'prompt' && currentView === 'home') {
              // Request camera permission with a minimal stream request
              try {
                const stream = await navigator.mediaDevices.getUserMedia({ 
                  video: { facingMode: 'environment' } 
                });
                // Immediately stop the stream - we just wanted the permission
                stream.getTracks().forEach(track => track.stop());
                setCameraPermissionStatus('granted');
              } catch (permErr) {
                if (permErr.name === 'NotAllowedError') {
                  setCameraPermissionStatus('denied');
                }
                // Other errors (NotFoundError, etc.) - don't change status
              }
            }
          } catch (permQueryErr) {
            // permissions.query might not support 'camera' on some browsers
            // Fall back to direct getUserMedia request on home screen
            if (currentView === 'home' && cameraPermissionStatus === 'unknown') {
              try {
                const stream = await navigator.mediaDevices.getUserMedia({ 
                  video: { facingMode: 'environment' } 
                });
                stream.getTracks().forEach(track => track.stop());
                setCameraPermissionStatus('granted');
              } catch (mediaErr) {
                if (mediaErr.name === 'NotAllowedError') {
                  setCameraPermissionStatus('denied');
                }
              }
            }
          }
        } else if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          // No permissions API - try direct request on home screen
          if (currentView === 'home' && cameraPermissionStatus === 'unknown') {
            try {
              const stream = await navigator.mediaDevices.getUserMedia({ 
                video: { facingMode: 'environment' } 
              });
              stream.getTracks().forEach(track => track.stop());
              setCameraPermissionStatus('granted');
            } catch (mediaErr) {
              if (mediaErr.name === 'NotAllowedError') {
                setCameraPermissionStatus('denied');
              }
            }
          }
        }
      } catch (error) {
        debug.log('[Camera Permission] Error checking permission:', error);
      }
    };
    
    // Only request permission when on home screen and after a brief delay to let UI settle
    if (currentView === 'home' && isAuthenticated) {
      const timer = setTimeout(checkAndRequestCameraPermission, 1000);
      return () => clearTimeout(timer);
    }
  }, [currentView, isAuthenticated, cameraPermissionStatus]);

  // Next Best Step Coach System
  // State for Meal Decision Coach (Phase 2)
  const [mealPlannerJustExited, setMealPlannerJustExited] = useState(false);
  const [detectedRestaurantText, setDetectedRestaurantText] = useState(null);
  
  // Get last meal timestamp from todayMeals
  const lastMealTimestamp = todayMeals && todayMeals.length > 0 
    ? Math.max(...todayMeals.map(m => new Date(m.timestamp || m.created_at).getTime()))
    : null;
  
  const { 
    prompt: coachPrompt, 
    dismiss: dismissCoachPrompt, 
    onActionClick: onCoachActionClick,
    markMIQTabVisited: markMIQVisited,
    markBarcodeUsed,
    markMealPlannerUsed,
    markMealPlannerExitWithoutGenerate,
    clearMealPlannerExitFlag
  } = useNextBestStep({
    userId,
    userProfile,
    totalMealsLogged,
    allTimeMealsLogged: aiMiq?.confidence?.meals_logged,  // From MIQ confidence data
    hasGoal: !!(aiNutritionGoals?.intake_complete || nutritionGoals),
    aiNutritionGoals,  // Added for goals confirmation prompt
    lastDailyCheckin: dailyCheckinData?.last_checkin,
    checkedInToday,
    subscriptionDetails,
    currentView,
    showOnboarding,
    isTestMode,
    hasTimeline: !!(aiNutritionGoals?.timeline_weeks || advancedGoals?.timeline_weeks),
    hasMealPattern: !!(aiNutritionGoals?.meals_per_day || userProfile?.meals_per_day),
    // Meal Decision Coach (Phase 2) parameters
    lastMealTimestamp,
    detectedRestaurantText,
    mealPlannerJustExited,
    // Real-Food Last-Mile Suggestions (Phase 3.1) parameters
    // Pass raw data - hook will calculate remaining calories
    todayCalories: todayTotals?.calories || 0,
    advancedGoals,
    nutritionGoals
  });

  // Coach action handler
  const handleCoachAction = (action) => {
    onCoachActionClick();
    switch(action) {
      case 'SET_GOAL':
        setShowAdvancedGoals(true);
        break;
      case 'SET_MEAL_PATTERN':
        // Open SmartGoals for meal pattern
        openSmartGoalsGated('button');
        break;
      case 'COMPLETE_PROFILE':
        setShowOnboarding(true);
        break;
      case 'SET_GOALS_TIMELINE':
        // RULE L: Open SmartGoals for goals & timeline setup
        openSmartGoalsGated('button');
        break;
      case 'TAKE_PHOTO':
        setCameraMode('camera');
        setCurrentView('camera');
        break;
      case 'VIEW_MIQ':
        setCurrentView('miq');
        markMIQVisited();
        break;
      case 'VIEW_IF_TIMER':
        setCurrentView('if');
        break;
      case 'VIEW_PEPTIDE':
        setCurrentView('miq');
        markMIQVisited();
        break;
      case 'DAILY_CHECKIN':
        setShowDailyCheckin(true);
        break;
      case 'BARCODE_SCAN':
        setShowBarcodeScanner(true);
        markBarcodeUsed();
        break;
      case 'LOG_MEAL':
        // Open camera mode for meal logging (from goals confirmation)
        setCameraMode('camera');
        break;
      case 'MEAL_PLANNER':
        // Open meal planner and mark as used
        setShowMealPlanner(true);
        markMealPlannerUsed();
        clearMealPlannerExitFlag();  // Clear exit flag when opening
        setMealPlannerJustExited(false);
        break;
      // ═══════════════════════════════════════════════════════════════════════
      // EATING OUT — SMART SWAPS (Phase 3)
      // Entry point for Rules C, D, E coach nudges - Opens Smart Swaps view
      // ═══════════════════════════════════════════════════════════════════════
      case 'SMART_PICKS_COMING_SOON':
      case 'EATING_OUT_OPTIONS':
      case 'SMART_PICKS':
      case 'SMART_SWAPS':
        setShowSmartSwaps(true);
        break;
      // ═══════════════════════════════════════════════════════════════════════
      // LAST-MILE SUGGESTIONS → Redirect to SmartMeals Fit Macros mode
      // ═══════════════════════════════════════════════════════════════════════
      case 'EVENING_SNACK_IDEAS':
      case 'MISSED_MEAL_IDEAS':
        openSmartMealsFitMode();
        break;
      default:
        break;
    }
  };

  // Show coach toast after actions
  const showCoachToast = (message, ctaLabel, ctaAction, icon = '✨', secondaryMessage = null) => {
    setCoachToast({ message, ctaLabel, ctaAction, icon, secondaryMessage });
    // Auto-hide after 4 seconds
    setTimeout(() => setCoachToast(null), 4000);
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // NAVIGATION WHEEL — Upgrade handler for locked items
  // ═══════════════════════════════════════════════════════════════════════════════
  const handleWheelUpgrade = () => {
    showUpgrade('compass');
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // AUTH READY EFFECT - Load user data when auth is resolved
  // Session checking is now handled by useAuth hook
  // ═══════════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!authReady) return; // Wait for auth to resolve
    
    // Check for guest mode data (hook auto-initializes, but we need calculatedGuestTargets)
    const guestData = safeGetItem('guest_mode_data');
    if (guestData) {
      try {
        const parsed = JSON.parse(guestData);
        setCalculatedGuestTargets(parsed.targets || null);
        debug.guest(' Restored guest targets, meals already loaded via hook:', guestMealCount, 'meals');
      } catch (e) {
        console.error('[GUEST] Error parsing guest data:', e);
      }
    }
    
    if (isAuthenticated && userId) {
      debug.auth('[App] Auth ready, loading user data for:', userId);
      
      // Update auth state for diagnostics
      updateAuthState({ 
        isReady: true, 
        userId: userId, 
        email: authUser?.email 
      });
      
      // Load user data and entitlements
      loadEntitlements(userId).catch(e => console.error('loadEntitlements error:', e));
      loadAiNutritionGoals(userId).catch(e => console.error('loadAiNutritionGoals error:', e));
      loadAiMiq(userId).catch(e => console.error('loadAiMiq error:', e));
      loadMetabolicProfile(userId).catch(e => console.error('loadMetabolicProfile error:', e));
      loadDailyCheckin(userId).catch(e => console.error('loadDailyCheckin error:', e));
      
      // ALWAYS load profile and meals - they handle missing data gracefully
      loadUserProfile(userId);
      loadTodayMeals(userId);
      loadFavorites(userId);
      loadTodayExercises(userId, userTimezone);
      
      // Check admin status independently (not dependent on profile loading)
      fetch(`${BACKEND_URL}/api/admin/check?user_id=${userId}`)
        .then(res => res.ok ? res.json() : null)
        .then(data => { if (data) setIsAdmin(data.is_admin || false); })
        .catch(() => {});
      
    } else if (isGuestMode) {
      debug.auth('[App] Auth ready, entering guest mode');
      updateAuthState({ isReady: true });
      // Mark loading states as complete for guest mode
      setProfileLoaded(true);
      setMealsLoaded(true);
    }
  }, [authReady, isAuthenticated, userId, isGuestMode]);
  
  // Check if TEST_MODE is enabled (for Dev Tier Simulator)
  // Auto-scroll nav to show Admin tab when it appears
  useEffect(() => {
    if (isAdmin && adminNavRef.current) {
      setTimeout(() => {
        adminNavRef.current?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }, 500);
    }
  }, [isAdmin]);
  useEffect(() => {
    const checkTestMode = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/dev/test-mode-status`);
        if (response.ok) {
          const data = await response.json();
          setIsTestMode(data.test_mode === true);
        }
      } catch (error) {
        debug.dev('TEST_MODE check failed (expected in production)');
        setIsTestMode(false);
      }
    };
    checkTestMode();
  }, []);

  // Global Escape key handler to close modals
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        // Close modals in priority order (most likely to be open first)
        if (showSmartSwaps) setShowSmartSwaps(false);
        else if (showFirstStepsJourney) setShowFirstStepsJourney(false);
        else if (showAiNutritionGoals) setShowAiNutritionGoals(false);
        else if (showDailyCheckin) setShowDailyCheckin(false);
        else if (showOnboarding) setShowOnboarding(false);
        else if (showMealPlanner) setShowMealPlanner(false);
        else if (showUpgradeModal) setShowUpgradeModal(false);
        else if (showPremiumTrackingModal) setShowPremiumTrackingModal(false);
        else if (showMenuSearch) setShowMenuSearch(false);
        else if (showBarcodeScanner) setShowBarcodeScanner(false);
        else if (showAdvancedGoals) setShowAdvancedGoals(false);
      }
      // TEST_MODE: Ctrl+Shift+S opens Smart Swaps view for testing
      if (event.ctrlKey && event.shiftKey && event.key === 'S') {
        debug.dev('[TEST] Opening Smart Swaps view via keyboard shortcut');
        setShowSmartSwaps(true);
      }
    };
    
    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [showSmartSwaps, showFirstStepsJourney, showAiNutritionGoals, showDailyCheckin, 
      showOnboarding, showMealPlanner, showUpgradeModal, showPremiumTrackingModal,
      showMenuSearch, showBarcodeScanner, showAdvancedGoals]);

  // SmartGoals modal tracking, MIQ intake auto-load, SmartGoals migration
  // effects now in useNutritionGoals hook
  
  // Weight status and steps sync now handled in useDailyCheckin hook

  // Google OAuth callback is now handled by useAuth hook
  // Guest meal migration + promo code application after Google auth
  useEffect(() => {
    if (isAuthenticated && userId && authUser?.auth_provider === 'google') {
      // Migrate guest meals if any
      migrateGuestMeals(userId).then(result => {
        if (result.migrated > 0) {
          debug.auth(`[App] Migrated ${result.migrated} guest meals after Google auth`);
        }
      }).catch(e => console.error('Guest meal migration error:', e));
      
      // Check for pending promo code persisted before Google redirect
      const savedPromo = localStorage.getItem('pending_promo_code');
      if (savedPromo) {
        localStorage.removeItem('pending_promo_code');
        (async () => {
          try {
            const promoRes = await fetch(`${BACKEND_URL}/api/users/${userId}/start-trial`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ promo_code: savedPromo })
            });
            const promoData = await promoRes.json();
            if (promoRes.ok && promoData.success) {
              console.log('[Google Auth] Promo code applied:', savedPromo);
              toast.success(`Promo code ${savedPromo} applied! Enjoy 30 days of Genius Plaite.`);
              loadEntitlements(userId).catch(e => console.error('loadEntitlements error:', e));
            } else {
              console.warn('[Google Auth] Promo code failed:', promoData.detail);
            }
          } catch (promoErr) {
            console.error('[Google Auth] Promo apply error:', promoErr);
          }
        })();
      }
    }
  }, [isAuthenticated, userId, authUser]);

  // Cleanup scanner when modal closes
  useEffect(() => {
    if (!showBarcodeScanner) {
      stopBarcodeScanner();
    }
  }, [showBarcodeScanner]);

  // Hide OAuth overlay when auth is ready (success or failure)
  useEffect(() => {
    if (authReady) {
      // Hide OAuth loading overlay if present - do this when auth is READY, not just when authenticated
      // This ensures overlay is hidden even if OAuth fails
      const overlay = document.getElementById('oauth-loading-overlay');
      if (overlay && (overlay.style.display !== 'none')) {
        // Small delay to ensure smooth transition
        setTimeout(() => {
          overlay.classList.add('hidden');
          sessionStorage.removeItem('show_oauth_overlay');
          // Remove from DOM after transition
          setTimeout(() => {
            overlay.style.display = 'none';
          }, 300);
        }, 100);
      }
    }
  }, [authReady]);

  // Scroll to top when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Force scroll to top when authentication completes - multiple attempts
      const scrollToTop = () => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      };
      
      // Immediate scroll
      scrollToTop();
      
      // Delayed scrolls to catch any async rendering
      setTimeout(scrollToTop, 100);
      setTimeout(scrollToTop, 300);
      setTimeout(scrollToTop, 500);
    }
  }, [isAuthenticated]);
  
  // Load Smart Hub data on entry (daily summaries for sparklines, saved recipes)
  useEffect(() => {
    if (currentView === 'history' && userId && isPremium) {
      loadDailySummaries(userId);
      loadSavedRecipes(userId); // Saved recipes from Smart Swaps
    }
  }, [currentView, userId, isPremium]);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // WELCOME MODAL DECISION - Loading Gate Pattern
  // This useEffect ONLY runs when ALL data is ready, preventing race conditions
  // NOTE: FirstStepsJourney modal is DISABLED by default to ensure users see the
  // main landing screen first. The journey can be triggered manually if needed.
  // ═══════════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    // Don't do anything until all data is loaded
    if (!dataReady || !userId) {
      return;
    }
    
    debug.log('[Welcome Modal Check] Data ready, checking conditions:', {
      userId,
      totalMealsLogged,
      userProfile: !!userProfile,
      onboardingCompleted: userProfile?.onboarding_completed
    });
    
    // Determine if this is a true first-time user:
    // 1. No profile exists OR profile doesn't indicate onboarding completed
    // 2. No meals have been logged
    // 3. Haven't already seen the journey (localStorage backup)
    // 
    // For legacy users without onboarding_completed field, we check for essential profile data
    // (height, weight, age) to determine if they've completed basic onboarding
    const hasCompletedProfile = userProfile && (
      userProfile.onboarding_completed === true || 
      (userProfile.height_cm && userProfile.weight_kg && userProfile.age)  // Legacy profile check
    );
    const hasMeals = totalMealsLogged > 0;
    const hasSeenJourney = localStorage.getItem(`smartplaite_seen_journey_${userId}`);
    
    // First-time user: No completed profile AND no meals AND hasn't seen journey
    const isFirstTimeUser = !hasCompletedProfile && !hasMeals && !hasSeenJourney;
    
    debug.log('[Welcome Modal Check] Decision:', {
      hasCompletedProfile,
      hasMeals,
      hasSeenJourney,
      isFirstTimeUser,
      userProfileExists: !!userProfile,
      profileDetails: userProfile ? {
        onboarding_completed: userProfile.onboarding_completed,
        height_cm: userProfile.height_cm,
        weight_kg: userProfile.weight_kg,
        age: userProfile.age
      } : null
    });
    
    // DISABLED: Auto-showing FirstStepsJourney modal
    // We want users to see the main app screen first, not a modal popup
    // The journey flow is now triggered manually through the Profile setup prompts
    if (isFirstTimeUser) {
      // Just mark as seen without showing the modal
      localStorage.setItem(`smartplaite_seen_journey_${userId}`, 'true');
      debug.log('[Welcome Modal Check] First-time user detected - skipping modal, showing main UI');
    }
  }, [dataReady, userId, totalMealsLogged, userProfile]);

  // ═══════════════════════════════════════════════════════════════════════════════
  // GUEST → ACCOUNT DATA MIGRATION
  // Preserves guest meals when user signs up or logs in
  // ═══════════════════════════════════════════════════════════════════════════════
  const migrateGuestMeals = async (userId) => {
    try {
      // Use the hook's getMigrationData to get guest data
      const { meals: hookMeals, profile: hookProfile, mealCount } = getMigrationData();
      
      // Also check localStorage as fallback (in case hook state is out of sync)
      const guestDataStr = localStorage.getItem('guest_mode_data');
      let guestData = null;
      if (guestDataStr) {
        try {
          guestData = JSON.parse(guestDataStr);
        } catch (parseError) {
          console.error('[GuestMigration] Failed to parse guest data:', parseError);
        }
      }
      
      // Use hook data if available, fallback to localStorage
      const mealsToMigrate = hookMeals?.length > 0 ? hookMeals : (guestData?.meals || []);
      const profileToMigrate = hookProfile || guestData?.profile || null;
      
      if (mealsToMigrate.length === 0) {
        debug.guest('[Migration] No guest meals found');
        clearGuestData(); // Use hook to clear
        setCalculatedGuestTargets(null);
        return { success: true, migrated: 0 };
      }
      
      debug.guest(`[Migration] Migrating ${mealsToMigrate.length} guest meals for user ${userId}`);
      
      try {
        // Use mealsService for cleaner API call
        const result = await mealsService.importGuestMeals(userId, mealsToMigrate, profileToMigrate);
        debug.guest(`[Migration] Successfully imported ${result.imported_count} meals`);
        
        // Clear guest data using the hook (handles localStorage too)
        clearGuestData();
        setCalculatedGuestTargets(null);
        
        // Show success toast with new copy
        if (result.imported_count > 0 || result.profile_migrated) {
          let message = '';
          if (result.imported_count > 0 && result.profile_migrated) {
            message = `Your meals and profile are now part of your MetaBolicIQ (MIQ) 🧠 — nothing lost. (${result.imported_count} meal${result.imported_count > 1 ? 's' : ''})`;
          } else if (result.imported_count > 0) {
            message = `Your meals are now part of your MetaBolicIQ (MIQ) 🧠 — nothing lost. (${result.imported_count} meal${result.imported_count > 1 ? 's' : ''})`;
          } else if (result.profile_migrated) {
            message = 'Your profile has been saved to your account.';
          }
          
          toast.success(message, { duration: 5000 });
          
          // Set flag for first-session reassurance in Settings
          localStorage.setItem('miq_migration_first_session', 'true');
          
          // If profile was migrated, offer to complete SmartGoals
          if (result.profile_migrated) {
            localStorage.setItem('show_smartgoals_after_migration', 'true');
          }
        }
        
        return { 
          success: true, 
          migrated: result.imported_count, 
          totals: result.updated_totals,
          profileMigrated: result.profile_migrated 
        };
      } catch (importError) {
        console.error('[GuestMigration] Import failed:', importError);
        
        // Do NOT clear guest data on failure - keep it safe
        toast.error('Your meals are safe — we couldn\'t sync yet. Try again later.', {
          duration: 5000
        });
        
        return { success: false, error: importError.message || 'Migration failed' };
      }
    } catch (error) {
      console.error('[GuestMigration] Error:', error);
      
      // Do NOT clear guest data on error - keep it safe
      toast.error('Your meals are safe — we couldn\'t sync yet. Try again later.', {
        duration: 5000
      });
      
      return { success: false, error: error.message };
    }
  };

  // DEV-ONLY: Test harness for guest migration
  if (DEV_MODE && typeof window !== 'undefined') {
    window.__seedGuestMeals__ = () => {
      const testMeals = [
        {
          id: `guest_test_${Date.now()}_1`,
          food_items: 'DEV TEST: Eggs and bacon breakfast',
          calories: 450,
          protein_g: 28,
          carbs_g: 5,
          fat_g: 35,
          timestamp: new Date().toISOString()
        },
        {
          id: `guest_test_${Date.now()}_2`,
          food_items: 'DEV TEST: Chicken salad lunch',
          calories: 380,
          protein_g: 32,
          carbs_g: 15,
          fat_g: 20,
          timestamp: new Date().toISOString()
        }
      ];
      
      // For dev testing, we need to manually set the state since the hook manages state
      // This is a workaround for testing purposes
      setGuestMeals(testMeals);
      setGuestMealCount(2);
      localStorage.setItem('guest_mode_data', JSON.stringify({
        mealCount: 2,
        meals: testMeals,
        profile: null,
        targets: null
      }));
      
      debug.dev(' Seeded 2 guest test meals:', testMeals);
      toast.success('[DEV] Seeded 2 guest test meals. Now sign up or log in to test migration.');
      return testMeals;
    };
    
    window.__clearGuestMeals__ = () => {
      clearGuestData(); // Use the hook's clear function
      debug.dev(' Cleared guest_mode_data');
      toast.success('[DEV] Cleared guest meals from localStorage');
    };
    
    debug.dev(' Guest migration test harness available:');
    debug.dev('  window.__seedGuestMeals__() - Seed 2 test meals');
    debug.dev('  window.__clearGuestMeals__() - Clear guest data');
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // AUTHENTICATION WRAPPER FUNCTIONS
  // These wrap the useAuth hook to add app-specific logic (guest migration, data loading)
  // ═══════════════════════════════════════════════════════════════════════════════
  const handleSignup = async (e) => {
    e.preventDefault();
    
    // Reset loading gate flags for fresh signup
    setProfileLoaded(false);
    setMealsLoaded(false);
    setSignupInProgress(true); // Prevent welcome modal flash
    
    // Use the hook's signup function
    const result = await authHandleSignup(e);
    
    if (result.success) {
      const uid = result.userId;
      
      // Migrate guest meals (if any)
      try {
        const migrationResult = await migrateGuestMeals(uid);
        debug.auth('[Signup] Guest migration result:', migrationResult);
      } catch (e) {
        console.error('[Signup] Migration error:', e);
      }
      
      // Now load profile and meals (migration is complete)
      await Promise.all([
        loadUserProfile(uid).catch(e => console.error('loadUserProfile error:', e)),
        loadTodayMeals(uid).catch(e => console.error('loadTodayMeals error:', e))
      ]);
      
      // Load entitlements to get correct tier
      loadEntitlements(uid).catch(e => console.error('loadEntitlements error:', e));
      
      // Auto-apply promo code if entered on auth page
      if (pendingPromoCode && pendingPromoCode.trim()) {
        try {
          const promoRes = await fetch(`${BACKEND_URL}/api/users/${uid}/start-trial`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ promo_code: pendingPromoCode.trim() })
          });
          const promoData = await promoRes.json();
          if (promoRes.ok && promoData.success) {
            console.log('[Signup] Promo code applied:', pendingPromoCode);
            // Reload entitlements to reflect promo trial
            loadEntitlements(uid).catch(e => console.error('loadEntitlements error:', e));
          } else {
            console.warn('[Signup] Promo code failed:', promoData.detail);
          }
        } catch (promoErr) {
          console.error('[Signup] Promo apply error:', promoErr);
        }
        setPendingPromoCode('');
      }
      
      // Signup flow complete - allow welcome modal check to proceed
      setSignupInProgress(false);
      
      // After signup, push user to set up profile
      setTimeout(() => {
        setShowOnboarding(true);
        toast.success('Welcome! Let\'s set up your profile to personalize your experience.', {
          duration: 4000
        });
      }, 500);
    } else {
      // Reset flags on error
      setProfileLoaded(true);
      setMealsLoaded(true);
      setSignupInProgress(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Reset loading gate flags for fresh login
    setProfileLoaded(false);
    setMealsLoaded(false);
    
    // Use the hook's login function
    const result = await authHandleLogin(e);
    
    if (result.success) {
      const uid = result.userId;
      
      // Migrate guest meals (if any) then load all data
      try {
        const migrationResult = await migrateGuestMeals(uid);
        debug.auth('[Login] Guest migration result:', migrationResult);
      } catch (e) {
        console.error('[Login] Migration error:', e);
      }
      
      // Now load profile and meals (migration is complete)
      loadUserProfile(uid).catch(e => console.error('loadUserProfile error:', e));
      loadTodayMeals(uid).catch(e => console.error('loadTodayMeals error:', e));
      loadFavorites(uid).catch(e => console.error('loadFavorites error:', e));
      const tzOffset = Math.round(-new Date().getTimezoneOffset() / 60);
      loadTodayExercises(uid, tzOffset).catch(e => console.error('loadTodayExercises error:', e));
      loadMetabolicProfile(uid).catch(e => console.error('loadMetabolicProfile error:', e));
      loadAiMiq(uid).catch(e => console.error('loadAiMiq error:', e));
      loadEntitlements(uid).catch(e => console.error('loadEntitlements error:', e));
      loadSubscriptionTiers().catch(e => console.error('loadSubscriptionTiers error:', e));
      loadAiNutritionGoals(uid).catch(e => console.error('loadAiNutritionGoals error:', e));
    } else {
      // Reset flags on error
      setProfileLoaded(true);
      setMealsLoaded(true);
    }
  };

  const handleLogout = async () => {
    // Use the hook's logout function (handles analytics, storage clearing, state reset)
    await authHandleLogout();
    
    // Reset app-specific state
    setUserProfile(null);
    setTodayMeals([]);
    setTodayTotals({ calories: 0, protein: 0, carbs: 0, fat: 0 });
    setIsAdmin(false);
    
    // Reset loading gate flags for next login
    setProfileLoaded(false);
    setMealsLoaded(false);
    setTotalMealsLogged(0);
    
    // Clear entitlements cache on logout
    clearEntitlementsCache();
  };

  // calculateIntelligenceLevel, loadSmartGoals, refreshSmartGoals, calculateDashboardStage
  // now in useNutritionGoals hook

  // ═══════════════════════════════════════════════════════════════════════════════
  // MIQ FOMO SYSTEM - Ambient presence, creates curiosity without explanation
  // ═══════════════════════════════════════════════════════════════════════════════
  
  // Show MIQ nudge after key actions (limit 1 per session)
  const showMiqNudge = (nudgeType) => {
    if (miqNudgeShownThisSession) return; // Only 1 nudge per session
    
    const nudges = {
      photo_log: '+1 MIQ 🧠 factor captured — your intelligence is building',
      barcode_scan: 'High-accuracy data improves MIQ 🧠 calibration',
      micronutrients: 'Micronutrient tracking sharpens MIQ 🧠 accuracy',
      manual_log: 'Meal logged — MIQ 🧠 learning from your patterns',
      first_meal: '🎯 First data point! MIQ 🧠 calibration has begun'
    };
    
    const message = nudges[nudgeType];
    if (message) {
      setMiqNudge(message);
      setMiqNudgeShownThisSession(true);
      // Auto-dismiss after 5 seconds
      setTimeout(() => setMiqNudge(null), 5000);
    }
  };
  
  // Calculate MIQ status text for mini strip (Calibrating → Learning → Optimized)
  // Uses shared CONFIDENCE_FACTOR_CONFIG for consistency with MIQ page
  const getMiqStatusText = () => {
    // Calculate factor counts using shared source of truth
    const hasAncestry = !!(aiNutritionGoals?.intake_data?.ancestry);
    const hasMetabolicBody = !!(userProfile?.body_composition?.body_type || metabolicProfile?.somatotype);
    const hasLifestyleSchedule = !!(aiNutritionGoals?.intake_data?.work_schedule_type || userProfile?.work_schedule_type);
    
    const factorData = calculateFactorCounts({
      profileComplete: !!userProfile?.onboarding_completed,
      assessmentComplete: !!aiNutritionGoals?.intake_complete,
      totalMealsLogged: totalMealsLogged || 0,
      dailyCheckinsCount: dailyCheckinContribution?.consecutive_days || 0,
      sleepVerified: dailyCheckinContribution?.sleep_verified || false,
      stepsVerified: dailyCheckinContribution?.steps_verified || false,
      hasMetabolicBody,
      hasAncestry,
      hasLifestyleSchedule,
      smartMealsLogged: aiMiq?.confidence?.smart_meals_logged || 0,
      savedRecipesLogged: aiMiq?.confidence?.saved_recipes_logged || 0
    });
    
    const factors = factorData.active;
    const total = CONFIDENCE_FACTOR_CONFIG.TOTAL_FACTORS;
    const confidence = aiMiq?.confidence?.overall || 0;
    
    let status = 'Calibrating';
    if (confidence >= 80) {
      status = 'Optimized';
    } else if (confidence >= 50) {
      status = 'Learning';
    } else if (factors >= 3) {
      status = 'Calibrating';
    } else {
      status = 'Initializing';
    }
    
    return { status, factors, total, confidence };
  };

  // getGenericInsight now in useMIQHealth hook

  // ═══════════════════════════════════════════════════════════════════════════════
  // PROFILE SIDE EFFECTS ORCHESTRATOR (strangler pattern)
  // These side effects run AFTER refreshProfile() resolves
  // Order is preserved exactly as before extraction
  // ═══════════════════════════════════════════════════════════════════════════════
  const runProfileSideEffects = async (uid, profileData, profileExists) => {
    if (profileExists && profileData) {
      // 1. Load medical conditions into state
      if (profileData.medical_conditions) {
        setSelectedMedicalConditions(profileData.medical_conditions);
      }
      
      // 1.5 Load food allergies into state
      if (profileData.food_allergies) {
        setSelectedFoodAllergies(profileData.food_allergies.filter(a => a && a !== 'none'));
      }
      
      // 2. Load unit preference from profile
      if (profileData.use_imperial !== undefined) {
        setUseImperial(profileData.use_imperial);
      }
      
      // 3. Load body type guidance if body composition exists
      if (profileData.body_composition && profileData.body_composition.body_type) {
        loadBodyTypeGuidance(profileData.body_composition.body_type);
      }
      
      // 5. Calculate intelligence level based on profile completeness (don't trigger auto-update on initial load)
      const level = calculateIntelligenceLevel(profileData, false);
      
      // 6. Load smart goals on initial profile load
      loadSmartGoals(uid, level);
      
      // 7. Calculate account age for dashboard staging
      if (profileData.created_at) {
        const createdDate = new Date(profileData.created_at);
        const now = new Date();
        const daysDiff = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
        setAccountAgeDays(daysDiff);
      }
      
      // 8. Load cuisine preferences for food recognition
      try {
        const cuisineResponse = await fetch(`${BACKEND_URL}/api/users/${uid}/cuisine-preferences`);
        if (cuisineResponse.ok) {
          const cuisineData = await cuisineResponse.json();
          setCuisinePreferences(cuisineData.cuisine_preferences || []);
          setCuisinePreferencesSet(cuisineData.cuisine_preferences_set || false);
          
          // Show cuisine preferences modal for existing users who haven't set them
          // Only show if they haven't dismissed it before
          const shownBefore = localStorage.getItem(`cuisine_prefs_shown_${uid}`);
          if (!cuisineData.cuisine_preferences_set && !shownBefore) {
            // Delay slightly so it doesn't compete with other modals
            setTimeout(() => {
              setShowCuisinePreferencesModal(true);
            }, 2000);
          }
        }
      } catch (cuisineErr) {
        console.error('Error loading cuisine preferences:', cuisineErr);
      }
    } else {
      // No profile yet - user is at basic level
      // Use calculateIntelligenceLevel(null) which internally sets level to 'basic'
      calculateIntelligenceLevel(null, false);
      setShowQuickStart(true);
      // Load basic smart goals for new users
      loadSmartGoals(uid, 'basic');
    }
    
    // 8. Load subscription status with detailed info from billing endpoint
    try {
      debug.log('Loading subscription status for:', uid);
      const subResponse = await fetch(`${BACKEND_URL}/api/billing/subscription-status/${uid}`);
      if (subResponse.ok) {
        const subData = await subResponse.json();
        debug.log('Subscription data received:', subData);
        setIsPremium(subData.is_premium);
        setSubscriptionDetails(subData);
      } else {
        console.error('Subscription status failed:', subResponse.status);
      }
    } catch (subError) {
      console.error('Error loading subscription:', subError);
    }
    
    // 8.5 Load Health Intelligence Ask Why usage for today
    try {
      const usageRes = await fetch(`${BACKEND_URL}/api/health-intelligence/usage/${uid}`);
      if (usageRes.ok) {
        const usageData = await usageRes.json();
        setAskWhyUsedToday(usageData.count || 0);
      }
    } catch (e) {
      // Non-critical - default to 0
    }
    
    // 8.6 Check admin status
    // (Moved to main auth useEffect for reliability)
    
    // 9. Load nutrition goals if premium
    try {
      const goalsResponse = await fetch(`${BACKEND_URL}/api/nutrition/goals/${uid}`);
      if (goalsResponse.ok) {
        const goalsData = await goalsResponse.json();
        setNutritionGoals(goalsData.goals);
      }
    } catch (e) {
      debug.log('No goals set yet');
    }
    
    // 10. Load advanced goals
    try {
      const advGoalsResponse = await fetch(`${BACKEND_URL}/api/nutrition/goals/advanced/${uid}`);
      if (advGoalsResponse.ok) {
        const advGoalsData = await advGoalsResponse.json();
        if (advGoalsData.goals) {
          setAdvancedGoals(advGoalsData.goals);
        }
      }
    } catch (e) {
      debug.log('No advanced goals set yet');
    }
  };

  // Wrapper function that uses the hook and runs side effects
  // This maintains the same API as the old loadUserProfile
  const loadUserProfile = async (uid) => {
    const { data, exists } = await refreshProfile(uid);
    await runProfileSideEffects(uid, data, exists);
  };

  // Load Metabolic Profile
  // loadMetabolicProfile, generateMetabolicProfile now in useMIQHealth hook

  // Load MIQ - Personal MetaBolicIQ Intelligence Profile
  // loadAiMiq now in useMIQHealth hook

  // Load Daily Check-In status for today
  const loadDailyCheckin = async (uid) => {
    try {
      const tzOffset = Math.round(-new Date().getTimezoneOffset() / 60);
      const response = await fetch(`${BACKEND_URL}/api/ai-miq/daily-checkin/${uid}/today?tz_offset=${tzOffset}`);
      if (response.ok) {
        const data = await response.json();
        setDailyCheckinData(data.data);
        setCheckedInToday(data.checked_in_today || false);
        setDailyCheckinContribution(data.contribution);
        // Pre-populate form with existing data
        if (data.data) {
          // Convert stored sleep_minutes back to hours + mins for display
          const storedMins = data.data.sleep_minutes || 0;
          const sleepHrs = storedMins > 0 ? Math.floor(storedMins / 60) : '';
          const sleepMins = storedMins > 0 ? storedMins % 60 : '';
          
          setCheckinFormData({
            sleep_quality: data.data.sleep_quality || 'good',
            stress_level: data.data.stress_level || 'moderate',
            activity_level: data.data.activity_level || 'moderate',
            notes: data.data.notes || '',
            // v1.1 - Verified source fields (now hours + mins)
            sleep_hours: sleepHrs,
            sleep_mins: sleepMins,
            sleep_source: data.data.sleep_source || 'self_report',
            // DON'T pre-fill steps from stored check-in - let it sync from Activity section
            // This ensures if user deletes steps from Activity, check-in reflects that
            steps_count: '',
            steps_source: data.data.steps_source || 'self_report',
            // v1.2 - Weight
            weight: '',
            weight_unit: data.data.weight_unit || 'kg',
            // v1.3 - Hydration
            hydration_ml: data.data.hydration_ml || 0
          });
        }
      }
      
      // Also fetch weight check-in status
      const weightResponse = await fetch(`${BACKEND_URL}/api/ai-miq/daily-checkin/${uid}/weight-status`);
      if (weightResponse.ok) {
        const weightData = await weightResponse.json();
        setWeightCheckStatus(weightData);
        // Pre-set unit from user preference
        if (weightData.weight_unit) {
          setCheckinFormData(prev => ({ ...prev, weight_unit: weightData.weight_unit }));
        }
      }
      
      // v1.3 - Also fetch hydration status
      loadHydrationStatus(uid);
    } catch (error) {
      console.error('Error loading daily check-in:', error);
    }
  };
  
  // v1.3 - Load Hydration Status for Home Screen
  // Submit Daily Check-In
  const submitDailyCheckin = async (checkinData) => {
    if (!userId) return;
    
    setDailyCheckinLoading(true);
    try {
      const tzOffset = Math.round(-new Date().getTimezoneOffset() / 60);
      const response = await fetch(`${BACKEND_URL}/api/ai-miq/daily-checkin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          sleep_quality: checkinData.sleep_quality,
          stress_level: checkinData.stress_level,
          activity_level: checkinData.activity_level,
          notes: checkinData.notes || null,
          // v1.1 - Verified source fields (convert hours + mins to total minutes)
          sleep_minutes: (checkinData.sleep_hours || checkinData.sleep_mins) 
            ? (parseInt(checkinData.sleep_hours || 0) * 60) + parseInt(checkinData.sleep_mins || 0)
            : null,
          sleep_hours: checkinData.sleep_hours ? parseFloat(checkinData.sleep_hours) + (parseInt(checkinData.sleep_mins || 0) / 60) : null,
          sleep_source: checkinData.sleep_source || 'self_report',
          steps_count: checkinData.steps_count ? parseInt(checkinData.steps_count) : null,
          steps_source: checkinData.steps_source || 'self_report',
          // v1.2 - Weekly weight check-in
          weight: checkinData.weight ? parseFloat(checkinData.weight) : null,
          weight_unit: checkinData.weight_unit || 'kg',
          // v1.3 - Hydration tracking (MVP)
          hydration_ml: checkinData.hydration_ml > 0 ? parseInt(checkinData.hydration_ml) : null,
          // Timezone offset for correct date calculation
          tz_offset: tzOffset
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setCheckedInToday(true);
        setDailyCheckinContribution(data.data?.contribution);
        setShowDailyCheckin(false);
        // Refresh MIQ to reflect updated contribution
        loadAiMiq(userId);
        
        // Refresh hydration status for home screen
        loadHydrationStatus(userId);
        
        // Coach toast after daily check-in
        let toastMsg = "Check-in logged — MIQ accuracy improved.";
        if (data.data?.weight_logged) {
          toastMsg = "Check-in + weight logged — great tracking!";
        }
        showCoachToast(toastMsg, null, null, '✨');
        
        return { success: true, message: data.message };
      } else {
        const error = await response.json();
        return { success: false, message: error.detail || 'Failed to save check-in' };
      }
    } catch (error) {
      console.error('Error submitting daily check-in:', error);
      return { success: false, message: 'Connection error. Please try again.' };
    } finally {
      setDailyCheckinLoading(false);
    }
  };

  // Load user entitlements and tier (with retry for iOS)
  // STRANGLER PATTERN: Uses useSubscription hook for fetch, orchestrates side effects here
  const loadEntitlements = async (uid, retryCount = 0) => {
    // Guard: Don't call without user_id
    if (!uid) {
      console.warn('[Entitlements] No user_id provided, skipping');
      return;
    }
    
    // Call hook's refreshEntitlements (handles dedup, retry, caching)
    const result = await refreshEntitlements(uid, { retryCount });
    
    if (result.success && result.data) {
      const data = result.data;
      
      // Orchestration: Set tester flags (kept in App.js)
      setIsTester(data.is_tester || false);
      setTesterOverrideActive(data.tester_override_active || false);
      
      // Orchestration: Set isPremium based on effective tier
      // Tier precedence is handled by backend: force_override > tester_override > subscription > free
      const effectiveTier = data.tier || 'free';
      const isPremiumTier = effectiveTier !== 'free';
      setIsPremium(isPremiumTier);
      
      debug.log('[Entitlements] Orchestrator: isPremium:', isPremiumTier, 'isTester:', data.is_tester, 'testerOverride:', data.tester_override_active);
      return data;
    }
    
    return null;
  };

  // DEV ONLY: Simulate tier change (TEST_MODE only)
  const simulateTierChange = async (newTier) => {
    if (!isTestMode || !userId) return;
    
    setSimulatingTier(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/dev/simulate-tier`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, tier: newTier })
      });
      
      if (response.ok) {
        const data = await response.json();
        debug.dev('DEV: Tier simulated to', newTier, data);
        
        // Immediately reload entitlements
        await loadEntitlements(userId);
        
        // Reload subscription details from billing endpoint
        try {
          const subResponse = await fetch(`${BACKEND_URL}/api/billing/subscription-status/${userId}`);
          if (subResponse.ok) {
            const subData = await subResponse.json();
            setIsPremium(subData.is_premium);
            setSubscriptionDetails(subData);
          }
        } catch (e) {
          debug.log('Error reloading subscription details');
        }
        
        // Reload MIQ data
        await loadAiMiq(userId);
        
        // Show confirmation
        alert(`TEST ONLY: Tier changed to ${data.tier_name}\n\nEntitlements updated immediately.`);
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to simulate tier'}`);
      }
    } catch (error) {
      console.error('Error simulating tier:', error);
      alert('Failed to simulate tier change');
    } finally {
      setSimulatingTier(false);
    }
  };

  // Load subscription tiers (for pricing display)
  // loadSubscriptionTiers now in useUpgradeFlow hook

  // loadAiNutritionGoals, loadMiqIntakeOptions now in useNutritionGoals hook

  // showUpgrade, handleHomeUpgrade now in useUpgradeFlow hook

  // Complete onboarding and save all data
  const completeOnboarding = async () => {
    try {
      // Convert height if imperial
      let heightCm = parseFloat(onboardingData.height) || 170;
      if (onboardingData.useImperial && onboardingData.heightFt) {
        const ft = parseFloat(onboardingData.heightFt) || 0;
        const inches = parseFloat(onboardingData.heightIn) || 0;
        heightCm = Math.round((ft * 30.48) + (inches * 2.54));
      }
      
      // Convert weight if imperial
      let weightKg = parseFloat(onboardingData.weight) || 70;
      if (onboardingData.useImperial && onboardingData.weightLbs) {
        weightKg = Math.round(parseFloat(onboardingData.weightLbs) * 0.453592);
      }
      
      // Convert goal weight if imperial
      let goalWeightKg = null;
      if (onboardingData.goalWeight) {
        goalWeightKg = parseFloat(onboardingData.goalWeight);
        if (onboardingData.useImperial) {
          goalWeightKg = Math.round(goalWeightKg * 0.453592);
        }
      }
      
      // Save profile with actual onboarding data (not userProfile defaults!)
      const profileData = {
        user_id: userId,
        height_cm: heightCm,
        weight_kg: weightKg,
        age: parseInt(onboardingData.age) || 30,
        gender: onboardingData.gender || 'male',
        activity_level: onboardingData.activityLevel || 'moderate',
        goal_weight_kg: goalWeightKg,
        dietary_preference: onboardingData.dietaryPreference || null,
        use_imperial: onboardingData.useImperial || false
      };
      
      // Save profile
      await fetch(`${BACKEND_URL}/api/users/${userId}/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileData)
      });
      
      // Save medical conditions if selected
      if (onboardingData.medicalConditions.length > 0) {
        await fetch(`${BACKEND_URL}/api/users/${userId}/medical-conditions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ conditions: onboardingData.medicalConditions })
        });
      }
      
      // Save body composition if selected
      if (onboardingData.bodyType) {
        await fetch(`${BACKEND_URL}/api/users/${userId}/body-composition`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            body_type: onboardingData.bodyType,
            body_fat_percentage: onboardingData.bodyFatPercentage || 20,
            muscle_mass: 'Medium',
            build: 'Average'
          })
        });
        
        // Generate metabolic profile
        await fetch(`${BACKEND_URL}/api/metabolic-profile/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId })
        });
      }
      
      // Reload user data AND meals (important for migrated guest meals)
      await loadUserProfile(userId);
      await loadMetabolicProfile(userId);
      await loadTodayMeals(userId); // Refresh meals to show migrated guest meals
      
      setShowOnboarding(false);
      setOnboardingStep(1);
      
      // Nudge user to SmartGoals — this is the key next step after basic profile
      showCoachToast(
        "Profile saved! Now set your SmartGoals to unlock personalized AI nutrition.",
        "Set SmartGoals",
        () => openSmartGoalsGated('post_onboarding'),
        '🧠'
      );
      
      // Show strong prompt to complete SmartGoals
      setTimeout(() => {
        setShowProfileCompletePrompt(true);
      }, 500);
      
    } catch (error) {
      console.error('Error completing onboarding:', error);
    }
  };

  // loadTodayMeals now in useMealData hook

  // MOVED TO useExercises hook: loadTodayExercises, logSteps, logExercise, deleteExercise

  // Load body type guidance for a specific body type
  // loadBodyTypeGuidance, loadAllBodyTypeGuidance, loadPlateauDiagnostics now in useMIQHealth hook

  const generateDailySummary = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/daily-summary/generate/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        setShowDailySummary(true);
        alert('✅ Daily summary generated! Check your History tab to view it.');
        await loadDailySummaries(userId);
      } else {
        const error = await response.json();
        alert(`Failed to generate summary: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error generating daily summary:', error);
      alert(`Failed to generate daily summary: ${error.message}`);
    }
  };

  const loadDailySummaries = async (uid) => {
    try {
      // Include timezone offset for proper date calculation
      const tzOffset = userTimezone || Math.round(-new Date().getTimezoneOffset() / 60);
      const response = await fetch(`${BACKEND_URL}/api/daily-summary/history/${uid}?days=30&tz_offset=${tzOffset}`);
      if (response.ok) {
        const data = await response.json();
        // Sort summaries by date in descending order (newest first) to ensure consistency
        const sortedSummaries = (data.summaries || []).sort((a, b) => 
          new Date(b.date) - new Date(a.date)
        );
        setDailySummaries(sortedSummaries);
        
        // If no summaries exist, try to backfill from historical meal data
        if (sortedSummaries.length === 0) {
          console.log('[Smart Charts] No daily summaries found, triggering backfill...');
          try {
            const backfillResponse = await fetch(`${BACKEND_URL}/api/daily-summary/backfill/${uid}?days=30`, {
              method: 'POST'
            });
            if (backfillResponse.ok) {
              // Reload summaries after backfill
              const reloadResponse = await fetch(`${BACKEND_URL}/api/daily-summary/history/${uid}?days=30&tz_offset=${tzOffset}`);
              if (reloadResponse.ok) {
                const reloadData = await reloadResponse.json();
                const reloadedSummaries = (reloadData.summaries || []).sort((a, b) => 
                  new Date(b.date) - new Date(a.date)
                );
                setDailySummaries(reloadedSummaries);
                console.log('[Smart Charts] Backfill complete, loaded', reloadedSummaries.length, 'summaries');
              }
            }
          } catch (backfillError) {
            console.warn('[Smart Charts] Backfill failed:', backfillError);
          }
        }
      }
    } catch (error) {
      console.error('Error loading daily summaries:', error);
    }
  };

  // Load meal history for Smart Intake History section
  // Includes timezone offset for proper date labeling
  // Load saved recipes from Smart Hub (Smart Swaps saves)
  const loadSavedRecipes = async (uid) => {
    setSavedRecipesLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/smart-hub/favorites/${uid}`);
      if (response.ok) {
        const data = await response.json();
        // Combine home_recipe (Smart Swaps) and healthy_swap recipes
        const recipes = [
          ...(data.home_recipe || []).map(r => ({ ...r, display_type: 'smart_swaps' })),
          ...(data.healthy_swap || []).map(r => ({ ...r, display_type: 'healthy_swap' }))
        ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setSavedRecipes(recipes);
        console.log('[SavedRecipes] Loaded', recipes.length, 'saved recipes');
      }
    } catch (error) {
      console.error('Error loading saved recipes:', error);
    } finally {
      setSavedRecipesLoading(false);
    }
  };

  const loadAdvancedGoals = async (uid) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/nutrition/goals/advanced/${uid}`);
      if (response.ok) {
        const data = await response.json();
        if (data.goals) {
          setAdvancedGoals(data.goals);
        }
      }
    } catch (error) {
      console.error('Error loading advanced goals:', error);
    }
  };

  // loadTrendData removed Feb 23 2026 — Trend Charts modal deprecated.
  // Smart Hub sparklines cover the 7-day glance need; MIQ gauge covers long-term.

  const saveAdvancedGoals = async () => {
    debug.log('[GOALS] saveAdvancedGoals called', { userId, isPremium, goalFormMode, goalFormData });
    
    if (!userId) {
      console.error('[GOALS] No userId - cannot save');
      alert('Please log in to save goals.');
      return;
    }
    
    if (!isPremium) {
      console.error('[GOALS] User not premium - cannot save');
      alert('Premium subscription required to save advanced goals.');
      return;
    }
    
    setAnalyzing(true);
    try {
      const goalWeight = useImperial ? lbsToKg(parseFloat(goalFormData.goal_weight)) : parseFloat(goalFormData.goal_weight);
      debug.log('[GOALS] Sending request with goalWeight:', goalWeight);
      
      const response = await fetch(`${BACKEND_URL}/api/nutrition/goals/advanced`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          goal_type: goalFormMode,
          goal_weight_kg: goalWeight || userProfile?.goal_weight_kg,
          timeline_weeks: parseInt(goalFormData.timeline_weeks) || 12,
          goal_pace: goalFormData.goal_pace || 'standard',
          daily_calorie_target: goalFormData.daily_calorie_target ? parseInt(goalFormData.daily_calorie_target) : null,
          protein_g: goalFormData.protein_g ? parseFloat(goalFormData.protein_g) : null,
          carbs_g: goalFormData.carbs_g ? parseFloat(goalFormData.carbs_g) : null,
          fat_g: goalFormData.fat_g ? parseFloat(goalFormData.fat_g) : null
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAdvancedGoals(data);
        await loadUserProfile(userId);
        await loadTodayMeals(userId);
        alert('✅ Goals saved successfully!');
        setShowAdvancedGoals(false);
        
        // Coach toast after goal saved
        if (!userProfile?.onboarding_completed) {
          showCoachToast(
            "Next: complete your basic profile to sharpen accuracy.",
            "Complete Profile",
            () => setShowOnboarding(true),
            '📋'
          );
        }
      } else {
        const error = await response.json();
        alert(`Failed to save goals: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving advanced goals:', error);
      alert('Failed to save goals. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const addMealPlanToToday = async (meal) => {
    try {
      // Show loading indicator
      toast.loading('Logging meal...', { id: 'meal-log' });
      
      // Use log-recipe endpoint with source='smart_meals' for proper tracking
      const tzOffset = Math.round(-new Date().getTimezoneOffset() / 60);
      const response = await fetch(`${BACKEND_URL}/api/meals/log-recipe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          recipe: {
            title: meal.dish_name,
            macros: {
              calories: meal.calories || 0,
              protein_g: meal.protein || 0,
              carbs_g: meal.carbs || 0,
              fat_g: meal.fat || 0,
              fiber_g: meal.fiber || 0,
              sugar: meal.sugar || 0,
              sodium: meal.sodium || 0,
              potassium: meal.potassium || 0,
              cholesterol: meal.cholesterol || 0,
              vitamin_a: meal.vitamin_a || 0,
              vitamin_c: meal.vitamin_c || 0,
              vitamin_d: meal.vitamin_d || 0,
              calcium: meal.calcium || 0,
              iron: meal.iron || 0
            },
            meal_type: meal.meal_type || 'Other',
            ingredients: meal.recipe?.ingredients,
            instructions: meal.recipe?.instructions
          },
          source: 'smart_meals',
          tz_offset: tzOffset
        })
      });

      if (response.ok) {
        // Dismiss loading toast
        toast.dismiss('meal-log');
        
        // Prepare meal data for post-log sheet (this meal's values)
        const mealData = {
          food_items: meal.dish_name,
          calories: meal.calories || 0,
          protein: meal.protein || 0,
          carbs: meal.carbs || 0,
          fat: meal.fat || 0,
          fiber: meal.fiber || 0,
          sugar: meal.sugar || 0,
          sodium: meal.sodium || 0,
          vitamin_c: meal.vitamin_c || 0,
          iron: meal.iron || 0,
          calcium: meal.calcium || 0
        };
        
        // Fetch fresh totals directly from API for accurate display
        try {
          const totalsResponse = await fetch(`${BACKEND_URL}/api/meals/${userId}/today`);
          if (totalsResponse.ok) {
            const totalsData = await totalsResponse.json();
            const freshTotals = totalsData.totals || {};
            
            // Update state with fresh totals
            setTodayTotals(freshTotals);
            setTodayMeals(totalsData.meals || []);
            
            // Trigger post-meal sheet with fresh totals
            triggerPostMealComplete(freshTotals, mealData, true);
          } else {
            // Fallback to loadTodayMeals if direct fetch fails
            await loadTodayMeals(userId);
            triggerPostMealComplete(null, mealData, true);
          }
        } catch (fetchError) {
          console.error('[addMealPlanToToday] Error fetching fresh totals:', fetchError);
          await loadTodayMeals(userId);
          triggerPostMealComplete(null, mealData, true);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('[addMealPlanToToday] API error:', response.status, errorData);
        toast.error("Couldn't save that meal. Check your connection and try again.", {
          id: 'meal-log',
          duration: 5000
        });
      }
    } catch (error) {
      console.error('[addMealPlanToToday] Error:', error);
      toast.error("Couldn't save that meal. Check your connection and try again.", {
        id: 'meal-log',
        duration: 5000
      });
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // SAVE SMART MEAL TO COOKBOOK (without logging)
  // ─────────────────────────────────────────────────────────────────────────────
  const handleSaveSmartMeal = async (meal, mealId) => {
    setSavingSmartMealId(mealId);
    
    try {
      // Prepare ingredients array from meal
      const ingredients = Array.isArray(meal.ingredients) 
        ? meal.ingredients.map(ing => typeof ing === 'string' ? { name: ing } : ing)
        : [];
      
      // Prepare instructions array
      let instructions = [];
      if (meal.instructions) {
        if (typeof meal.instructions === 'string') {
          instructions = meal.instructions.split('\n').filter(s => s.trim());
        } else if (Array.isArray(meal.instructions)) {
          instructions = meal.instructions;
        }
      }
      
      const response = await fetch(`${BACKEND_URL}/api/smart-hub/favorites/recipe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          recipe_title: meal.title || meal.dish_name,
          cuisine_type: meal.cuisine || '',
          calories: meal.macros?.calories || meal.calories || 0,
          protein: meal.macros?.protein || meal.protein || 0,
          carbs: meal.macros?.carbs || meal.carbs || 0,
          fat: meal.macros?.fat || meal.fat || 0,
          prep_time_minutes: meal.prep_time_minutes || meal.prep_time || 0,
          cook_time_minutes: meal.cook_time_minutes || meal.cook_time || 0,
          difficulty: meal.difficulty || 'medium',
          ingredients: ingredients,
          instructions: instructions,
          tags: meal.tags || [],
          fit_score: meal.fit_score || 0,
          source: 'smart_meals'
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to save recipe');
      }
      
      await response.json();
      
      toast.success(`"${meal.title || meal.dish_name}" saved to your Saved Recipes!`, { 
        duration: 3000,
        description: 'Find it in Smart Hub → Saved Recipes'
      });
      
      // Refresh saved recipes if we're on Smart Hub view
      if (currentView === 'history' && userId) {
        loadSavedRecipes(userId);
      }
      
    } catch (err) {
      console.error('[handleSaveSmartMeal] Error:', err);
      toast.error(err.message || 'Failed to save recipe. Please try again.', { duration: 4000 });
    } finally {
      setSavingSmartMealId(null);
    }
  };

  // Unit conversion helpers
  const cmToFeetInches = (cm) => {
    const totalInches = cm / 2.54;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches % 12);
    return { feet, inches };
  };

  const feetInchesToCm = (feet, inches) => {
    return Math.round((feet * 12 + inches) * 2.54 * 10) / 10;
  };

  const kgToLbs = (kg) => {
    return Math.round(kg * 2.20462 * 10) / 10;
  };

  const lbsToKg = (lbs) => {
    return Math.round(lbs / 2.20462 * 10) / 10;
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    // Handle imperial/metric conversion
    let height_cm, weight_kg;
    
    if (useImperial) {
      const feet = parseInt(formData.get('height_feet'));
      const inches = parseInt(formData.get('height_inches'));
      const lbs = parseFloat(formData.get('weight'));
      height_cm = feetInchesToCm(feet, inches);
      weight_kg = lbsToKg(lbs);
    } else {
      height_cm = parseFloat(formData.get('height'));
      weight_kg = parseFloat(formData.get('weight'));
    }
    
    // Convert goal weight if needed
    let goal_weight_kg = null;
    if (formData.get('goal_weight')) {
      const goalWeight = parseFloat(formData.get('goal_weight'));
      goal_weight_kg = useImperial ? lbsToKg(goalWeight) : goalWeight;
    }
    
    const profileData = {
      user_id: userId,
      height_cm: height_cm,
      weight_kg: weight_kg,
      age: parseInt(formData.get('age')),
      gender: formData.get('gender'),
      activity_level: formData.get('activity_level'),
      goal_weight_kg: goal_weight_kg,
      dietary_preference: formData.get('dietary_preference') || null,
      food_allergies: selectedFoodAllergies,
      medical_conditions: selectedMedicalConditions,
      use_imperial: useImperial
    };

    try {
      const response = await fetch(`${BACKEND_URL}/api/users/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileData)
      });

      if (response.ok) {
        const data = await response.json();
        const newUserId = data.user_id;
        setUserId(newUserId);
        authService.setStoredUserId(newUserId);
        await loadUserProfile(newUserId);
        await loadTodayMeals(newUserId);
        setCurrentView('home');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to save profile');
    }
  };


  // Camera mode management — camera state/handlers now in CameraView
  const openCameraMode = (mode) => {
    setCameraMode(mode);
    setCurrentView('camera');
  };

  const closeCameraMode = () => {
    setCameraMode(null);
    setCurrentView('home');
  };


  // deleteMeal now in useMealData hook

  // ============== EDIT PORTION FUNCTIONS ==============
  
  const openEditPortion = (meal) => {
    // Gate feature to Genius tier
    const isGeniusTier = userTier === 'miq' || userTier === 'genius';
    if (!isGeniusTier) {
      showUpgrade('edit_portion');
      return;
    }
    
    setEditingMeal(meal);
    setPortionPercent(meal.portion_percent || 100);
    setShowEditPortion(true);
  };
  
  // savePortionEdit now in usePostMealFlow hook

  // ============== POST-LOG METABOLIC INSIGHT ==============
  // fetchPostLogInsight, dismissPostLogInsight now in usePostMealFlow hook

  // ============== FAVORITES FUNCTIONS (now in useFavorites hook) ==============

  const getAIGoalSuggestions = async () => {
    if (!userId || !isPremium) return;
    
    const confirmed = window.confirm('Get AI-powered personalized nutrition goals based on your profile?');
    if (!confirmed) return;
    
    setAnalyzing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/nutrition/goals/ai-suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId })
      });
      
      if (response.ok) {
        const goals = await response.json();
        
        // Show AI explanation with body type optimization
        let message = '🧠 SmartGoals\n\n';
        
        // Show body type if available
        if (goals.body_type) {
          message += `💪 Body Type: ${goals.body_type}\n`;
          if (goals.body_fat_percentage) {
            message += `📊 Body Fat: ${goals.body_fat_percentage}%\n`;
          }
          message += '\n';
        }
        
        message += `📊 Daily Macros:\n`;
        message += `• Protein: ${goals.protein_g}g\n`;
        message += `• Carbs: ${goals.carbs_g}g\n`;
        message += `• Fat: ${goals.fat_g}g\n`;
        message += `• Fiber: ${goals.fiber_g}g\n\n`;
        
        // Show body type optimization if available
        if (goals.body_type_optimization) {
          message += `🎯 Body Type Optimization:\n${goals.body_type_optimization}\n\n`;
        }
        
        message += `📝 ${goals.explanation}\n\n`;
        message += 'Would you like to save these goals?';
        
        const save = window.confirm(message);
        
        if (save) {
          // Save goals
          const saveResponse = await fetch(`${BACKEND_URL}/api/nutrition/goals`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: userId,
              ...goals
            })
          });
          
          if (saveResponse.ok) {
            setNutritionGoals(goals);
            alert('✅ Goals saved successfully!');
            await loadTodayMeals(userId);
          }
        }
      } else {
        alert('Failed to get AI suggestions. Please try again.');
      }
    } catch (error) {
      console.error('Error getting AI goals:', error);
      alert('Failed to get AI suggestions.');
    } finally {
      setAnalyzing(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // POST-MEAL COMPLETE FLOW - Unified handler for all meal logging sources
  // Shows PostMealCompleteSheet after successful log + totals update
  // ═══════════════════════════════════════════════════════════════════════════════
  const triggerPostMealComplete = (updatedTotals = null, currentMeal = null, forceShow = false) => {
    // Check cooldown (2-hour soft cooldown) - can be bypassed with forceShow
    if (!forceShow && !shouldShowPostMealSheet()) {
      debug.log('[PostMeal] Skipping - cooldown active');
      return;
    }
    
    // Check if we're in lunchtime window for Smart Picks suggestion
    const hour = new Date().getHours();
    const isLunchWindow = hour >= 11 && hour < 14;
    
    // Get the daily calorie goal
    const currentDailyGoal = aiNutritionGoals?.nutrition_goals?.daily_targets?.calories || 2000;
    
    // Use updated totals if provided (to avoid race condition with state updates)
    const totalsToUse = updatedTotals || todayTotals;
    
    // Set data for the sheet - include current meal's macros and medical alerts
    setPostMealData({
      // Current meal data (what was just logged)
      currentMealName: currentMeal?.food_items || currentMeal?.name || null,
      currentMealCalories: Math.round(currentMeal?.calories || 0),
      currentMealProtein: Math.round(currentMeal?.protein || 0),
      currentMealCarbs: Math.round(currentMeal?.carbs || 0),
      currentMealFat: Math.round(currentMeal?.fat || 0),
      // Medical sensitivity alerts for this meal
      medicalAlerts: currentMeal?.medical_alerts || [],
      // Cumulative totals
      todayCalories: totalsToUse?.calories || 0,
      calorieTarget: currentDailyGoal,
      todayProtein: Math.round(totalsToUse?.protein || 0),
      todayCarbs: Math.round(totalsToUse?.carbs || 0),
      todayFat: Math.round(totalsToUse?.fat || 0),
      // Micronutrient totals (accumulated)
      todayFiber: Math.round(totalsToUse?.fiber || 0),
      todaySugar: Math.round(totalsToUse?.sugar || 0),
      todaySodium: Math.round(totalsToUse?.sodium || 0),
      todayCholesterol: Math.round(totalsToUse?.cholesterol || 0),
      mealsLoggedToday: todayMeals?.length || 0,
      hasAIGoals: !!(aiNutritionGoals?.nutrition_goals?.daily_targets?.calories),
      hasCalorieTarget: !!(currentDailyGoal && currentDailyGoal > 0),
      showSmartSwaps: isLunchWindow || detectedRestaurantText,
      showBarcode: userTier !== 'free'
    });
    
    setShowPostMealComplete(true);
  };
  
  // Handle post-meal primary action
  const handlePostMealAction = (action) => {
    handleCoachAction(action);
  };
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // TEST MODE: Post-Meal Sheet Test Scenarios
  // Call from browser console: window.testPostMealSheet('scenario1') etc.
  // Scenarios:
  //   1. 'missing_profile' - User hasn't completed profile → CTA: "Complete Profile"
  //   2. 'profile_complete' - Profile + goals done → CTA: logical next step (Meal Planner)
  //   3. 'evening_edge' - 6-9 PM with 100-500 kcal remaining → Shows snack suggestion
  // ═══════════════════════════════════════════════════════════════════════════════
  if (typeof window !== 'undefined') {
    window.testPostMealSheet = (scenario) => {
      // Clear cooldown for testing
      localStorage.removeItem('post_meal_sheet_cooldown');
      
      const baseData = {
        todayProtein: 65,
        mealsLoggedToday: 2,
        hasAIGoals: true,
        showSmartSwaps: true,
        showBarcode: true
      };
      
      switch(scenario) {
        case 'missing_profile':
        case 'scenario1':
        case '1':
          setPostMealData({
            ...baseData,
            todayCalories: 1200,
            calorieTarget: 2000,
            hasCalorieTarget: true,
            _testForceNoProfile: true,
            _testForceEvening: false
          });
          break;
        case 'profile_complete':
        case 'scenario2':
        case '2':
          setPostMealData({
            ...baseData,
            todayCalories: 1500,
            calorieTarget: 2000,
            hasCalorieTarget: true,
            _testForceNoProfile: false,
            _testForceEvening: false
          });
          break;
        case 'evening_edge':
        case 'scenario3':
        case '3':
          // Evening with 100-500 kcal remaining (e.g., 1700/2000 = 300 remaining)
          setPostMealData({
            ...baseData,
            todayCalories: 1700,
            calorieTarget: 2000,
            hasCalorieTarget: true,
            _testForceNoProfile: false,
            _testForceEvening: true  // Force evening mode
          });
          break;
        default:
          debug.dev('Available scenarios: missing_profile (1), profile_complete (2), evening_edge (3)');
          return;
      }
      setShowPostMealComplete(true);
      debug.dev(`[TEST] PostMealSheet opened with scenario: ${scenario}`);
    };
    
    // Also expose close function for testing
    window.closePostMealSheet = () => {
      setShowPostMealComplete(false);
      debug.dev('[TEST] PostMealSheet closed');
    };
  }

  const addMenuItemToMeals = async (nutritionData) => {
    // DEV-ONLY: Check for forced failure
    if (shouldForceFailure()) {
      toast.error('[DEV] Forced failure: Menu item meal save blocked', { duration: 5000 });
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/meals/add-manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          ...nutritionData,
          // Include eating-out context if coming from Smart Picks fallback
          ...(eatingOutContext && {
            is_eating_out: true,
            restaurant_name: eatingOutContext.restaurantName
          })
        })
      });

      // Clear eating-out context after meal is logged
      if (eatingOutContext) {
        setEatingOutContext(null);
      }

      if (response.ok) {
        setShowMenuSearch(false);
        setMenuSearchResults(null);
        const updatedTotals = await loadTodayMeals(userId);
        
        // Fetch and show post-log metabolic insight
        const mealData = {
          food_items: nutritionData.food_items,
          calories: nutritionData.calories || 0,
          protein: nutritionData.protein || 0,
          carbs: nutritionData.carbs || 0,
          fat: nutritionData.fat || 0,
          sodium: nutritionData.sodium || 0
        };
        
        // Determine meal type based on time
        const hour = new Date().getHours();
        let mealType = 'Other';
        if (hour >= 5 && hour < 11) mealType = 'Breakfast';
        else if (hour >= 11 && hour < 15) mealType = 'Lunch';
        else if (hour >= 15 && hour < 18) mealType = 'Snack';
        else if (hour >= 18 && hour < 22) mealType = 'Dinner';
        
        await fetchPostLogInsight(mealData, mealType);
        
        // Trigger post-meal complete sheet with updated totals + current meal
        triggerPostMealComplete(updatedTotals, mealData);
      } else {
        // API returned non-200 - show error toast, don't navigate
        const errorData = await response.json().catch(() => ({}));
        console.error('[addMenuItemToMeals] API error:', response.status, errorData);
        toast.error("Couldn't save that meal. Check your connection and try again.", {
          duration: 5000,
          action: {
            label: 'Try again',
            onClick: () => {}
          }
        });
      }
    } catch (error) {
      console.error('[addMenuItemToMeals] Error:', error);
      toast.error("Couldn't save that meal. Check your connection and try again.", {
        duration: 5000,
        action: {
          label: 'Try again',
          onClick: () => {}
        }
      });
    }
  };

  const analyzeManualEntry = async () => {
    if (!manualEntryText.trim()) {
      alert('Please enter a food description');
      return;
    }
    
    if (!userId) {
      alert('Please log in first to analyze food.');
      return;
    }

    setAnalyzing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/meals/analyze-text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description: manualEntryText,
          user_id: userId
        })
      });

      if (response.ok) {
        const data = await response.json();
        setManualEntryResult(data);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Food analysis error:', response.status, errorData);
        alert(`Failed to analyze food: ${errorData.detail || 'Please try again.'}`);
      }
    } catch (error) {
      console.error('Error analyzing manual entry:', error);
      alert(`Failed to analyze food: ${error.message || 'Network error. Please try again.'}`);
    } finally {
      setAnalyzing(false);
    }
  };

  const addManualEntryToMeals = async (overrideResult = null) => {
    const resultToUse = overrideResult || manualEntryResult;
    if (!resultToUse) return;

    // DEV-ONLY: Check for forced failure
    if (shouldForceFailure()) {
      toast.error('[DEV] Forced failure: Manual entry meal save blocked', { duration: 5000 });
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/meals/add-manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          tz_offset: userTimezone,
          ...(selectedDate ? { date: selectedDate } : {}),
          ...resultToUse
        })
      });

      if (response.ok) {
        setShowManualEntry(false);
        setManualEntryText('');
        // Reload the correct date's meals
        if (selectedDate) {
          handleDateChange(selectedDate);
        } else {
          await loadTodayMeals(userId);
        }
        
        // Fetch and show post-log metabolic insight
        const mealData = {
          food_items: resultToUse.food_items,
          calories: resultToUse.calories || 0,
          protein: resultToUse.protein || 0,
          carbs: resultToUse.carbs || 0,
          fat: resultToUse.fat || 0,
          sodium: resultToUse.sodium || 0
        };
        
        // Determine meal type based on time
        const hour = new Date().getHours();
        let mealType = 'Other';
        if (hour >= 5 && hour < 11) mealType = 'Breakfast';
        else if (hour >= 11 && hour < 15) mealType = 'Lunch';
        else if (hour >= 15 && hour < 18) mealType = 'Snack';
        else if (hour >= 18 && hour < 22) mealType = 'Dinner';
        
        setManualEntryResult(null);
        await fetchPostLogInsight(mealData, mealType);
        showMiqNudge('manual_log');
        
        // Trigger post-meal complete sheet with current totals + meal
        triggerPostMealComplete(todayTotals, mealData, true);
      }
    } catch (error) {
      console.error('Error adding meal:', error);
      alert('Failed to add meal.');
    }
  };

  const startBarcodeScanner = async () => {
    setScannerActive(true);
    
    try {
      const html5QrCode = new Html5Qrcode("barcode-reader");
      setScannerInstance(html5QrCode);
      
      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 }
        },
        async (decodedText) => {
          // Barcode detected — stop scanner and show confirmation
          debug.log('Barcode detected:', decodedText);
          
          // Stop scanner
          await html5QrCode.stop();
          setScannerActive(false);
          setScannerInstance(null);
          
          // Show the detected barcode for confirmation (don't auto-lookup)
          setBarcodeInput(decodedText);
        },
        (errorMessage) => {
          // Scanning errors (ignore, they happen frequently)
        }
      );
    } catch (error) {
      console.error('Error starting scanner:', error);
      alert('Failed to start camera. Please check permissions or enter barcode manually.');
      setScannerActive(false);
    }
  };

  const stopBarcodeScanner = async () => {
    if (scannerInstance) {
      try {
        await scannerInstance.stop();
      } catch (error) {
        console.error('Error stopping scanner:', error);
      }
      setScannerInstance(null);
    }
    setScannerActive(false);
  };

  const lookupBarcode = async (barcode) => {
    if (!barcode.trim()) {
      alert('Please enter a barcode number');
      return;
    }

    if (!isPremium) {
      alert('Barcode scanning is a Premium feature. Upgrade to unlock!');
      return;
    }

    setAnalyzing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/meals/scan-barcode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          barcode: barcode,
          user_id: userId
        })
      });

      if (response.ok) {
        const data = await response.json();
        setBarcodeResult(data);
      } else {
        const errorData = await response.json();
        alert(errorData.detail || 'Product not found. Try entering manually.');
      }
    } catch (error) {
      console.error('Error scanning barcode:', error);
      alert('Failed to scan barcode. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const scanBarcode = () => {
    lookupBarcode(barcodeInput);
  };

  // ─── NUTRITION LABEL PHOTO FALLBACK ──────────────────────────────────────
  // When the barcode is unscannable (glossy / curved / damaged) OR the product
  // has no barcode (homemade / store-brand / international), the user can
  // photograph the Nutrition Facts panel instead. Result is normalized to
  // the same `barcodeResult` shape so the existing confirm card just works.
  const scanNutritionLabel = async (file, productNameHint = '') => {
    if (!isPremium) {
      alert('Nutrition label scanning is a Premium feature. Upgrade to unlock!');
      return;
    }
    if (!file) {
      alert('Please select or take a photo of the nutrition panel.');
      return;
    }
    setAnalyzing(true);
    try {
      const reader = new FileReader();
      const base64 = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Could not read image file'));
        reader.readAsDataURL(file);
      });

      const response = await fetch(`${BACKEND_URL}/api/meals/scan-nutrition-label`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_base64: base64,
          user_id: userId,
          product_name: productNameHint,
        })
      });

      if (response.ok) {
        const data = await response.json();
        setBarcodeResult(data);
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.detail || 'Could not read the nutrition panel. Please retake the photo.');
      }
    } catch (error) {
      console.error('[scanNutritionLabel] Error:', error);
      alert('Failed to scan nutrition label. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // DATE NAVIGATION - Load meals for a specific date (past-day editing)
  // ═══════════════════════════════════════════════════════════════════════════════
  const handleDateChange = async (dateStr) => {
    setSelectedDate(dateStr);
    if (!userId) return;
    
    if (!dateStr) {
      // Going back to today - reload today's meals
      loadTodayMeals(userId);
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/meals/${userId}/for-date?date=${dateStr}&tz_offset=${userTimezone}`);
      if (response.ok) {
        const data = await response.json();
        setTodayMeals(data.meals || []);
        setTodayTotals(data.totals || { calories: 0, protein: 0, carbs: 0, fat: 0 });
      }
    } catch (error) {
      console.error('Error loading meals for date:', error);
    }
  };

  const addBarcodeProductToMeals = async ({ saveAsFavorite = false } = {}) => {
    if (!barcodeResult) return;

    // DEV-ONLY: Check for forced failure
    if (shouldForceFailure()) {
      toast.error('[DEV] Forced failure: Barcode meal save blocked', { duration: 5000 });
      return;
    }

    try {
      // Multiply all nutritional values by serving count
      const adjustedProduct = {
        ...barcodeResult,
        calories: Math.round(barcodeResult.calories * barcodeServings),
        protein: parseFloat((barcodeResult.protein * barcodeServings).toFixed(1)),
        carbs: parseFloat((barcodeResult.carbs * barcodeServings).toFixed(1)),
        fat: parseFloat((barcodeResult.fat * barcodeServings).toFixed(1)),
        fiber: parseFloat((barcodeResult.fiber * barcodeServings).toFixed(1)),
        sugar: parseFloat((barcodeResult.sugar * barcodeServings).toFixed(1)),
        sodium: Math.round(barcodeResult.sodium * barcodeServings),
        serving_size: barcodeServings > 1 ? `${barcodeServings} servings` : barcodeResult.serving_size
      };

      // Optionally save as Favorite (per-serving values, not multiplied) so the
      // re-log uses the same baseline as the original scan.
      if (saveAsFavorite) {
        const favoritePayload = {
          user_id: userId,
          name: barcodeResult.food_items || 'Scanned Item',
          food_items: barcodeResult.food_items || '',
          calories: barcodeResult.calories,
          protein: barcodeResult.protein,
          carbs: barcodeResult.carbs,
          fat: barcodeResult.fat,
          fiber: barcodeResult.fiber,
          sugar: barcodeResult.sugar,
          sodium: barcodeResult.sodium,
          serving_size: barcodeResult.serving_size || '1 serving',
          category: 'meal',
          source: barcodeResult.source || 'barcode',
        };
        // Fire-and-forget: don't block meal logging on favorite save
        fetch(`${BACKEND_URL}/api/favorites/add`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(favoritePayload),
        })
          .then(r => r.ok ? loadFavorites && loadFavorites(userId) : null)
          .catch(e => console.warn('[saveAsFavorite] failed:', e));
      }

      const response = await fetch(`${BACKEND_URL}/api/meals/add-manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          tz_offset: userTimezone,
          ...(selectedDate ? { date: selectedDate } : {}),
          ...adjustedProduct
        })
      });

      if (response.ok) {
        setShowBarcodeScanner(false);
        setBarcodeInput('');
        setBarcodeServings(1);
        let updatedTotals = null;
        if (selectedDate) {
          handleDateChange(selectedDate);
        } else {
          updatedTotals = await loadTodayMeals(userId);
        }
        
        // Fetch and show post-log metabolic insight
        const mealData = {
          food_items: adjustedProduct.food_items || adjustedProduct.product_name,
          calories: adjustedProduct.calories,
          protein: adjustedProduct.protein,
          carbs: adjustedProduct.carbs,
          fat: adjustedProduct.fat,
          sodium: adjustedProduct.sodium || 0
        };
        
        // Determine meal type based on time
        const hour = new Date().getHours();
        let mealType = 'Snack'; // Barcode items are usually snacks
        if (hour >= 5 && hour < 11) mealType = 'Breakfast';
        else if (hour >= 11 && hour < 15) mealType = 'Lunch';
        else if (hour >= 18 && hour < 22) mealType = 'Dinner';
        
        setBarcodeResult(null);
        await fetchPostLogInsight(mealData, mealType);
        showMiqNudge('barcode_scan'); // MIQ FOMO nudge for barcode scans
        
        // Trigger post-meal complete sheet with updated totals + current meal
        triggerPostMealComplete(updatedTotals, mealData);
      } else {
        // API returned non-200 - show error toast, don't navigate
        const errorData = await response.json().catch(() => ({}));
        console.error('[addBarcodeProduct] API error:', response.status, errorData);
        toast.error("Couldn't save that meal. Check your connection and try again.", {
          duration: 5000,
          action: {
            label: 'Try again',
            onClick: () => {}
          }
        });
      }
    } catch (error) {
      console.error('[addBarcodeProduct] Error:', error);
      toast.error("Couldn't save that meal. Check your connection and try again.", {
        duration: 5000,
        action: {
          label: 'Try again',
          onClick: () => {}
        }
      });
    }
  };

  // Calculate remaining calories - Prioritize SmartGoals
  // Priority: SmartGoals > Advanced Goals > User Profile > Default
  const dailyGoal = aiNutritionGoals?.nutrition_goals?.daily_targets?.calories 
    || advancedGoals?.daily_calorie_target 
    || userProfile?.daily_calories 
    || 2000;
  
  // AI-based macro targets (prioritize SmartGoals)
  const targetProtein = aiNutritionGoals?.nutrition_goals?.daily_targets?.protein_g 
    || advancedGoals?.protein_g 
    || nutritionGoals?.protein_g 
    || 50;
  const targetCarbs = aiNutritionGoals?.nutrition_goals?.daily_targets?.carbs_g 
    || advancedGoals?.carbs_g 
    || nutritionGoals?.carbs_g 
    || 250;
  const targetFat = aiNutritionGoals?.nutrition_goals?.daily_targets?.fat_g 
    || advancedGoals?.fat_g 
    || nutritionGoals?.fat_g 
    || 65;
  
  const remainingCalories = dailyGoal - todayTotals.calories;
  const progressPercent = Math.min((todayTotals.calories / dailyGoal) * 100, 100);
  
  // Calculate macro progress percentages
  const proteinProgress = Math.min((todayTotals.protein / targetProtein) * 100, 100);
  const carbsProgress = Math.min((todayTotals.carbs / targetCarbs) * 100, 100);
  const fatProgress = Math.min((todayTotals.fat / targetFat) * 100, 100);
  
  // Check if AI goals are active (for UI indicators)
  const hasAiGoals = aiNutritionGoals?.intake_complete && aiNutritionGoals?.nutrition_goals?.daily_targets;

  // ═══════════════════════════════════════════════════════════════════════════════
  // GUEST MODE HELPER FUNCTIONS
  // These must be defined before any conditional returns
  // ═══════════════════════════════════════════════════════════════════════════════
  
  // Handle guest meal success and show appropriate flow
  const handleGuestMealSuccess = (mealData) => {
    debug.guest('[GuestMeal] handleGuestMealSuccess called with:', mealData);
    debug.guest('[GuestMeal] mealData fields - calories:', mealData.calories, 'protein_g:', mealData.protein_g, 'carbs_g:', mealData.carbs_g, 'fat_g:', mealData.fat_g);
    
    // Use the hook's addGuestMeal function which handles state and localStorage
    const normalizedMeal = addGuestMeal(mealData);
    const newMealCount = guestMealCount + 1;
    
    debug.guest('[GuestMeal] Added meal via hook, new count:', newMealCount);
    debug.guest('[GuestMeal] Setting currentView to home, isGuestMode:', isGuestMode, 'isAuthenticated:', isAuthenticated);
    
    // NEW FLOW: Go to dashboard and let user see their meal data
    // Don't auto-popup profile setup - let them experience the value first
    setCurrentView('home');
    
    // Show success toast
    if (newMealCount === 1) {
      toast.success('Meal logged! 🎉', {
        description: 'Your progress is shown below',
        duration: 4000
      });
    } else if (newMealCount >= 2 && !guestProfile) {
      // Gentle reminder after 2nd meal
      toast('Personalize your targets for better insights', {
        action: {
          label: 'Set Up',
          onClick: () => setShowPersonalizeTargets(true)
        },
        duration: 5000
      });
    }
  };
  
  // Calculate targets from guest profile
  const calculateGuestTargets = (profile) => {
    // Simple BMR calculation (Mifflin-St Jeor)
    let bmr;
    if (profile.gender === 'male') {
      bmr = 10 * profile.weight + 6.25 * profile.height - 5 * profile.age + 5;
    } else {
      bmr = 10 * profile.weight + 6.25 * profile.height - 5 * profile.age - 161;
    }
    
    // Activity multiplier
    const activityMultipliers = {
      sedentary: 1.2,
      light: 1.375,
      moderate: 1.55,
      active: 1.725
    };
    const tdee = bmr * (activityMultipliers[profile.activityLevel] || 1.2);
    
    // Goal adjustment
    let calories;
    if (profile.goal === 'lose') {
      calories = Math.round(tdee - 500);
    } else if (profile.goal === 'gain') {
      calories = Math.round(tdee + 300);
    } else {
      calories = Math.round(tdee);
    }
    
    // Macro calculation (balanced split)
    const protein = Math.round(profile.weight * 1.6); // 1.6g per kg
    const fat = Math.round((calories * 0.25) / 9);
    const carbs = Math.round((calories - protein * 4 - fat * 9) / 4);
    
    return { calories, protein, carbs, fat };
  };
  
  // Handle personalization completion
  const handlePersonalizationComplete = (profileData) => {
    const profile = {
      ...profileData,
      height_cm: parseFloat(profileData.height),  // Store as height_cm for backend compatibility
      weight_kg: parseFloat(profileData.weight),  // Store as weight_kg for backend compatibility
      height: parseFloat(profileData.height),     // Keep for local calculations
      weight: parseFloat(profileData.weight),     // Keep for local calculations
      age: parseInt(profileData.age),
      useImperial: profileData.useImperial || false
    };
    
    // Update profile using the hook (handles localStorage persistence)
    updateGuestProfile(profile);
    const targets = calculateGuestTargets(profile);
    setCalculatedGuestTargets(targets);
    
    // Also sync targets to hook for complete persistence
    setGuestTargetsInHook(targets);
    
    setShowPersonalizeTargets(false);
    
    // NEW FLOW: After profile, show SmartGoals nudge with updated targets
    setShowTargetsUpdated(true);
  };
  
  // Handle targets updated sheet action - flow to SmartGoals or Sign Up
  const handleTargetsUpdatedAction = (action) => {
    setShowTargetsUpdated(false);
    
    if (action === 'smartgoals') {
      // Show SmartGoals modal for guests
      openSmartGoalsGated('button');
    } else if (action === 'signup') {
      // Show signup prompt with trial messaging
      setShowSignUpPrompt(true);
    } else {
      // Continue to dashboard
      toast.success('Your targets are set! 🎯', {
        description: 'Start logging meals to see your progress',
        duration: 3000
      });
    }
  };
  
  // Listen for sign-in request from guest landing
  useEffect(() => {
    const handleShowSignIn = () => {
      setShowAuthFromGuest(true);
    };
    window.addEventListener('showSignIn', handleShowSignIn);
    return () => window.removeEventListener('showSignIn', handleShowSignIn);
  }, []);

  // Show loading while checking auth
  if (authLoading) {
    return <AuthLoadingScreen />;
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // GUEST MODE - Frictionless onboarding flow
  // Show guest landing screen or auth page based on state
  // ═══════════════════════════════════════════════════════════════════════════════

  // Show auth page if user explicitly requests sign-in from guest mode
  if (showAuthFromGuest && !isAuthenticated) {
    return (
      <AuthPage
        authView={authView}
        setAuthView={setAuthView}
        authForm={authForm}
        setAuthForm={setAuthForm}
        authError={authError}
        setAuthError={setAuthError}
        handleLogin={handleLogin}
        handleSignup={handleSignup}
        handleGoogleSignIn={handleGoogleSignIn}
        pendingPromoCode={pendingPromoCode}
        setPendingPromoCode={setPendingPromoCode}
        onBack={() => setShowAuthFromGuest(false)}
      />
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // PROTOTYPE MODE - UI-only mockups for navigation restructure
  // Access via: ?prototype=true
  // ═══════════════════════════════════════════════════════════════════════════════
  const isPrototypeMode = (() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('prototype') === 'true';
  })();

  if (isPrototypeMode) {
    return (
      <Suspense fallback={<div style={{ 
        minHeight: '100vh', 
        background: '#08080d', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: 'white'
      }}>Loading prototype...</div>}>
        <PrototypeRouter />
      </Suspense>
    );
  }

  // Show guest landing screen if in guest mode and no meals logged yet
  if (!isAuthenticated && isGuestMode && guestMealCount === 0 && currentView !== 'camera') {
    return (
      <>
        <Toaster position="top-center" richColors />
        <GuestLandingScreen
          onTakePhoto={() => {
            setCurrentView('camera');
            openCameraMode('camera');
          }}
          onUploadPhoto={() => {
            // Note: This is a fallback - the component now handles file input internally
            setCurrentView('camera');
            openCameraMode('upload');
          }}
          onImageSelected={(base64Image) => {
            // Set pending image and open camera view — CameraView will auto-analyze
            setPendingCameraImage(base64Image);
            openCameraMode('upload');
          }}
          onManualEntry={() => setShowManualEntry(true)}
        />
        
        {/* Manual Entry Modal for guests */}
        {showManualEntry && (
          <ManualEntryModal
            manualEntryText={manualEntryText}
            manualEntryResult={manualEntryResult}
            analyzing={analyzing}
            setManualEntryText={setManualEntryText}
            setManualEntryResult={setManualEntryResult}
            setShowManualEntry={setShowManualEntry}
            analyzeManualEntry={async () => {
              // For guests, we'll use the analyze endpoint with a guest user ID
              if (!manualEntryText.trim()) return;
              setAnalyzing(true);
              try {
                const response = await fetch(`${BACKEND_URL}/api/meals/analyze-text`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    description: manualEntryText,
                    user_id: 'guest_user'
                  })
                });
                if (response.ok) {
                  const data = await response.json();
                  setManualEntryResult(data);
                }
              } catch (error) {
                console.error('Error analyzing text:', error);
              }
              setAnalyzing(false);
            }}
            addManualEntryToMeals={async () => {
              // For guests, store meal locally
              if (manualEntryResult) {
                const mealData = {
                  id: `guest_${Date.now()}`,
                  food_items: manualEntryText,
                  calories: manualEntryResult.calories,
                  protein_g: manualEntryResult.protein || 0,
                  carbs_g: manualEntryResult.carbs || 0,
                  fat_g: manualEntryResult.fat || 0,
                  timestamp: new Date().toISOString()
                };
                handleGuestMealSuccess(mealData);
                setShowManualEntry(false);
                setManualEntryText('');
                setManualEntryResult(null);
                toast.success('Meal logged!');
              }
            }}
          />
        )}
      </>
    );
  }

  // Show auth page if not authenticated and not in guest mode
  if (!isAuthenticated && !isGuestMode) {
    return (
      <AuthPage
        authView={authView}
        setAuthView={setAuthView}
        authForm={authForm}
        setAuthForm={setAuthForm}
        authError={authError}
        setAuthError={setAuthError}
        handleLogin={handleLogin}
        handleSignup={handleSignup}
        handleGoogleSignIn={handleGoogleSignIn}
        pendingPromoCode={pendingPromoCode}
        setPendingPromoCode={setPendingPromoCode}
      />
    );
  }

  // Intelligence Level Indicator - Wrapper using extracted component
  const renderIntelligenceLevelIndicator = (compact = false) => (
    <IntelligenceLevelIndicator
      compact={compact}
      intelligenceLevel={intelligenceLevel}
      smartGoals={smartGoals}
      smartGoalsLoading={smartGoalsLoading}
      userProfile={userProfile}
      nutritionGoals={nutritionGoals}
      isPremium={isPremium}
      userTier={userTier}
      onRefreshGoals={refreshSmartGoals}
      onShowOnboarding={() => setShowOnboarding(true)}
      onShowAiNutritionGoals={() => openSmartGoalsGated('button')}
      loadMiqIntakeOptions={loadMiqIntakeOptions}
      loadAiNutritionGoals={loadAiNutritionGoals}
      userId={userId}
    />
  );

  // Quick Start Home - Shown when user has no meals logged
  // QuickStartHome - Extracted to components/home/QuickStartHome.js
  const renderQuickStartHome = () => (
    <QuickStartHome
      userName={authUser?.name?.split(' ')[0]}
      userTier={userTier}
      isPremium={isPremium}
      subscriptionDetails={subscriptionDetails}
      IntelligenceLevelIndicator={() => renderIntelligenceLevelIndicator(false)}
      onSetupProfile={() => setShowOnboarding(true)}
      onTakePhoto={() => openCameraMode('camera')}
      onManualEntry={() => setShowManualEntry(true)}
      onBarcodeScanner={() => setShowBarcodeScanner(true)}
      onShowUpgrade={showUpgrade}
      onNavigateToMIQ={() => setCurrentView('miq')}
      onSmartGoals={() => openSmartGoalsGated('button')}
      onSmartMeals={() => setShowMealPlanner(true)}
      onSmartSwaps={openSmartSwaps}
      onUpgradeGenius={() => handleHomeUpgrade('genius_plaite_monthly')}
    />
  );

  // MiqIntakeForm - Extracted to components/miq/MiqIntakeForm.js
  // Used in AINutritionGoalsModal when user starts MIQ intake flow

  return (
    <div className="app">
      {/* Session Expired Modal - Global handler for auth failures */}
      <SessionExpiredModal 
        onLogin={() => {
          handleLogout();
          setCurrentView('auth');
        }}
      />
      
      {/* Tester Diagnostics Panel (Lazy Loaded) */}
      {isTester && showDiagnosticsPanel && (
        <Suspense fallback={<ViewLoadingFallback />}>
          <TesterDiagnosticsPanel
            isOpen={showDiagnosticsPanel}
            onClose={() => setShowDiagnosticsPanel(false)}
            userId={userId}
            userEmail={authUser?.email}
            userTier={userTier}
            entitlements={userEntitlements}
          />
        </Suspense>
      )}
      
      {/* Goals Update Toast Notification */}
      {goalsToast && (
        <div style={{
          position: 'fixed',
          top: authUser ? '120px' : '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10001,
          background: goalsToast.type === 'upgrade' 
            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
            : 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          maxWidth: '90%',
          width: '360px',
          animation: 'slideDown 0.3s ease-out'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <span style={{ fontSize: '20px' }}>{goalsToast.icon}</span>
            <span style={{ fontWeight: '700', fontSize: '14px' }}>{goalsToast.title}</span>
            <button
              onClick={() => setGoalsToast(null)}
              style={{
                marginLeft: 'auto',
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                cursor: 'pointer',
                color: 'white',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >×</button>
          </div>
          <p style={{ margin: 0, fontSize: '12px', opacity: 0.9, lineHeight: 1.4 }}>
            {goalsToast.message}
          </p>
        </div>
      )}
      
      {/* Compact Header for logged-in users - FIXED position so it's always visible */}
      {authUser ? (
        <header style={{
          background: 'rgba(6, 6, 16, 0.85)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255,255,255,0.04)',
          position: 'sticky',
          top: 0,
          width: '100%',
          zIndex: 9999,
          boxSizing: 'border-box',
          paddingTop: 'env(safe-area-inset-top)',
          WebkitTransform: 'translateZ(0)',
          transform: 'translateZ(0)'
        }}>
          {/* Top bar: Brand + User */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 16px',
            borderBottom: '1px solid rgba(255,255,255,0.04)',
            minHeight: '40px'
          }}>
            {/* Left side: Logo */}
            <span data-testid="app-header-logo" style={{ flexShrink: 0 }}>
              <SmartPlAIteWordmark fontSize={18} />
            </span>
            
            {/* Right side: Badges + User info */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              overflow: 'hidden',
              maxWidth: '70%'
            }}>
              {/* Trial/Premium badge */}
              {subscriptionDetails.is_trial && subscriptionDetails.trial_days_remaining > 0 && (
                <span 
                  onClick={() => setCurrentView('subscription')}
                  style={{ 
                    background: 'linear-gradient(135deg, #7C5CFC 0%, #3B82F6 100%)', 
                    color: 'white', 
                    padding: '3px 8px', 
                    borderRadius: '10px', 
                    fontSize: '9px',
                    fontFamily: "'Outfit', sans-serif",
                    fontWeight: '600',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    letterSpacing: '0.02em'
                  }}
                >
                  {subscriptionDetails.trial_days_remaining}d trial
                </span>
              )}
              {isPremium && !subscriptionDetails.is_trial && (
                <span style={{ 
                  background: 'linear-gradient(135deg, #00E5A0 0%, #00C48C 100%)', 
                  color: '#060610', 
                  padding: '3px 8px', 
                  borderRadius: '10px', 
                  fontSize: '9px',
                  fontFamily: "'Outfit', sans-serif",
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  letterSpacing: '0.02em'
                }}>
                  PRO
                </span>
              )}
              {/* TESTER badge - compact */}
              {isTester && (
                <span style={{ 
                  background: 'rgba(245, 158, 11, 0.15)', 
                  color: '#F59E0B', 
                  padding: '3px 8px', 
                  borderRadius: '10px', 
                  fontSize: '8px',
                  fontFamily: "'Outfit', sans-serif",
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  border: '1px solid rgba(245, 158, 11, 0.2)'
                }}>
                  TESTER
                </span>
              )}
              {authUser.picture && (
                <img 
                  src={authUser.picture} 
                  alt="" 
                  style={{ width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0 }}
                />
              )}
              <span style={{ 
                fontSize: '10px',
                fontFamily: "'Manrope', sans-serif",
                color: '#A1A1AA',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: '30px',
                maxWidth: '60px'
              }}>
                {authUser.name?.split(' ')[0] || authUser.email?.split('@')[0]}
              </span>
              <button
                onClick={handleLogout}
                data-testid="header-logout-btn"
                style={{
                  padding: '3px 8px',
                  background: 'rgba(239, 68, 68, 0.10)',
                  border: '1px solid rgba(239, 68, 68, 0.20)',
                  borderRadius: '8px',
                  color: '#EF4444',
                  fontSize: '9px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                Logout
              </button>
            </div>
          </div>
          
          {/* Navigation tabs */}
          {currentView !== 'camera' && (
            <nav style={{
              display: 'flex',
              justifyContent: 'flex-start',
              gap: '4px',
              padding: '8px 16px 10px',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              borderBottom: '1px solid rgba(255,255,255,0.04)',
            }}>
              <button 
                className={`nav-link ${currentView === 'home' ? 'active' : ''}`}
                onClick={() => setCurrentView('home')}
              >
                Home
              </button>
              <button 
                className={`nav-link ${currentView === 'if' ? 'active' : ''}`}
                onClick={() => {
                  if (userTier === 'free') {
                    showUpgrade('smart_timer');
                  } else {
                    setCurrentView('if');
                  }
                }}
                style={{
                  background: currentView === 'if' 
                    ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(139, 195, 74, 0.2) 100%)' 
                    : ifFastingActive 
                      ? 'rgba(76, 175, 80, 0.08)' 
                      : undefined,
                  borderColor: currentView === 'if' ? '#4CAF50' : ifFastingActive ? 'rgba(76, 175, 80, 0.4)' : undefined,
                  position: 'relative',
                  boxShadow: ifFastingActive && currentView !== 'if' ? '0 0 8px rgba(76, 175, 80, 0.3)' : undefined,
                  opacity: userTier === 'free' ? 0.7 : 1
                }}
              >
                {userTier === 'free' ? '🔒' : '🧠'} MIQ Rhythm
                {/* Active fasting badge with time remaining */}
                {ifFastingActive && ifTimeRemaining && formatIfBadgeTime(ifTimeRemaining) && (
                  <span 
                    style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      background: 'linear-gradient(135deg, #4CAF50, #66BB6A)',
                      color: 'white',
                      fontSize: '9px',
                      fontWeight: '600',
                      padding: '2px 5px',
                      borderRadius: '8px',
                      minWidth: '20px',
                      textAlign: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      animation: 'subtle-pulse 3s ease-in-out infinite'
                    }}
                  >
                    ⏱ {formatIfBadgeTime(ifTimeRemaining)}
                  </span>
                )}
                {/* Inactive state - "Start" micro-label */}
                {!ifFastingActive && currentView !== 'if' && (
                  <span 
                    style={{
                      position: 'absolute',
                      bottom: '-6px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontSize: '8px',
                      fontWeight: '500',
                      color: 'rgba(76, 175, 80, 0.7)',
                      letterSpacing: '0.5px'
                    }}
                  >
                    Start
                  </span>
                )}
              </button>
              {/* HEALTH SYNC TAB — Smart Hub subtab, mirrors the dashboard hierarchy
                   Smart Hub
                   ─────────
                   Health Sync
                  Tapping it opens Smart Hub view AND forces the health-sync
                  wheel section open so the card lands at the top of the dashboard.
                  This replaces the standalone "Smart Hub" tab — combined into one. */}
              <button
                data-testid="health-sync-nav-tab"
                className={`nav-link ${currentView === 'history' ? 'active' : ''}`}
                onClick={() => {
                  if (userTier === 'free') {
                    // Smart Hub itself requires Smart+ tier; let the existing upsell handle it
                    showUpgrade('smart_hub');
                    return;
                  }
                  // Ensure the Health Sync wheel section is open so HistoryView
                  // pins the Health Sync card to the top of the dashboard.
                  if (!hubOpenSections.has('health-sync')) {
                    toggleHubSection('health-sync');
                  }
                  setCurrentView('history');
                }}
                style={{
                  background: currentView === 'history'
                    ? 'linear-gradient(135deg, rgba(168, 85, 247, 0.18) 0%, rgba(99, 102, 241, 0.18) 100%)'
                    : undefined,
                  borderColor: currentView === 'history' ? '#a855f7' : undefined,
                  opacity: userTier === 'free' ? 0.7 : 1,
                  position: 'relative',
                  display: 'inline-flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  lineHeight: 1.15,
                  padding: '6px 12px'
                }}
              >
                <span style={{
                  fontSize: '8px',
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'rgba(168, 85, 247, 0.75)',
                  marginBottom: '1px'
                }}>
                  Smart Hub
                </span>
                <span style={{
                  display: 'block',
                  width: '60%',
                  height: '1px',
                  background: 'rgba(168, 85, 247, 0.35)',
                  marginBottom: '2px'
                }} />
                <span style={{ fontSize: '12px', fontWeight: 600 }}>
                  {userTier === 'free' ? '🔒 ' : '📡 '}Health Sync
                </span>
              </button>
              <button 
                className={`nav-link ${currentView === 'miq' ? 'active' : ''}`}
                onClick={() => {
                  setCurrentView('miq');
                  markMIQVisited();
                }}
                style={{
                  background: currentView === 'miq' ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)' : undefined,
                  borderColor: currentView === 'miq' ? '#667eea' : undefined
                }}
              >
                🧠 MIQ
              </button>
              <button 
                className={`nav-link ${currentView === 'profile' ? 'active' : ''}`}
                onClick={() => setCurrentView('profile')}
              >
                Profile
              </button>
              <button 
                className={`nav-link ${currentView === 'settings' ? 'active' : ''}`}
                onClick={() => setCurrentView('settings')}
              >
                Settings
              </button>
              {isAdmin && (
                <button 
                  ref={adminNavRef}
                  data-testid="admin-nav-tab"
                  className={`nav-link ${currentView === 'admin' ? 'active' : ''}`}
                  onClick={() => setCurrentView('admin')}
                  style={{
                    background: currentView === 'admin' ? 'rgba(239, 68, 68, 0.15)' : undefined,
                    borderColor: currentView === 'admin' ? '#ef4444' : undefined,
                    color: currentView === 'admin' ? '#ef4444' : undefined,
                  }}
                >
                  Admin
                </button>
              )}
            </nav>
          )}
          {/* Quick Action Bar - inside header for proper sticky on all devices */}
          {currentView === 'home' && (
            <QuickActionBar 
              onTakePhoto={() => openCameraMode('camera')}
              onLogMeal={() => openCameraMode('upload')}
              onManualEntry={() => setShowManualEntry(true)}
              onBarcode={() => setShowBarcodeScanner(true)}
            />
          )}
        </header>
      ) : (
        /* Full header for non-logged-in users */
        <header className="header">
          <h1 className="brand-display">SmartPlAIte</h1>
          <p className="tagline">Track your meals with AI</p>
        </header>
      )}

      {/* Intelligence Setup Modal (soft-gated onboarding) */}
      <OnboardingModal
        show={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onboardingStep={onboardingStep}
        setOnboardingStep={setOnboardingStep}
        onboardingData={onboardingData}
        setOnboardingData={setOnboardingData}
        userId={userId}
        BACKEND_URL={BACKEND_URL}
        setUseImperial={setUseImperial}
        loadUserProfile={loadUserProfile}
        loadAiMiq={loadAiMiq}
        loadAiNutritionGoals={loadAiNutritionGoals}
      />
      
      {/* Profile Complete Prompt - Strong nudge to complete AI Goals */}
      <ProfileCompletePrompt
        show={showProfileCompletePrompt}
        onClose={() => setShowProfileCompletePrompt(false)}
        getMiqStatusText={getMiqStatusText}
        aiNutritionGoals={aiNutritionGoals}
        userProfile={userProfile}
        metabolicProfile={metabolicProfile}
        onOpenAiGoals={() => openSmartGoalsGated('button')}
        onNavigateToMIQ={() => setCurrentView('miq')}
      />
      
      {/* First Steps Journey - Welcome flow for new users */}
      <FirstStepsJourney 
        show={showFirstStepsJourney}
        onClose={() => setShowFirstStepsJourney(false)}
        openCameraMode={openCameraMode}
      />
      
      {/* Premium Journey Prompt - Shown after first meal */}
      <PremiumJourneyPrompt 
        show={showPremiumPrompt}
        onClose={() => setShowPremiumPrompt(false)}
        onUpgrade={() => {
          setShowPremiumPrompt(false);
          setCurrentView('subscription');
        }}
        onCompleteProfile={() => {
          setShowPremiumPrompt(false);
          setShowOnboarding(true);
        }}
      />
      
      {/* First Meal Insight Modal */}
      <FirstMealInsightModal 
        show={showFirstMealInsight}
        mealData={firstMealData}
        userProfile={userProfile}
        userTier={userTier}
        onClose={() => setShowFirstMealInsight(false)}
        onCompleteProfile={() => setShowOnboarding(true)}
        onShowPremiumPrompt={() => setShowPremiumPrompt(true)}
        getGenericInsight={getGenericInsight}
      />

      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {/* UPGRADE MODAL - Shows when user hits locked feature */}
      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      <UpgradeModal
        show={showUpgradeModal}
        upgradeContext={upgradeContext}
        onClose={() => setShowUpgradeModal(false)}
        userId={userId}
        currentTier={userTier}
      />

      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {/* $4.99 PREMIUM TRACKING UPSELL MODAL - Exact copy as specified */}
      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      <PremiumTrackingModal
        show={showPremiumTrackingModal}
        onClose={() => setShowPremiumTrackingModal(false)}
        onSubscribe={() => {
          setShowPremiumTrackingModal(false);
          setCurrentView('settings');
        }}
      />      {/* SmartGoals Modal */}
      {showAiNutritionGoals && (
        <AINutritionGoalsModal
          aiNutritionGoals={aiNutritionGoals}
          miqIntakeOptions={miqIntakeOptions}
          userProfile={userProfile}
          metabolicProfile={metabolicProfile}
          todayMeals={todayMeals}
          userId={userId}
          dailyCheckinContribution={dailyCheckinContribution}
          checkedInToday={checkedInToday}
          BACKEND_URL={BACKEND_URL}
          setShowAiNutritionGoals={setShowAiNutritionGoals}
          showUpgrade={showUpgrade}
          loadAiMiq={loadAiMiq}
          loadMiqIntakeOptions={loadMiqIntakeOptions}
          loadAiNutritionGoals={loadAiNutritionGoals}
          setAiNutritionGoals={setAiNutritionGoals}
          setShowMealPlanner={setShowMealPlanner}
          setShowDailyCheckin={setShowDailyCheckin}
          showCoachToast={showCoachToast}
          setCurrentView={setCurrentView}
          MiqIntakeForm={MiqIntakeForm}
          aiMiq={aiMiq}
          userTier={userTier}
        />
      )}

      {/* Main Content */}
      <main className="main-content">
        
        {/* Home View - Main dashboard (always shown for authenticated users) */}
        {currentView === 'home' && userId && (
          <HomeView
            // Data props
            todayTotals={todayTotals}
            dailyGoal={dailyGoal}
            targetProtein={targetProtein}
            targetCarbs={targetCarbs}
            targetFat={targetFat}
            todayMeals={todayMeals}
            favorites={favorites}
            exercises={todayExercises}
            totalCaloriesBurned={totalCaloriesBurned}
            dailySteps={dailySteps}
            micros={todayTotals}
            rdaValues={getRDAValues()}
            hasAiGoals={hasAiGoals}
            userTier={userTier}
            miqStatus={getMiqStatusText().status}
            miqFactorsActive={getMiqStatusText().factors}
            miqTotalFactors={CONFIDENCE_FACTOR_CONFIG.TOTAL_FACTORS}
            flexibleMacroMode={aiNutritionGoals?.daily_targets?.flexible_macro_mode || false}
            // Action handlers
            onTakePhoto={() => openCameraMode('camera')}
            onLogMeal={() => openCameraMode('upload')}
            onManualEntry={() => setShowManualEntry(true)}
            onSetGoals={() => openSmartGoalsGated('button')}
            onNavigateToMIQ={() => setCurrentView('miq')}
            onFavoriteMeal={(meal) => {
              const name = window.prompt('Name this favorite:', meal.food_items?.substring(0, 30) || 'My Favorite');
              if (name) addToFavorites(meal, name);
            }}
            onRemoveMeal={async (mealId) => {
              await deleteMeal(mealId);
              // If viewing a past date, reload that date's meals
              if (selectedDate) {
                handleDateChange(selectedDate);
              }
            }}
            onEditPortion={openEditPortion}
            onLogActivity={() => setShowExerciseLogger(true)}
            onLogSteps={logSteps}
            onDeleteActivity={deleteExercise}
            onUpgradeMicros={() => showUpgrade('micronutrients')}
            onMicronutrients={() => {
              // Scroll to micronutrients section or expand it
              const microsPreview = document.querySelector('[data-testid="micros-preview"]');
              if (microsPreview) {
                microsPreview.scrollIntoView({ behavior: 'smooth', block: 'center' });
                // Also click to expand it
                const expandBtn = microsPreview.querySelector('button');
                if (expandBtn) expandBtn.click();
              }
            }}
            onSmartGoals={() => openSmartGoalsGated('button')}
            onDailyCheckin={() => setShowDailyCheckin(true)}
            onBarcode={() => setShowBarcodeScanner(true)}
            onSmartMeals={() => setShowMealPlanner(true)}
            onSmartSwaps={openSmartSwaps}
            onFitMyMacros={openSmartMealsFitMode}
            onSmartHub={openCookbook}
            userTimezone={userTimezone}
            onOpenSettings={() => setCurrentView('settings')}
            // Upgrade handlers for direct Stripe checkout
            onUpgradeSmart={() => handleHomeUpgrade('smart_plaite_monthly')}
            onUpgradeGenius={() => handleHomeUpgrade('genius_plaite_monthly')}
            isCheckingOut={homeCheckingOut}
            // Health Intelligence
            onHealthIntelFixThis={handleHealthIntelFixThis}
            onHealthIntelAskWhy={handleHealthIntelAskWhy}
            askWhyUsedToday={askWhyUsedToday}
            // Trial & Promo
            userId={userId}
            subscriptionDetails={subscriptionDetails}
            onTrialActivated={async () => {
              try {
                const r = await fetch(`${BACKEND_URL}/api/billing/subscription-status/${userId}`);
                if (r.ok) {
                  const d = await r.json();
                  setIsPremium(d.is_premium);
                  setSubscriptionDetails(d);
                }
              } catch (e) { console.error('onTrialActivated reload failed:', e); }
            }}
            // MIQ Calibration Banner props
            miqCalibrationDay={aiMiq?.calibration?.current_day || 1}
            miqIsCalibrating={aiMiq?.calibration?.is_calibrating !== false && !aiMiq?.calibration?.calibration_complete}
            miqCalibrationComplete={aiMiq?.calibration?.calibration_complete === true && getMiqStatusText().factors >= 9}
            miqBaselineViewedAt={aiMiq?.baseline_viewed_at || null}
            isGeniusTier={userTier === 'miq' || userTier === 'genius'}
            isSignupTrial={subscriptionDetails.is_trial || subscriptionDetails.is_trialing}
            onViewBaseline={() => {
              setCurrentView('miq');
              // Trigger baseline report view in MIQView
              setTimeout(() => {
                const event = new CustomEvent('openBaselineReport');
                window.dispatchEvent(event);
              }, 500);
            }}
            onUpgradeFromMIQ={() => showUpgrade('miq_baseline')}
            // Smart Timer props
            ifFastingActive={ifFastingActive}
            ifTimeRemaining={ifTimeRemaining}
            ifWindowType={null}
            onIFTimer={() => setCurrentView('if')}
            // v1.3 - Hydration tracking props
            hydrationMl={hydrationStatus.hydration_ml}
            hydrationGoalMl={hydrationStatus.hydration_goal_ml}
            onHydrationTap={() => setShowHydrationModal(true)}
            // Trial countdown
            miqScore={aiMiq?.dynamic_score}
            totalMealsLogged={totalMealsLogged}
            // Date navigation for past-day editing
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            // Navigation Wheel (section controller)
            navigationWheel={
              <NavigationWheel
                sections={HOME_SECTIONS}
                openSections={homeOpenSections}
                onToggleSection={toggleHomeSection}
                userTier={userTier}
                onUpgrade={handleWheelUpgrade}
                onCenterClick={() => openSmartGoalsGated('compass')}
              />
            }
            openSections={homeOpenSections}
            // Extra components
            eatingOutBanner={eatingOutContext && (
              <div 
                data-testid="eating-out-context-banner"
                style={{
                  padding: '10px 12px',
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                  borderRadius: '8px',
                  marginBottom: '8px',
                  border: '1px solid rgba(102, 126, 234, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '2px' }}>
                    🍴 Eating out context active
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                    Log your meal below — MIQ will factor in restaurant conditions.
                  </div>
                </div>
                <button
                  onClick={() => setEatingOutContext(null)}
                  style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '16px', cursor: 'pointer', padding: '4px' }}
                >
                  ×
                </button>
              </div>
            )}
            featureDiscoveryCard={
              <FeatureDiscoveryCard
                userId={userId}
                smartGoalsComplete={aiNutritionGoals?.intake_complete === true}
                totalMealsLogged={totalMealsLogged}
                isTrialUser={subscriptionDetails?.is_trial || subscriptionDetails?.is_trialing}
                onSmartGoals={() => openSmartGoalsGated('feature_discovery')}
                onMealPlan={() => { setShowMealPlanner(true); setMealPlannerFitMode(false); }}
                onSmartCoach={() => openSmartCoach('feature_discovery')}
                onWorkout={() => { setCurrentView('miq'); }}
                onLogMeal={() => setCurrentView('camera')}
              />
            }
            coachStrip={!showOnboarding && coachPrompt && (
              <CoachStrip 
                prompt={coachPrompt}
                onPrimaryAction={() => handleCoachAction(coachPrompt.primaryAction)}
                onSecondaryAction={(action) => handleCoachAction(action)}
                onDismiss={dismissCoachPrompt}
                variant="default"
              />
            )}
            smartHubComponent={(
              <SmartHub
                userId={userId}
                onMealLogged={async (logged, name) => {
                  // Prepare current meal data
                  const currentMeal = {
                    food_items: name,
                    calories: logged.calories || 0,
                    protein: logged.protein || 0,
                    carbs: logged.carbs || 0,
                    fat: logged.fat || 0,
                    fiber: logged.fiber || 0,
                    sugar: logged.sugar || 0,
                    sodium: logged.sodium || 0,
                    vitamin_c: logged.vitamin_c || 0,
                    iron: logged.iron || 0,
                    calcium: logged.calcium || 0
                  };
                  
                  // Fetch fresh totals from API for accurate display
                  try {
                    const totalsResponse = await fetch(`${BACKEND_URL}/api/meals/${userId}/today`);
                    if (totalsResponse.ok) {
                      const totalsData = await totalsResponse.json();
                      const freshTotals = totalsData.totals || {};
                      
                      // Update state with fresh totals
                      setTodayTotals(freshTotals);
                      setTodayMeals(totalsData.meals || []);
                      
                      // Trigger post-meal sheet with fresh totals
                      triggerPostMealComplete(freshTotals, currentMeal, true);
                    } else {
                      // Fallback
                      loadTodayMeals(userId);
                      triggerPostMealComplete(null, currentMeal, true);
                    }
                  } catch (fetchError) {
                    console.error('[SmartHub onMealLogged] Error:', fetchError);
                    loadTodayMeals(userId);
                    triggerPostMealComplete(null, currentMeal, true);
                  }
                }}
              />
            )}
          />
        )}

        {/* Profile Setup - Extracted to ProfileView component */}
        {(currentView === 'profile' || (!userId && !isGuestMode && currentView !== 'camera')) && (
          <Suspense fallback={<ViewLoadingFallback />}>
            <ProfileView
              userId={userId}
              currentView={currentView}
              isGuestMode={isGuestMode}
              userProfile={userProfile}
              useImperial={useImperial}
              setUseImperial={setUseImperial}
              selectedFoodAllergies={selectedFoodAllergies}
              setSelectedFoodAllergies={setSelectedFoodAllergies}
              handleProfileSubmit={handleProfileSubmit}
              openSmartGoalsGated={openSmartGoalsGated}
              setCurrentView={setCurrentView}
              aiMiq={aiMiq}
              metabolicProfile={metabolicProfile}
              cmToFeetInches={cmToFeetInches}
              kgToLbs={kgToLbs}
            />
          </Suspense>
        )}

        {/* Personal MetaBolicIQ Intelligence Profile Tab - Dedicated Intelligence View */}
        {currentView === 'miq' && userId && (
          <>
            {/* COACH STRIP for MIQ Tab - Uses prompt variant for intelligence-style SmartGoals */}
            {!showOnboarding && coachPrompt && (
              <div style={{ padding: '0 16px', marginTop: '12px' }}>
                <CoachStrip 
                  prompt={coachPrompt}
                  onPrimaryAction={() => handleCoachAction(coachPrompt.primaryAction)}
                  onSecondaryAction={(action) => handleCoachAction(action)}
                  onDismiss={dismissCoachPrompt}
                  variant={coachPrompt.variant || 'miq'}
                />
              </div>
            )}
            {/* Navigation Wheel on MIQ screen */}
            <div style={{ padding: '0 16px', marginTop: '8px' }}>
              <NavigationWheel
                sections={MIQ_SECTIONS}
                openSections={miqOpenSections}
                onToggleSection={toggleMiqSection}
                userTier={userTier}
                onUpgrade={handleWheelUpgrade}
                onCenterClick={() => openSmartGoalsGated('miq_compass')}
                centerLabel="SmartGoals"
                centerSub="SET YOUR PLAN"
              />
            </div>
            <MIQView
            userId={userId}
            aiMiq={aiMiq}
            aiNutritionGoals={aiNutritionGoals}
            metabolicProfile={metabolicProfile}
            userProfile={userProfile}
            dailyCheckinContribution={dailyCheckinContribution}
            dailyCheckinData={dailyCheckinData}
            checkedInToday={checkedInToday}
            todayMeals={todayMeals}
            totalMealsLogged={aiMiq?.confidence?.meals_logged || totalMealsLogged}
            isPremium={isPremium}
            userTier={userTier}
            miqIntakeOptions={miqIntakeOptions}
            expandWhatIsMiq={expandWhatIsMiq}
            BACKEND_URL={BACKEND_URL}
            setShowDailyCheckin={setShowDailyCheckin}
            setShowMealPlanner={setShowMealPlanner}
            onOpenSmartGoals={() => openSmartGoalsGated('miq_view')}
            setExpandWhatIsMiq={setExpandWhatIsMiq}
            loadMiqIntakeOptions={loadMiqIntakeOptions}
            showUpgrade={showUpgrade}
            setCurrentView={setCurrentView}
            setShowOnboarding={setShowOnboarding}
            openCameraMode={openCameraMode}
            loadAiNutritionGoals={loadAiNutritionGoals}
            onSmartCoachOpen={openSmartCoach}
            onAskCoachWithPrefill={handleHealthIntelAskWhy}
            onWorkoutLogged={() => loadTodayExercises(userId, userTimezone)}
            openSections={miqOpenSections}
            onShowToast={(message, type) => {
              // Use existing toast mechanism or create simple one
              if (typeof window !== 'undefined') {
                // Simple toast notification
                const toast = document.createElement('div');
                toast.style.cssText = `
                  position: fixed;
                  bottom: 100px;
                  left: 50%;
                  transform: translateX(-50%);
                  background: ${type === 'success' ? 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)' : '#333'};
                  color: white;
                  padding: 12px 24px;
                  border-radius: 8px;
                  font-size: 14px;
                  font-weight: 500;
                  z-index: 10000;
                  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
                  animation: slideUp 0.3s ease;
                `;
                toast.textContent = message;
                document.body.appendChild(toast);
                setTimeout(() => {
                  toast.style.opacity = '0';
                  toast.style.transition = 'opacity 0.3s';
                  setTimeout(() => toast.remove(), 300);
                }, 3000);
              }
            }}
          />
          </>
        )}

        {/* Smart Timer Tab - Smart Plaite+ Feature */}
        {currentView === 'if' && userId && userTier !== 'free' && (
          <IFTimerTab
            userId={userId}
            aiNutritionGoals={aiNutritionGoals}
            BACKEND_URL={BACKEND_URL}
            workScheduleType={aiNutritionGoals?.intake_data?.work_schedule_type}
            onSettingsSaved={() => loadAiNutritionGoals(userId, true)}
            mealHistory={mealHistory}
            mealHistoryLoading={mealHistoryLoading}
            onLoadMealHistory={(days) => loadMealHistory(userId, days)}
            dailySummaries={dailySummaries}
            initialTab={ifDeepLinkTab}
            onInitialTabConsumed={() => setIfDeepLinkTab(null)}
          />
        )}

        {/* Settings View */}
        {/* Admin Dashboard */}
        {currentView === 'admin' && isAdmin && userId && (
          <AdminDashboard
            userId={userId}
            colors={brand.colors}
            spacing={brand.spacing}
            radius={brand.radius}
            typography={brand.typography}
          />
        )}

        {currentView === 'settings' && userId && (
          <SettingsView
            userId={userId}
            userTimezone={userTimezone}
            setUserTimezone={setUserTimezone}
            setCurrentView={setCurrentView}
            isPwaInstalled={isPwaInstalled}
            handlePwaInstallClick={handlePwaInstallClick}
            setShowCuisinePreferencesModal={setShowCuisinePreferencesModal}
            cuisinePreferencesSet={cuisinePreferencesSet}
            cuisinePreferences={cuisinePreferences}
            userTier={userTier}
            showUpgrade={showUpgrade}
            aiNutritionGoals={aiNutritionGoals}
            isTester={isTester}
            testerOverrideActive={testerOverrideActive}
            authUser={authUser}
            isPremium={isPremium}
            setShowDiagnosticsPanel={setShowDiagnosticsPanel}
            handleLogout={handleLogout}
            loadMeals={loadTodayMeals}
            loadTodayExercises={loadTodayExercises}
            loadHydrationStatus={loadHydrationStatus}
            loadMealHistory={loadMealHistory}
          />
        )}

        {/* Tester Dashboard View - TESTER ONLY */}
        {currentView === 'tester-dashboard' && isTester && (
          <div className="tester-dashboard-view" style={{ paddingBottom: '80px' }}>
            <div className="card" style={{ background: 'var(--card-bg)', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
              <button 
                className="btn-secondary"
                onClick={() => setCurrentView('settings')}
                style={{ marginBottom: '16px' }}
              >
                ← Back to Settings
              </button>
              
              <Suspense fallback={<ViewLoadingFallback />}>
                <TesterDashboard 
                  userId={userId}
                  onTierChange={() => {
                    // Clear pending call to ensure fresh fetch
                    clearEntitlementsPendingCall();
                    // Refresh entitlements after tier change
                    loadEntitlements(userId);
                  }}
                />
              </Suspense>
            </div>
          </div>
        )}

        {/* Subscription & Pricing View */}
        {currentView === 'subscription' && (
          <SubscriptionView
            userId={userId}
            userTier={userTier}
            isPremium={isPremium}
            subscriptionDetails={subscriptionDetails}
            setCurrentView={setCurrentView}
          />
        )}

        {/* Privacy & Data Use View - Extracted to PrivacyView component */}
        {currentView === 'privacy' && (
          <Suspense fallback={<ViewLoadingFallback />}>
            <PrivacyView setCurrentView={setCurrentView} />
          </Suspense>
        )}

        {/* Legal & Medical Disclaimer View - Extracted to DisclaimerView component */}
        {currentView === 'disclaimer' && (
          <Suspense fallback={<ViewLoadingFallback />}>
            <DisclaimerView setCurrentView={setCurrentView} />
          </Suspense>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
            GUEST DASHBOARD - Shown when guest has logged meals but not signed up
        ═══════════════════════════════════════════════════════════════════════ */}
        {isGuestMode && !isAuthenticated && guestMealCount > 0 && currentView === 'home' && (
          <div style={{
            padding: '20px',
            paddingTop: authUser ? '16px' : '20px'
          }}>
            {/* Locked Features Banner */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.15) 0%, rgba(255, 87, 34, 0.1) 100%)',
              borderRadius: '12px',
              padding: '14px 16px',
              marginBottom: '16px',
              border: '1px solid rgba(255, 152, 0, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '20px' }}>🔒</span>
                <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.9)' }}>
                  Some features are locked
                </span>
              </div>
              <button
                onClick={() => {
                  setShowAuthFromGuest(true);
                  setAuthView('signup');
                }}
                style={{
                  padding: '8px 14px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                Sign Up Free
              </button>
            </div>
            
            {/* Guest Welcome */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '24px',
              border: '1px solid rgba(102, 126, 234, 0.2)'
            }}>
              <h2 style={{ 
                fontSize: '20px', 
                fontWeight: '700', 
                color: 'white',
                margin: '0 0 8px 0'
              }}>
                You&apos;re building momentum
              </h2>
              <p style={{ 
                fontSize: '14px', 
                color: 'rgba(255,255,255,0.7)',
                margin: '0 0 4px 0'
              }}>
                {guestMealCount} meal{guestMealCount !== 1 ? 's' : ''} logged. MIQ 🧠 is learning from your choices.
              </p>
              <p style={{ 
                fontSize: '12px', 
                color: 'rgba(255,255,255,0.5)',
                margin: '0 0 16px 0',
                fontStyle: 'italic'
              }}>
                MetaBolicIQ (MIQ) improves as patterns emerge.
              </p>
              
              {/* Preview Mode Disclaimer Banner */}
              <div style={{
                background: 'rgba(255, 193, 7, 0.15)',
                border: '1px solid rgba(255, 193, 7, 0.3)',
                borderRadius: '10px',
                padding: '12px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px'
              }}>
                <span style={{ fontSize: '16px' }}>👋</span>
                <div>
                  <p style={{ 
                    margin: '0 0 4px 0', 
                    fontSize: '13px', 
                    fontWeight: '600',
                    color: '#FFD54F'
                  }}>
                    This is a preview
                  </p>
                  <p style={{ 
                    margin: 0, 
                    fontSize: '11px', 
                    color: 'rgba(255,255,255,0.6)',
                    lineHeight: '1.4'
                  }}>
                    Create an account to save meals to your daily goals. Just re-snap your meal after signing up!
                  </p>
                </div>
              </div>
              
              {/* Quick Stats */}
              {guestMeals.length > 0 && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '8px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    background: 'rgba(0,0,0,0.2)',
                    padding: '10px',
                    borderRadius: '10px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: '#667eea' }}>
                      {guestMeals.reduce((sum, m) => sum + (m.calories || 0), 0)}
                    </div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>kcal</div>
                  </div>
                  <div style={{
                    background: 'rgba(0,0,0,0.2)',
                    padding: '10px',
                    borderRadius: '10px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: '#81C784' }}>
                      {guestMeals.reduce((sum, m) => sum + (m.protein_g || 0), 0)}g
                    </div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>protein</div>
                  </div>
                  <div style={{
                    background: 'rgba(0,0,0,0.2)',
                    padding: '10px',
                    borderRadius: '10px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: '#FFD54F' }}>
                      {guestMeals.reduce((sum, m) => sum + (m.carbs_g || 0), 0)}g
                    </div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>carbs</div>
                  </div>
                  <div style={{
                    background: 'rgba(0,0,0,0.2)',
                    padding: '10px',
                    borderRadius: '10px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: '#FF8A65' }}>
                      {guestMeals.reduce((sum, m) => sum + (m.fat_g || 0), 0)}g
                    </div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>fat</div>
                  </div>
                </div>
              )}
              
              {/* Small note below macros */}
              {guestMeals.length > 0 && (
                <p style={{
                  fontSize: '10px',
                  color: 'rgba(255,255,255,0.4)',
                  textAlign: 'center',
                  margin: '0 0 16px 0',
                  fontStyle: 'italic'
                }}>
                  ⚡ Preview only — sign up to track daily goals
                </p>
              )}
              
              {/* Create Account CTA */}
              <button
                onClick={() => {
                  setShowAuthFromGuest(true);
                  setAuthView('signup');
                }}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: '10px',
                  color: 'white',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginBottom: '10px'
                }}
              >
                Create Free Account
              </button>
              <button
                onClick={() => {
                  setShowAuthFromGuest(true);
                  setAuthView('login');
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Sign in with existing account
              </button>
            </div>
            
            {/* Quick Log Options */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ 
                fontSize: '16px', 
                fontWeight: '600', 
                color: 'white',
                margin: '0 0 12px 0'
              }}>
                Quick log
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button
                  onClick={() => {
                    setCurrentView('camera');
                    openCameraMode('camera');
                  }}
                  style={{
                    padding: '16px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer'
                  }}
                >
                  <span style={{ fontSize: '24px' }}>📸</span>
                  <span style={{ fontWeight: '500' }}>Take Photo</span>
                </button>
                <button
                  onClick={() => {
                    setCurrentView('camera');
                    openCameraMode('upload');
                  }}
                  style={{
                    padding: '16px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer'
                  }}
                >
                  <span style={{ fontSize: '24px' }}>📁</span>
                  <span style={{ fontWeight: '500' }}>Upload Photo</span>
                </button>
                <button
                  onClick={() => setShowManualEntry(true)}
                  style={{
                    padding: '16px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer'
                  }}
                >
                  <span style={{ fontSize: '24px' }}>✍️</span>
                  <span style={{ fontWeight: '500' }}>Manual Entry</span>
                </button>
              </div>
            </div>
            
            {/* Today's Logged Meals */}
            {guestMeals.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ 
                  fontSize: '16px', 
                  fontWeight: '600', 
                  color: 'white',
                  margin: '0 0 12px 0'
                }}>
                  Today's meals ({guestMeals.length})
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {guestMeals.slice().reverse().map((meal, index) => (
                    <div 
                      key={meal.id || index}
                      style={{
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '12px',
                        padding: '14px 16px'
                      }}
                    >
                      <div style={{ 
                        fontSize: '14px', 
                        fontWeight: '500', 
                        color: 'var(--text-primary)',
                        marginBottom: '6px'
                      }}>
                        {meal.food_items || meal.name || 'Meal'}
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: '12px',
                        fontSize: '13px',
                        color: 'var(--text-secondary)'
                      }}>
                        <span><strong style={{ color: '#667eea' }}>{meal.calories || 0}</strong> kcal</span>
                        <span>P: <strong style={{ color: '#81C784' }}>{meal.protein_g || 0}g</strong></span>
                        <span>C: <strong style={{ color: '#FFD54F' }}>{meal.carbs_g || 0}g</strong></span>
                        <span>F: <strong style={{ color: '#FF8A65' }}>{meal.fat_g || 0}g</strong></span>
                      </div>
                      {meal.logged_at && (
                        <div style={{ 
                          fontSize: '11px', 
                          color: 'rgba(255,255,255,0.4)',
                          marginTop: '6px'
                        }}>
                          {new Date(meal.logged_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Personalize Targets (if not done) */}
            {!guestProfile && (
              <button
                onClick={() => setShowPersonalizeTargets(true)}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: 'transparent',
                  border: '1px solid rgba(102, 126, 234, 0.4)',
                  borderRadius: '10px',
                  color: '#667eea',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Personalize my targets
              </button>
            )}
            
            {/* Personalized Targets Display */}
            {guestProfile && calculatedGuestTargets && (
              <div style={{
                background: 'rgba(102, 126, 234, 0.1)',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid rgba(102, 126, 234, 0.2)'
              }}>
                <div style={{ 
                  fontSize: '12px', 
                  color: 'rgba(255,255,255,0.5)',
                  marginBottom: '8px'
                }}>
                  Your personalized targets
                </div>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: '700', 
                  color: '#667eea',
                  marginBottom: '8px'
                }}>
                  {calculatedGuestTargets.calories} kcal/day
                </div>
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  fontSize: '13px',
                  color: 'rgba(255,255,255,0.6)'
                }}>
                  <span>P: {calculatedGuestTargets.protein}g</span>
                  <span>C: {calculatedGuestTargets.carbs}g</span>
                  <span>F: {calculatedGuestTargets.fat}g</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Camera View — extracted to /components/camera/CameraView.js */}
        {currentView === 'camera' && (
          <CameraView
            cameraState={{
              cameraMode,
              analyzing,
            }}
            cameraActions={{
              setAnalyzing,
              onClose: () => { setCurrentView('home'); },
              onShowManualEntry: () => setShowManualEntry(true),
              onShowFoodContribution: () => setShowFoodContribution(true),
              onGuestMealSuccess: handleGuestMealSuccess,
              loadTodayMeals,
              setEatingOutContext,
              trackMealLogged,
              showMiqNudge,
              setFirstMealData,
              setShowFirstMealInsight,
              triggerPostMealComplete,
              fetchPostLogInsight,
            }}
            cameraConfig={{
              BACKEND_URL,
              userId,
              isGuestMode,
              eatingOutContext,
              totalMealsLogged,
              pendingImage: pendingCameraImage,
              clearPendingImage: () => setPendingCameraImage(null),
            }}
          />
        )}

        {/* Smart Hub View - Extracted to HistoryView component */}
        {currentView === 'history' && userId && (
          <Suspense fallback={<ViewLoadingFallback />}>
            {/* Navigation Wheel on Smart Hub */}
            <div style={{ padding: '0 16px', marginTop: '8px' }}>
              <NavigationWheel
                sections={SMARTHUB_SECTIONS}
                openSections={hubOpenSections}
                onToggleSection={toggleHubSection}
                userTier={userTier}
                onUpgrade={handleWheelUpgrade}
                onCenterClick={() => openSmartGoalsGated('hub_compass')}
                centerLabel="SmartGoals"
                centerSub="SET YOUR PLAN"
              />
            </div>
            <HistoryView
              userId={userId}
              todayMeals={todayMeals}
              todayTotals={todayTotals}
              totalCaloriesBurned={totalCaloriesBurned}
              dailyGoal={dailyGoal}
              targetProtein={targetProtein}
              targetCarbs={targetCarbs}
              targetFat={targetFat}
              dailySummaries={dailySummaries}
              savedRecipes={savedRecipes}
              savedRecipesExpanded={savedRecipesExpanded}
              savedRecipesLoading={savedRecipesLoading}
              setSavedRecipesExpanded={setSavedRecipesExpanded}
              favorites={favorites}
              logFavorite={logFavorite}
              removeFavorite={removeFavorite}
              setShowFavorites={setShowFavorites}
              setCurrentView={setCurrentView}
              openSmartSwaps={openSmartSwaps}
              setShowCookbook={setShowCookbook}
              setShowDailyCheckin={setShowDailyCheckin}
              smartHubFavoritesExpanded={smartHubFavoritesExpanded}
              setSmartHubFavoritesExpanded={setSmartHubFavoritesExpanded}
              smartHubTodayExpanded={smartHubTodayExpanded}
              setSmartHubTodayExpanded={setSmartHubTodayExpanded}
              hydrationStatus={hydrationStatus}
              ifFastingActive={ifFastingActive}
              ifTimeRemaining={ifTimeRemaining}
              isPremium={isPremium}
              aiNutritionGoals={aiNutritionGoals}
              openSections={hubOpenSections}
              onViewPast7Days={() => {
                setIfDeepLinkTab('history');
                setCurrentView('if');
              }}
              onUpgradeHealthSync={() => showUpgrade('health_sync')}
            />
          </Suspense>
        )}

        {/* Manual Entry Modal */}
        {showManualEntry && (
          <ManualEntryModal
            manualEntryText={manualEntryText}
            manualEntryResult={manualEntryResult}
            analyzing={analyzing}
            setManualEntryText={setManualEntryText}
            setManualEntryResult={setManualEntryResult}
            setShowManualEntry={setShowManualEntry}
            analyzeManualEntry={analyzeManualEntry}
            addManualEntryToMeals={addManualEntryToMeals}
          />
        )}
        
        {/* Standalone Hydration Quick Log Modal */}
        <HydrationQuickLogModal
          isOpen={showHydrationModal}
          onClose={() => setShowHydrationModal(false)}
          userId={userId}
          currentMl={hydrationStatus?.hydration_ml || 0}
          goalMl={hydrationStatus?.hydration_goal_ml || 2500}
          BACKEND_URL={BACKEND_URL}
          onSuccess={(data) => {
            // Update local hydration state
            setHydrationStatus(prev => ({
              ...prev,
              hydration_ml: data.total_ml,
              hydration_goal_ml: data.goal_ml
            }));
          }}
        />

        {/* Intermittent Fasting Consent Modal */}
        {/* Fasting Consent Modal */}
        <FastingConsentModal
          isOpen={showFastingConsent}
          onClose={() => setShowFastingConsent(false)}
          mealPlanPreferences={mealPlanPreferences}
          setMealPlanPreferences={setMealPlanPreferences}
          setFastingConsentGiven={setFastingConsentGiven}
        />

        {/* Advanced Goals Modal */}        {showAdvancedGoals && (
          <AdvancedGoalsModal
            goalFormMode={goalFormMode}
            goalFormData={goalFormData}
            useImperial={useImperial}
            userProfile={userProfile}
            analyzing={analyzing}
            setGoalFormMode={setGoalFormMode}
            setGoalFormData={setGoalFormData}
            setShowAdvancedGoals={setShowAdvancedGoals}
            saveAdvancedGoals={saveAdvancedGoals}
            setUseImperial={setUseImperial}
            kgToLbs={kgToLbs}
          />
        )}

        {/* Post-Log Metabolic Insight Panel */}
        {showPostLogInsight && postLogInsight && (
          <div className="modal-overlay" onClick={dismissPostLogInsight}>
            <div 
              className="modal-content" 
              onClick={(e) => e.stopPropagation()} 
              style={{ 
                maxWidth: '440px', 
                borderRadius: '20px',
                overflow: 'hidden',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* Close Button - Fixed at top right */}
              <button
                onClick={dismissPostLogInsight}
                style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  border: 'none',
                  background: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontSize: '18px',
                  cursor: 'pointer',
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
              
              {/* Header */}
              <div style={{
                background: postLogInsight.alignmentScore >= 85 
                  ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                  : postLogInsight.alignmentScore >= 60 
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                padding: '24px',
                color: 'white',
                textAlign: 'center'
              }}>
                <p style={{ fontSize: '12px', opacity: 0.9, margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Meal Logged Successfully
                </p>
                <h2 style={{ fontSize: '20px', fontWeight: '700', margin: '0 0 16px 0' }}>
                  {lastLoggedMeal?.food_items?.slice(0, 50)}{lastLoggedMeal?.food_items?.length > 50 ? '...' : ''}
                </h2>
                
                {/* Alignment Score/Label */}
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '16px',
                  padding: '16px',
                  display: 'inline-block'
                }}>
                  <p style={{ fontSize: '12px', opacity: 0.9, margin: '0 0 4px 0' }}>
                    Alignment with Metabolic Profile
                  </p>
                  {postLogInsight.isPremium && postLogInsight.alignmentScore !== null ? (
                    <p style={{ fontSize: '36px', fontWeight: '700', margin: 0 }}>
                      {postLogInsight.alignmentScore}%
                    </p>
                  ) : (
                    <p style={{ fontSize: '24px', fontWeight: '600', margin: 0 }}>
                      {postLogInsight.alignmentEmoji} {postLogInsight.alignmentLabel}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Content */}
              <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
                {/* Explanation */}
                <p style={{ 
                  fontSize: '14px', 
                  color: 'var(--text-secondary)', 
                  marginBottom: '20px',
                  lineHeight: '1.6'
                }}>
                  {postLogInsight.explanation}
                </p>
                
                {/* Swap Suggestion */}
                {postLogInsight.swapSuggestion && (
                  <div style={{
                    background: postLogInsight.swapSuggestion.type === 'locked' 
                      ? 'rgba(0,0,0,0.03)' 
                      : 'rgba(102, 126, 234, 0.08)',
                    border: postLogInsight.swapSuggestion.type === 'locked'
                      ? '1px dashed rgba(0,0,0,0.15)'
                      : '1px solid rgba(102, 126, 234, 0.2)',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '16px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <span style={{ fontSize: '24px' }}>{postLogInsight.swapSuggestion.icon}</span>
                      <div>
                        <p style={{ 
                          margin: 0, 
                          fontWeight: '600', 
                          fontSize: '14px',
                          color: postLogInsight.swapSuggestion.type === 'locked' ? 'var(--text-secondary)' : 'var(--text-primary)'
                        }}>
                          {postLogInsight.swapSuggestion.type === 'locked' ? 'Swap Suggestion' : 'Quick Swap'}
                        </p>
                        <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
                          {postLogInsight.swapSuggestion.reason}
                        </p>
                      </div>
                    </div>
                    
                    {postLogInsight.swapSuggestion.type !== 'locked' ? (
                      <button
                        style={{
                          width: '100%',
                          padding: '12px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '10px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          marginTop: '8px'
                        }}
                        onClick={() => {
                          alert(`💡 Suggestion: ${postLogInsight.swapSuggestion.suggestion}`);
                        }}
                      >
                        View Swap Suggestion
                      </button>
                    ) : (
                      <div style={{ marginTop: '8px' }}>
                        <p style={{ 
                          fontSize: '13px', 
                          fontWeight: '600', 
                          margin: '0 0 4px 0',
                          color: 'var(--text-primary)'
                        }}>
                          🔒 Unlock this insight
                        </p>
                        <p style={{ 
                          fontSize: '11px', 
                          color: 'var(--text-secondary)',
                          margin: '0 0 10px 0'
                        }}>
                          See why this matters for your body and how to fix it.
                        </p>
                        <button
                          style={{
                            width: '100%',
                            padding: '12px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '10px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                          onClick={() => {
                            dismissPostLogInsight();
                            setCurrentView('settings');
                          }}
                        >
                          Upgrade to Premium
                        </button>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Profile Summary for Premium */}
                {postLogInsight.isPremium && postLogInsight.profile && (
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: '16px',
                    flexWrap: 'wrap'
                  }}>
                    <span style={{
                      fontSize: '11px',
                      padding: '4px 10px',
                      background: postLogInsight.profile.carbSensitivity === 'High' ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                      color: postLogInsight.profile.carbSensitivity === 'High' ? '#c62828' : '#2e7d32',
                      borderRadius: '20px'
                    }}>
                      🍞 Carbs: {postLogInsight.profile.carbSensitivity}
                    </span>
                    <span style={{
                      fontSize: '11px',
                      padding: '4px 10px',
                      background: postLogInsight.profile.proteinRequirement === 'High' ? 'rgba(33, 150, 243, 0.1)' : 'rgba(255, 193, 7, 0.1)',
                      color: postLogInsight.profile.proteinRequirement === 'High' ? '#1565c0' : '#f57f17',
                      borderRadius: '20px'
                    }}>
                      🥩 Protein: {postLogInsight.profile.proteinRequirement}
                    </span>
                    <span style={{
                      fontSize: '11px',
                      padding: '4px 10px',
                      background: postLogInsight.profile.sodiumSensitivity === 'High' ? 'rgba(244, 67, 54, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                      color: postLogInsight.profile.sodiumSensitivity === 'High' ? '#c62828' : '#2e7d32',
                      borderRadius: '20px'
                    }}>
                      🧂 Sodium: {postLogInsight.profile.sodiumSensitivity}
                    </span>
                  </div>
                )}
                
                {/* Done Button */}
                <button
                  onClick={dismissPostLogInsight}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)',
                    border: 'none',
                    borderRadius: '12px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Trend Charts Modal removed Feb 23 2026 — replaced by Smart Hub sparklines + MIQ gauge */}

        {/* Body Type Nutrition Guidance Modal */}
        <BodyTypeGuidanceModal
          isOpen={showBodyTypeGuidance}
          onClose={() => setShowBodyTypeGuidance(false)}
          userProfile={userProfile}
          bodyTypeGuidance={bodyTypeGuidance}
          allBodyTypeGuidance={allBodyTypeGuidance}
        />
{/* Food Knowledge Contribution Modal */}
        {/* Food Contribution Modal */}
        <FoodContributionModal
          isOpen={showFoodContribution}
          onClose={() => setShowFoodContribution(false)}
          userId={userId}
          foodContribution={foodContribution}
          setFoodContribution={setFoodContribution}
        />

        {/* SmartMeals Modal */}
        <SmartMealsModal
          isOpen={showMealPlanner}
          onClose={() => {
            if (!mealPlanResult) {
              markMealPlannerExitWithoutGenerate();
              setMealPlannerJustExited(true);
            }
            setShowMealPlanner(false);
            setMealPlannerFitMode(false);
            setFitMacroResults(null);
            setMealPlanResult(null);
          }}
          userId={userId}
          userProfile={userProfile}
          aiNutritionGoals={aiNutritionGoals}
          mealPlanPreferences={mealPlanPreferences}
          setMealPlanPreferences={setMealPlanPreferences}
          mealPlanResult={mealPlanResult}
          setMealPlanResult={setMealPlanResult}
          mealPlannerFitMode={mealPlannerFitMode}
          setMealPlannerFitMode={setMealPlannerFitMode}
          analyzing={analyzing}
          setAnalyzing={setAnalyzing}
          fitMacroResults={fitMacroResults}
          setFitMacroResults={setFitMacroResults}
          fitMacroLoading={fitMacroLoading}
          setFitMacroLoading={setFitMacroLoading}
          loggingFitMealIdx={loggingFitMealIdx}
          setLoggingFitMealIdx={setLoggingFitMealIdx}
          dailyGoal={dailyGoal}
          targetProtein={targetProtein}
          targetFat={targetFat}
          targetCarbs={targetCarbs}
          todayTotals={todayTotals}
          ifFastingActive={ifFastingActive}
          loadTodayMeals={loadTodayMeals}
          addMealPlanToToday={addMealPlanToToday}
          markMealPlannerExitWithoutGenerate={markMealPlannerExitWithoutGenerate}
          setMealPlannerJustExited={setMealPlannerJustExited}
          openSmartGoalsGated={openSmartGoalsGated}
          isPremium={isPremium}
          formatMealInstructions={formatMealInstructions}
          savingSmartMealId={savingSmartMealId}
          handleSaveSmartMeal={handleSaveSmartMeal}
        />

        {/* Daily Check-In Modal */}
        <DailyCheckinModal
          isOpen={showDailyCheckin}
          onClose={() => setShowDailyCheckin(false)}
          userId={userId}
          checkinFormData={checkinFormData}
          setCheckinFormData={setCheckinFormData}
          checkedInToday={checkedInToday}
          dailyCheckinContribution={dailyCheckinContribution}
          dailyCheckinLoading={dailyCheckinLoading}
          submitDailyCheckin={submitDailyCheckin}
          weightCheckStatus={weightCheckStatus}
        />


        {/* ═══════════════════════════════════════════════════════════════════════════════ */}
        {/* MIQ Day-30 Modal removed - no longer needed */}

        {/* Barcode Scanner Modal */}
        {/* Barcode Scanner Modal */}
        <BarcodeScannerModal
          isOpen={showBarcodeScanner}
          onClose={() => setShowBarcodeScanner(false)}
          analyzing={analyzing}
          scannerActive={scannerActive}
          startBarcodeScanner={startBarcodeScanner}
          stopBarcodeScanner={stopBarcodeScanner}
          barcodeInput={barcodeInput}
          setBarcodeInput={setBarcodeInput}
          scanBarcode={scanBarcode}
          barcodeResult={barcodeResult}
          setBarcodeResult={setBarcodeResult}
          barcodeServings={barcodeServings}
          setBarcodeServings={setBarcodeServings}
          addBarcodeProductToMeals={addBarcodeProductToMeals}
          scanNutritionLabel={scanNutritionLabel}
        />


        {/* Exercise Logger Modal */}
        {/* Exercise Logger Modal */}
        <ExerciseLoggerModal
          isOpen={showExerciseLogger}
          onClose={() => setShowExerciseLogger(false)}
          exerciseForm={exerciseForm}
          setExerciseForm={setExerciseForm}
          logExercise={logExercise}
          analyzing={analyzing}
        />


        {/* All Favorites Modal */}
        {/* Favorites Modal */}
        <FavoritesModal
          isOpen={showFavorites}
          onClose={() => setShowFavorites(false)}
          favorites={favorites}
          logFavorite={logFavorite}
          removeFavorite={removeFavorite}
        />


        {/* Edit Portion Modal */}
        {/* Edit Portion Modal */}
        <EditPortionModal
          isOpen={showEditPortion}
          onClose={() => { setShowEditPortion(false); setEditingMeal(null); setPortionPercent(100); }}
          editingMeal={editingMeal}
          portionPercent={portionPercent}
          setPortionPercent={setPortionPercent}
          portionLoading={portionLoading}
          savePortionEdit={savePortionEdit}
        />


        {/* Menu Search Modal */}
        {/* Restaurant Menu Search Modal */}
        <MenuSearchModal
          isOpen={showMenuSearch}
          onClose={closeMenuSearch}
          analyzing={menuSearchLoading}
          menuSearchQuery={menuSearchQuery}
          handleRestaurantQueryChange={handleRestaurantQueryChange}
          menuSearchCity={menuSearchCity}
          setMenuSearchCity={setMenuSearchCity}
          searchRestaurantMenu={() => searchRestaurantMenu(userId)}
          isKnownChain={isKnownChain}
          menuSearchResults={menuSearchResults}
          selectedMenuItem={selectedMenuItem}
          setSelectedMenuItem={setSelectedMenuItem}
          addMenuItemToMeals={addMenuItemToMeals}
          showSmartSuggestions={showSmartSuggestions}
          SMART_SUGGESTIONS={SMART_SUGGESTIONS}
          getCuisineType={getCuisineType}
        />

        {/* Premium Paywall Modal - Unlock SmartPlAIte Premium */}
        {/* Premium Paywall Modal */}
        <PaywallModal
          isOpen={showPaywall}
          onClose={() => setShowPaywall(false)}
          userId={userId}
          setCurrentView={setCurrentView}
        />
      </main>
      
      {/* Coach Toast - Post-action notifications */}
      {coachToast && (
        <CoachToast
          message={coachToast.message}
          secondaryMessage={coachToast.secondaryMessage}
          ctaLabel={coachToast.ctaLabel}
          onCtaClick={() => {
            if (coachToast.ctaAction) coachToast.ctaAction();
            setCoachToast(null);
          }}
          onClose={() => setCoachToast(null)}
          icon={coachToast.icon}
        />
      )}
      
      {/* Post-Meal Complete Sheet - P0: No more stranded users */}
      <PostMealCompleteSheet
        isOpen={showPostMealComplete}
        onClose={() => setShowPostMealComplete(false)}
        // Current meal data (what was just logged)
        currentMealName={postMealData?.currentMealName || null}
        currentMealCalories={postMealData?.currentMealCalories || 0}
        currentMealProtein={postMealData?.currentMealProtein || 0}
        currentMealCarbs={postMealData?.currentMealCarbs || 0}
        currentMealFat={postMealData?.currentMealFat || 0}
        // Cumulative totals
        todayCalories={postMealData?.todayCalories || todayTotals?.calories || 0}
        calorieTarget={postMealData?.calorieTarget || dailyGoal || 0}
        todayProtein={postMealData?.todayProtein || Math.round(todayTotals?.protein || 0)}
        todayCarbs={postMealData?.todayCarbs || Math.round(todayTotals?.carbs || 0)}
        todayFat={postMealData?.todayFat || Math.round(todayTotals?.fat || 0)}
        mealsLoggedToday={postMealData?.mealsLoggedToday || todayMeals?.length || 0}
        hasAIGoals={postMealData?.hasAIGoals || !!(aiNutritionGoals?.nutrition_goals?.daily_targets?.calories)}
        hasCalorieTarget={postMealData?.hasCalorieTarget || !!(dailyGoal && dailyGoal > 0)}
        micronutrients={(postMealData?.todayFiber || postMealData?.todaySugar || postMealData?.todaySodium || todayTotals?.fiber || todayTotals?.sugar || todayTotals?.sodium) ? {
          fiber: postMealData?.todayFiber ?? Math.round(todayTotals?.fiber || 0),
          sugar: postMealData?.todaySugar ?? Math.round(todayTotals?.sugar || 0),
          sodium: postMealData?.todaySodium ?? Math.round(todayTotals?.sodium || 0),
          cholesterol: postMealData?.todayCholesterol ?? Math.round(todayTotals?.cholesterol || 0)
        } : null}
        isPro={userTier === 'enhanced' || userTier === 'miq' || userTier === 'smart' || userTier === 'genius'}
        isTrial={subscriptionDetails.is_trial || subscriptionDetails.is_trialing}
        onUpgrade={() => handleHomeUpgrade('genius_plaite_monthly')}
        nextBestAction={postMealData?._testForceNoProfile ? { primaryAction: 'COMPLETE_PROFILE', primaryLabel: '🧍 Complete Profile' } : coachPrompt}
        onPrimaryAction={handlePostMealAction}
        onSmartSwaps={openSmartSwaps}
        onBarcodeScanner={() => setShowBarcodeScanner(true)}
        showSmartSwaps={postMealData?.showSmartSwaps || false}
        showBarcode={postMealData?.showBarcode || false}
        _testForceEvening={postMealData?._testForceEvening || false}
        _testForceNoProfile={postMealData?._testForceNoProfile || false}
      />
      
      {/* Smart Swaps View - Healthy Recipe Hub (Lazy Loaded) */}
        {/* Smart Swaps View */}
        {showSmartSwaps && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000, background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', overflowY: 'auto' }}>
            <Suspense fallback={<ViewLoadingFallback />}>
              <SmartSwapsView
                userId={userId}
                userTier={userTier}
                aiNutritionGoals={aiNutritionGoals}
                onBack={() => setShowSmartSwaps(false)}
                onError={(message, errorType) => {
                  console.error(`[SmartSwaps] Error (${errorType}):`, message);
                  toast.error(message, { duration: 5000, action: { label: 'Try again', onClick: () => {} } });
                }}
                remainingCalories={Math.max(0, dailyGoal - todayTotals.calories)}
                remainingProtein={Math.max(0, targetProtein - todayTotals.protein)}
                remainingCarbs={Math.max(0, targetCarbs - todayTotals.carbs)}
                remainingFat={Math.max(0, targetFat - todayTotals.fat)}
                onMealLogged={async (updatedTotals, itemName) => {
                  const mealData = {
                    food_items: itemName,
                    calories: updatedTotals.calories || 0,
                    protein: updatedTotals.protein || 0,
                    carbs: updatedTotals.carbs || 0,
                    fat: updatedTotals.fat || 0,
                    fiber: updatedTotals.fiber || 0,
                    sugar: updatedTotals.sugar || 0,
                    sodium: updatedTotals.sodium || 0,
                    vitamin_c: updatedTotals.vitamin_c || 0,
                    iron: updatedTotals.iron || 0,
                    calcium: updatedTotals.calcium || 0
                  };
                  setShowSmartSwaps(false);
                  setCurrentView('home');
                  try {
                    const totalsResponse = await fetch(`${BACKEND_URL}/api/meals/${userId}/today`);
                    if (totalsResponse.ok) {
                      const totalsData = await totalsResponse.json();
                      const freshTotals = totalsData.totals || {};
                      setTodayTotals(freshTotals);
                      setTodayMeals(totalsData.meals || []);
                      triggerPostMealComplete(freshTotals, mealData, true);
                    } else {
                      loadTodayMeals(userId);
                      triggerPostMealComplete(null, mealData, true);
                    }
                  } catch (fetchError) {
                    console.error('[SmartSwaps onMealLogged] Error:', fetchError);
                    loadTodayMeals(userId);
                    triggerPostMealComplete(null, mealData, true);
                  }
                }}
              />
            </Suspense>
          </div>
        )}
      
      {/* Cookbook View - Saved Recipes (Genius Plaite) (Lazy Loaded) */}
      {showCookbook && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          overflowY: 'auto'
        }}>
          <Suspense fallback={<ViewLoadingFallback />}>
            <CookbookView
              userId={userId}
              userTier={userTier}
              onBack={() => setShowCookbook(false)}
              onGoToSmartSwaps={() => {
                setShowCookbook(false);
                setShowSmartSwaps(true);
              }}
              onMealLogged={(mealData) => {
                // Update today's totals
              const newTotals = {
                calories: (todayTotals.calories || 0) + (mealData.calories || 0),
                protein: (todayTotals.protein || 0) + (mealData.protein_g || 0),
                carbs: (todayTotals.carbs || 0) + (mealData.carbs_g || 0),
                fat: (todayTotals.fat || 0) + (mealData.fat_g || 0)
              };
              setTodayTotals(newTotals);
              // Refresh meals list
              loadTodayMeals(userId);
              // Trigger post-meal complete sheet with current meal
              const currentMeal = {
                food_items: mealData.food_items || mealData.name,
                calories: mealData.calories || 0,
                protein: mealData.protein_g || 0,
                carbs: mealData.carbs_g || 0,
                fat: mealData.fat_g || 0
              };
              triggerPostMealComplete(newTotals, currentMeal);
            }}
          />
          </Suspense>
        </div>
      )}
      
      {/* Footer */}
      {userId && (
        <footer style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'var(--background)',
          borderTop: '1px solid #ddd',
          padding: '12px 16px',
          textAlign: 'center',
          fontSize: '13px',
          color: 'var(--text-secondary)',
          zIndex: 100
        }}>
          <button
            onClick={() => setCurrentView('disclaimer')}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--brand-primary)',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: '13px',
              padding: '4px 8px'
            }}
          >
            Legal & Medical Disclaimer
          </button>
          {' | '}
          <span>© 2026 SmartPlAIte - AI-Powered Nutrition</span>
        </footer>
      )}
      
      {/* ═══════════════════════════════════════════════════════════════════════
          GUEST MODE MODALS - Frictionless onboarding flow
      ═══════════════════════════════════════════════════════════════════════ */}
      
      {/* First Meal Success Sheet */}
      {showFirstMealSuccess && (
        <FirstMealSuccessSheet
          mealData={lastGuestMealData}
          onPersonalize={() => {
            // "Create Free Account" - show signup
            setShowFirstMealSuccess(false);
            setShowAuthFromGuest(true);
            setAuthView('signup');
          }}
          onLogAnother={() => {
            // "Continue to Explore" - go to dashboard with locked features
            setShowFirstMealSuccess(false);
            // Show a toast about locked features
            toast('Explore your dashboard! Some features are locked until you sign up.', {
              duration: 4000,
              icon: '🔒'
            });
          }}
          onDismiss={() => setShowFirstMealSuccess(false)}
        />
      )}
      
      {/* Personalize Targets Modal */}
      {showPersonalizeTargets && (
        <PersonalizeTargetsModal
          onComplete={handlePersonalizationComplete}
          onSkip={() => {
            setShowPersonalizeTargets(false);
            // Just close the modal - let them see the dashboard with their meal
            toast('You can personalize your targets anytime from settings', {
              duration: 4000
            });
          }}
          onClose={() => setShowPersonalizeTargets(false)}
        />
      )}
      
      {/* Targets Updated Sheet */}
      {showTargetsUpdated && (
        <TargetsUpdatedSheet
          calculatedTargets={calculatedGuestTargets}
          onSmartGoals={() => {
            setShowTargetsUpdated(false);
            // SmartGoals requires an account - show signup prompt
            toast('Create an account to unlock SmartGoals 🧠', {
              description: 'Your basic targets are already set. SmartGoals adds AI-powered personalization.',
              duration: 5000
            });
            setTimeout(() => {
              setShowSignUpPrompt(true);
            }, 1000);
          }}
          onSaveProgress={() => {
            setShowTargetsUpdated(false);
            setShowSignUpPrompt(true);
          }}
          onContinueWithoutSaving={() => {
            setShowTargetsUpdated(false);
            toast.success('Your targets are set! 🎯', {
              description: 'Log more meals to see your progress',
              duration: 3000
            });
          }}
          onDismiss={() => setShowTargetsUpdated(false)}
        />
      )}
      
      {/* Sign Up Prompt Sheet */}
      {showSignUpPrompt && (
        <SignUpPromptSheet
          onCreateAccount={() => {
            setShowSignUpPrompt(false);
            setShowAuthFromGuest(true);
            setAuthView('signup');
          }}
          onSkip={() => setShowSignUpPrompt(false)}
          onDismiss={() => setShowSignUpPrompt(false)}
        />
      )}
      
      {/* PWA Update Notification */}
      {pwaUpdateAvailable && (
        <div
          data-testid="pwa-update-banner"
          style={{
            position: 'fixed',
            bottom: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            zIndex: 9999,
            fontSize: '14px',
            fontWeight: 500,
            animation: 'slideUp 0.3s ease-out'
          }}
        >
          <span>✨ Update available</span>
          <button
            onClick={handlePwaUpdate}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white',
              padding: '6px 14px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600
            }}
          >
            Refresh
          </button>
          <button
            onClick={() => setPwaUpdateAvailable(false)}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.7)',
              cursor: 'pointer',
              padding: '4px',
              fontSize: '16px',
              lineHeight: 1
            }}
          >
            ×
          </button>
        </div>
      )}
      
      {/* PWA Install Modal */}
      <PWAInstallModal 
        isOpen={showPwaInstallModal}
        onClose={() => setShowPwaInstallModal(false)}
        platform={pwaInstallPlatform}
      />
      
      {/* Cuisine Preferences Modal - for improved food recognition */}
      <CuisinePreferencesModal
        isOpen={showCuisinePreferencesModal}
        onClose={() => setShowCuisinePreferencesModal(false)}
        onSave={(prefs) => {
          setCuisinePreferences(prefs);
          setCuisinePreferencesSet(true);
          toast.success('Cuisine preferences saved! Food recognition will now be more accurate.');
        }}
        userId={userId}
        BACKEND_URL={BACKEND_URL}
        initialPreferences={cuisinePreferences}
        isFirstTime={!cuisinePreferencesSet}
      />
      
      {/* Health Intelligence Ask Why Modal */}
      <AskWhyModal
        show={showAskWhyModal}
        onClose={() => {
          setShowAskWhyModal(false);
          // Refresh usage count after closing
          if (userId) {
            fetch(`${BACKEND_URL}/api/health-intelligence/usage/${userId}`)
              .then(r => r.ok ? r.json() : { count: 0 })
              .then(d => setAskWhyUsedToday(d.count || 0))
              .catch(() => {});
          }
        }}
        prefillMessage={askWhyPrefill}
        insightType={askWhyInsightType}
        userId={userId}
        userTier={userTier}
        isTrial={subscriptionDetails.is_trial || subscriptionDetails.is_trialing}
        todayContext={{
          calories: todayTotals?.calories || 0,
          calorieGoal: dailyGoal || 2000,
          protein: todayTotals?.protein || 0,
          proteinGoal: targetProtein || 150,
          fat: todayTotals?.fat || 0,
          fatGoal: targetFat || 65,
          carbs: todayTotals?.carbs || 0,
          carbsGoal: targetCarbs || 250
        }}
        userContext={{
          dietType: userProfile?.dietary_preference || aiNutritionGoals?.intake_data?.diet_type || '',
          allergies: [
            ...(userProfile?.food_allergies?.filter(a => a && a !== 'none') || []),
            ...(aiNutritionGoals?.intake_data?.food_allergies?.filter(a => a && a !== 'none') || [])
          ].filter((v, i, arr) => arr.indexOf(v) === i),
          medicalConditions: [
            ...(userProfile?.medical_conditions || []),
            ...(aiNutritionGoals?.intake_data?.medical_conditions || [])
          ].filter((v, i, arr) => v && arr.indexOf(v) === i),
          medications: aiNutritionGoals?.intake_data?.medications || [],
          ancestry: aiNutritionGoals?.intake_data?.ancestry || aiNutritionGoals?.intake_data?.ethnicity || '',
          bodyComposition: userProfile?.body_composition || {},
          activityLevel: aiNutritionGoals?.intake_data?.activity_level || userProfile?.activity_level || '',
          primaryGoal: aiNutritionGoals?.intake_data?.primary_goal || '',
          cuisinePreferences: userProfile?.cuisine_preferences || [],
          isFasting: !!localStorage.getItem('smartplaite_fasting_active'),
          mealsToday: (todayMeals || []).slice(0, 8).map(m => ({
            name: m.food_name || m.description || 'Meal',
            calories: m.calories || 0
          }))
        }}
      />
      
      {/* Health Intelligence Fix This Modal */}
      <FixThisModal
        show={showFixThisModal}
        onClose={() => setShowFixThisModal(false)}
        insightType={fixThisInsightType}
        headline={fixThisHeadline}
        userId={userId}
        userTier={userTier}
        isTrial={subscriptionDetails.is_trial || subscriptionDetails.is_trialing}
        calories={todayTotals?.calories || 0}
        calorieGoal={dailyGoal || 2000}
        protein={todayTotals?.protein || 0}
        proteinGoal={targetProtein || 150}
        fat={todayTotals?.fat || 0}
        fatGoal={targetFat || 65}
        carbs={todayTotals?.carbs || 0}
        carbsGoal={targetCarbs || 250}
        isFasting={ifFastingActive}
        fastingTimeRemaining={ifTimeRemaining}
        dietType={userProfile?.dietary_preference || aiNutritionGoals?.intake_data?.dietary_preference || ''}
        allergies={[
          ...(userProfile?.food_allergies?.filter(a => a && a !== 'none') || []),
          ...(aiNutritionGoals?.intake_data?.food_allergies?.filter(a => a && a !== 'none') || [])
        ].filter((v, i, a) => a.indexOf(v) === i)}
        onAddMeal={() => openCameraMode('upload')}
        onSmartMeals={openSmartMealsFitMode}
        onAskWhy={(prefill, type) => {
          setAskWhyPrefill(prefill);
          setAskWhyInsightType(type);
          setShowAskWhyModal(true);
        }}
        onUpgradeGenius={() => handleHomeUpgrade('genius_plaite_monthly')}
      />
      
      {/* MIQ Smart Coach FAB - Floating Action Button */}
      {isAuthenticated && !showAskWhyModal && currentView !== 'auth' && currentView !== 'disclaimer' && (
        <button
          data-testid="smart-coach-fab"
          onClick={openSmartCoach}
          style={{
            position: 'fixed',
            bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
            right: '20px',
            width: '52px',
            height: '52px',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(102, 126, 234, 0.4), 0 2px 6px rgba(0,0,0,0.2)',
            zIndex: 900,
            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.08)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5), 0 3px 8px rgba(0,0,0,0.25)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(102, 126, 234, 0.4), 0 2px 6px rgba(0,0,0,0.2)';
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            <circle cx="9" cy="10" r="1" fill="white" stroke="none"/>
            <circle cx="12" cy="10" r="1" fill="white" stroke="none"/>
            <circle cx="15" cy="10" r="1" fill="white" stroke="none"/>
          </svg>
        </button>
      )}
      
      <Toaster position="bottom-center" />
    </div>
  );
}

export default App;